import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Button,
  Skeleton,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  TextField,
  CircularProgress,
  Tooltip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack,
  Timeline,
  Assessment,
  Person,
  TrendingUp,
  SportsBaseball,
  ScatterPlot,
  Star,
  StarBorder,
} from '@mui/icons-material';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { motion } from 'framer-motion';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useYear } from '../context/YearContext';
import { useFavorites } from '../context/FavoritesContext';

// API and utils
import { playersApi, analyticsApi, pitchSplitsApi, gamesApi } from '../services/apiService';
import { themeUtils } from '../theme/theme';
import { getCVRDisplay, formatSalary } from '../utils/cvrCalculations';
import CVRRadarChart from '../components/analytics/CVRRadarChart';
import WARDisplay from '../components/analytics/WARDisplay';
import TradeValueGauge from '../components/analytics/TradeValueGauge';
import useMobile from '../hooks/useMobile';

// New visualization components
import PitcherSummaryCard from '../components/player/PitcherSummaryCard';
import HitterSummaryCard from '../components/player/HitterSummaryCard';
import SplitsExplorer from '../components/SplitsExplorer';
import AtBatExplorer from '../components/AtBatExplorer';
import CareerTrendChart from '../components/charts/CareerTrendChart';
import { calculateGrade, STAT_THRESHOLDS } from '../components/charts/PitchGradeCard';

// Pitch analysis chart components
import InteractiveZoneChart from '../components/charts/InteractiveZoneChart';
import SprayChart from '../components/charts/SprayChart';
import ZoneHeatmap from '../components/charts/ZoneHeatmap';
import PitchTypeBreakdown from '../components/charts/PitchTypeBreakdown';
// PitcherStuffCard removed — Stuff+ profile now lives in ArsenalTab
import ExpectedStatsCard from '../components/ExpectedStatsCard';
import Boxscore from '../components/Boxscore';
import SprayChartExplorer from '../components/SprayChartExplorer';
import BattedBallTab from '../components/player/BattedBallTab';
import ArsenalTab from '../components/player/ArsenalTab';
import PercentileRails from '../components/player/PercentileRails';
import AtBatDetailDialog from '../components/AtBatDetailDialog';
import axios from 'axios';
import { getTeamLogoUrl as getSharedTeamLogoUrl } from '../utils/teamLogos';

const boxscoreApiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
});

// Position strings vary by data source: 'P', 'SP', 'RP', 'CP', 'Pitcher', 'TWP'...
// Use this everywhere instead of strict === 'P' checks.
const isPitcherPosition = (pos) => {
  const p = (pos || '').toUpperCase();
  return p === 'P' || p === 'SP' || p === 'RP' || p === 'CP' || p === 'LHP' || p === 'RHP' || p.includes('PITCHER');
};

const getTeamLogoUrl = (tc) => getSharedTeamLogoUrl(tc, 500);

// Map MLB game type codes to simplified categories: S(Spring), R(Regular), P(Postseason)
// MLB uses: S=Spring Training, E=Exhibition, R=Regular, A=All-Star,
//           F=Wild Card, D=Division Series, L=League Championship, W=World Series
const normalizeGameType = (code) => {
  if (!code) return null;
  const c = code.toUpperCase();
  if (c === 'S' || c === 'E') return 'S';
  if (c === 'R' || c === 'A') return 'R';
  if (['P', 'F', 'D', 'L', 'W'].includes(c)) return 'P';
  return 'R'; // fallback
};

// Game type classification: prefer actual gameType code, fall back to date heuristic
const classifyGameType = (dateStr) => {
  if (!dateStr) return 'R';
  const parts = dateStr.split('-');
  const month = parseInt(parts[1]);
  const day = parseInt(parts[2]);
  // Spring training: before March 26 (regular season typically starts ~March 26-27)
  if (month < 3 || (month === 3 && day <= 25)) return 'S'; // Spring Training
  if (month >= 10) return 'P'; // Postseason
  return 'R'; // Regular Season
};

const GAME_TYPE_OPTIONS = [
  { value: 'all', label: 'All', emoji: '📊' },
  { value: 'R', label: 'Regular Season', emoji: '🏟️' },
  { value: 'S', label: 'Spring Training', emoji: '🌴' },
  { value: 'P', label: 'Postseason', emoji: '🏆' }
];

// Helper to aggregate stats from game log entries
const aggregateGameStats = (games) => {
  const agg = {
    batting: {
      gamesPlayed: 0, atBats: 0, hits: 0, runs: 0, rbi: 0, homeRuns: 0,
      baseOnBalls: 0, strikeOuts: 0, plateAppearances: 0, doubles: 0,
      triples: 0, totalBases: 0, stolenBases: 0, caughtStealing: 0,
      hitByPitch: 0, sacFlies: 0, sacrificeFlies: 0, groundIntoDoublePlay: 0
    },
    pitching: {
      gamesPlayed: 0, gamesStarted: 0, wins: 0, losses: 0, saves: 0,
      holds: 0, inningsPitched: 0, hits: 0, runs: 0, earnedRuns: 0,
      homeRuns: 0, baseOnBalls: 0, strikeOuts: 0, hitByPitch: 0,
      battersFaced: 0
    },
    fielding: {
      gamesStarted: 0, assists: 0, putOuts: 0, errors: 0, chances: 0
    }
  };
  
  games.forEach(game => {
    const batting = game.stats?.batting || {};
    const pitching = game.stats?.pitching || {};
    const fielding = game.stats?.fielding || {};
    
    // Batting aggregation
    if (batting.atBats > 0 || batting.plateAppearances > 0) {
      agg.batting.gamesPlayed++;
      Object.keys(agg.batting).forEach(k => {
        if (k !== 'gamesPlayed' && typeof batting[k] === 'number') {
          agg.batting[k] += batting[k] || 0;
        }
      });
    }
    
    // Pitching aggregation
    const ip = parseInningsPitched(pitching.inningsPitched);
    if (ip > 0 || pitching.battersFaced > 0) {
      agg.pitching.gamesPlayed++;
      agg.pitching.inningsPitched += ip;
      if (pitching.gamesStarted) agg.pitching.gamesStarted += pitching.gamesStarted;
      Object.keys(agg.pitching).forEach(k => {
        if (!['gamesPlayed', 'gamesStarted', 'inningsPitched'].includes(k) && typeof pitching[k] === 'number') {
          agg.pitching[k] += pitching[k] || 0;
        }
      });
    }
    
    // Fielding aggregation
    Object.keys(agg.fielding).forEach(k => {
      if (typeof fielding[k] === 'number') {
        agg.fielding[k] += fielding[k] || 0;
      }
    });
  });
  
  // Calculate rate stats
  const b = agg.batting;
  if (b.atBats > 0) {
    b.avg = b.hits / b.atBats;
    const tb = b.hits + b.doubles + (2 * b.triples) + (3 * b.homeRuns);
    b.slg = tb / b.atBats;
    b.totalBases = tb;
  }
  const pa = b.atBats + b.baseOnBalls + b.hitByPitch + (b.sacFlies || b.sacrificeFlies || 0);
  if (pa > 0) {
    b.obp = (b.hits + b.baseOnBalls + b.hitByPitch) / pa;
    b.ops = (b.obp || 0) + (b.slg || 0);
    b.kRate = b.strikeOuts / pa;
    b.bbRate = b.baseOnBalls / pa;
  }
  
  const p = agg.pitching;
  if (p.inningsPitched > 0) {
    p.era = (p.earnedRuns * 9) / p.inningsPitched;
    p.whip = (p.hits + p.baseOnBalls) / p.inningsPitched;
    p.strikeoutsPer9Inn = (p.strikeOuts * 9) / p.inningsPitched;
    p.walksPer9Inn = (p.baseOnBalls * 9) / p.inningsPitched;
    p.hitsPer9Inn = (p.hits * 9) / p.inningsPitched;
    p.homeRunsPer9 = (p.homeRuns * 9) / p.inningsPitched;
    if (p.baseOnBalls > 0) p.strikeoutWalkRatio = p.strikeOuts / p.baseOnBalls;
  }
  // Convert decimal IP back to baseball notation (e.g. 10.667 → "10.2", not "10.7")
  const wholeIP = Math.floor(p.inningsPitched);
  const outsIP = Math.round((p.inningsPitched - wholeIP) * 3);
  p.inningsPitched = `${wholeIP}.${outsIP}`;
  
  const f = agg.fielding;
  if (f.chances > 0) {
    f.fieldingPercentage = (f.chances - f.errors) / f.chances;
  }
  
  return agg;
};

// WAR Display Helper
// Grade WAR on a 162-game projected pace so early-season grades aren't
// penalised for a short sample. gamesPlayed = actual GP so far;
// when < 40 we project to 162 and grade on pace, then note it.
const getWARDisplay = (war, gamesPlayed = 162) => {
  const warValue = war || 0;

  // Project to full-season pace when sample is small (< 40 GP).
  // Cap projection at 15 WAR to avoid absurd early-season outliers.
  const gp = Math.max(gamesPlayed || 1, 1);
  const projected = gp < 40 ? Math.min(warValue * (162 / gp), 15) : warValue;
  const isPaced = gp < 40 && warValue !== 0;

  const gradeFor = (v) => {
    if (v >= 8.0) return { description: 'MVP Pace',        color: '#ff6b35', emoji: '🔥', grade: 'A+' };
    if (v >= 6.0) return { description: 'All-Star Pace',   color: '#ff8c42', emoji: '⭐', grade: 'A'  };
    if (v >= 4.0) return { description: 'Very Good Pace',  color: '#ffd23f', emoji: '👍', grade: 'B+' };
    if (v >= 2.5) return { description: 'Above Avg Pace',  color: '#06ffa5', emoji: '✅', grade: 'B'  };
    if (v >= 1.0) return { description: 'Average Pace',    color: '#4fb3d9', emoji: '➖', grade: 'C+' };
    if (v >= 0.0) return { description: 'Below Avg Pace',  color: '#9e9e9e', emoji: '📉', grade: 'C'  };
    return          { description: 'Replacement',          color: '#c0392b', emoji: '🔻', grade: 'D'  };
  };

  const { description, color, emoji, grade } = gradeFor(projected);

  return {
    value: warValue.toFixed(2),
    projected: isPaced ? projected.toFixed(1) : null, // non-null only when paced
    gamesPlayed: gp,
    description: isPaced
      ? description.replace(' Pace', '') + ` (${projected.toFixed(1)} pace/${gp} GP)`
      : description.replace(' Pace', ''),
    color,
    emoji,
    grade,
  };
};

// Helper function to format innings pitched for display (e.g., 5.33 → "5.1", 5.67 → "5.2")
const formatInningsPitched = (ip) => {
  if (!ip || ip === 0) return '0.0';
  
  const wholeInnings = Math.floor(ip);
  const fractionalPart = ip - wholeInnings;
  
  // Convert decimal back to outs (thirds)
  if (fractionalPart < 0.1) {
    return `${wholeInnings}.0`;
  } else if (fractionalPart < 0.5) {
    return `${wholeInnings}.1`; // 1 out
  } else if (fractionalPart < 0.9) {
    return `${wholeInnings}.2`; // 2 outs
  } else {
    return `${wholeInnings + 1}.0`; // Round up to next inning
  }
};

// CVR Explanation Component — unified v6 (0-100 scale)
const CVRExplanation = ({ player, cvrData, salaryData, advancedProfile }) => {
  const cvrScore = cvrData?.cvr || 0;
  const components = advancedProfile?.cvr?.components || {};
  const maxes = advancedProfile?.cvr?.maxes || {
    production: 25, approach: 12, powerStuff: 10, speedDefense: 8,
    situational: 10, durability: 10, contract: 10, trajectory: 15,
  };
  const tier = advancedProfile?.cvr?.tier || getCVRDisplay(cvrScore);
  const salary = salaryData?.salary || 0;
  const war = advancedProfile?.war || player?.war || 0;
  const isPitcher = advancedProfile?.cvr?.isPitcher || isPitcherPosition(player?.position);

  const hasComponents = Object.keys(components).length > 0;

  // Component display config
  const componentConfig = isPitcher ? [
    { key: 'production', label: 'Production', icon: '⚾', desc: 'Park-adjusted ERA+/FIP- blend' },
    { key: 'approach', label: 'Command', icon: '🎯', desc: 'Walk rate, WHIP quality' },
    { key: 'powerStuff', label: 'Stuff', icon: '💨', desc: 'K rate, swing-and-miss' },
    { key: 'speedDefense', label: 'Fielding', icon: '🧤', desc: 'Fielding & positional value' },
    { key: 'situational', label: 'Situational', icon: '📊', desc: 'Platoon, home/away, clutch' },
    { key: 'durability', label: 'Durability', icon: '💪', desc: 'IP volume, consistency' },
    { key: 'contract', label: 'Contract', icon: '💰', desc: 'Surplus value vs salary' },
    { key: 'trajectory', label: 'Trajectory', icon: '📈', desc: 'Age curve, projection' },
  ] : [
    { key: 'production', label: 'Production', icon: '⚾', desc: 'Park-adjusted wRC+/OPS+ blend' },
    { key: 'approach', label: 'Discipline', icon: '👁️', desc: 'Walk rate, K rate, contact' },
    { key: 'powerStuff', label: 'Power', icon: '💥', desc: 'ISO, HR rate' },
    { key: 'speedDefense', label: 'Speed/Defense', icon: '⚡', desc: 'Baserunning, stolen bases, fielding' },
    { key: 'situational', label: 'Situational', icon: '📊', desc: 'Platoon, home/away, clutch' },
    { key: 'durability', label: 'Durability', icon: '💪', desc: 'Games played, PA volume' },
    { key: 'contract', label: 'Contract', icon: '💰', desc: 'Surplus value vs salary' },
    { key: 'trajectory', label: 'Trajectory', icon: '📈', desc: 'Age curve, projection' },
  ];

  return (
    <Box sx={{ maxWidth: 420, p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: 'primary.main', fontSize: '0.95rem' }}>
        Cycle Value Rating (CVR)
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1.5 }}>
        CVR is a composite 0-100 rating that evaluates total player value across 8 weighted components
        including on-field production, contract efficiency, and future trajectory. 
        It incorporates park-adjusted stats, WAR, salary surplus value, and age projections.
      </Typography>

      {/* Component breakdown */}
      {hasComponents && (
        <Box sx={{ mb: 2 }}>
          {componentConfig.map(({ key, label, icon, desc }) => {
            const val = components[key] || 0;
            const max = maxes[key] || 10;
            const pct = Math.min(100, (val / max) * 100);
            return (
              <Box key={key} sx={{ mb: 0.75 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {icon} {label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.75rem' }}>
                    {val.toFixed(1)}/{max}
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: 4, bgcolor: 'action.hover', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{
                    width: `${pct}%`, height: '100%', borderRadius: 2,
                    bgcolor: pct >= 80 ? '#00C851' : pct >= 60 ? '#4CAF50' : pct >= 40 ? '#FFC107' : pct >= 20 ? '#FF9800' : '#F44336',
                  }} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Key context */}
      <Box sx={{ mb: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
          Key Inputs
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>
          WAR: {typeof war === 'number' ? war.toFixed(1) : '—'}
          {salary > 0 ? ` • Salary: $${(salary / 1000000).toFixed(1)}M` : ' • Salary: —'}
          {salary > 0 && war > 0 ? ` • Surplus: $${((war * 10000000 - salary) / 1000000).toFixed(1)}M` : ''}
        </Typography>
      </Box>

      {/* Scale */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
          CVR Scale:
        </Typography>
        <Box sx={{ ml: 0.5 }}>
          {[
            { min: 85, label: 'Elite / MVP Caliber', color: '#FFD700', emoji: '🏆' },
            { min: 70, label: 'All-Star', color: '#C0C0C0', emoji: '⭐' },
            { min: 55, label: 'Above Average Starter', color: '#4CAF50', emoji: '📈' },
            { min: 40, label: 'Average Starter', color: '#2196F3', emoji: '📊' },
            { min: 25, label: 'Below Average / Platoon', color: '#FF9800', emoji: '📉' },
            { min: 0, label: 'Replacement Level', color: '#F44336', emoji: '🔄' },
          ].map(t => (
            <Typography key={t.min} variant="caption" sx={{ display: 'block', color: t.color, fontSize: '0.75rem' }}>
              {t.min}+: {t.label} {t.emoji}
            </Typography>
          ))}
        </Box>
      </Box>

      <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.75rem' }}>
        Contract component uses $10M/WAR market rate to calculate surplus value.
        {!hasComponents && ' Detailed breakdown loads from advanced analytics.'}
      </Typography>
    </Box>
  );
};

// Helper function to parse innings pitched from string format (e.g., "5.1" = 5.33, "5.0" = 5.0)
const parseInningsPitched = (ip) => {
  if (!ip) return 0;
  if (typeof ip === 'number') return ip;
  
  const ipStr = String(ip);
  if (ipStr.includes('.')) {
    const [whole, fraction] = ipStr.split('.');
    const wholeInnings = parseInt(whole) || 0;
    const fractionPart = parseInt(fraction) || 0;
    
    // If the fraction is 0 (like "1.0"), treat it as exact decimal
    // If the fraction is 1 or 2 (like "6.1" or "6.2"), treat as outs
    if (fractionPart === 0) {
      return wholeInnings; // "1.0" = 1.0, "8.0" = 8.0
    } else {
      return wholeInnings + (fractionPart / 3); // "6.1" = 6.33, "6.2" = 6.67
    }
  }
  
  return parseFloat(ipStr) || 0;
};

// ── Prefill helpers ─────────────────────────────────────────────────────────
// When navigating from the players list, route state carries the list-row data.
// These helpers convert it into the shape PlayerDetail expects so the page
// renders instantly (no skeleton) while the full profile loads in background.
const buildStatsFromPrefill = (pf) => {
  if (!pf) return null;
  const batting  = pf.stats?.batting  || pf.batting  || {};
  const pitching = pf.stats?.pitching || pf.pitching || {};
  const hasData  = (batting.atBats > 0) || (batting.gamesPlayed > 0) ||
                   (pitching.gamesPlayed > 0) || !!pitching.inningsPitched;
  if (!hasData) return null;
  return {
    batting, pitching, fielding: {},
    war: pf.war || 0, pwar: pf.pwar || 0,
    cvr: pf.cvr || 0, acvr: pf.acvr || 0, acvrTrend: pf.acvrTrend || 'stable',
  };
};
const buildPlayerFromPrefill = (pf) => {
  if (!pf) return null;
  return {
    id: pf.id || '', mlbId: pf.mlbId || null,
    name: pf.name || '', team: pf.team || pf.currentTeam || '',
    year: String(pf.year || new Date().getFullYear()),
    position: pf.position || '', playerType: pf.playerType || '',
    batSide: pf.batSide || null, pitchHand: pf.pitchHand || null,
    bats: pf.batSide || null, throws: pf.pitchHand || null,
    gameCount: pf.gameCount || 0, war: pf.war || 0, pwar: pf.pwar || 0,
    cvr: pf.cvr || 0, acvr: pf.acvr || 0, acvrTrend: pf.acvrTrend || 'stable',
    salary: pf.salary || null, career: {}, seasonStats: buildStatsFromPrefill(pf),
    careerHighlights: [], analytics: { strengths: [], improvements: [], comparisons: [] },
    gameLog: [], dataSource: 'regular', springTrainingOnly: false,
  };
};

const PlayerDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Support both unified slug route AND legacy team/playerName/year route
  const params = useParams();
  const { year: globalYear } = useYear();
  const { toggleFavoritePlayer, isPlayerFavorite } = useFavorites();
  const prevGlobalYearRef = useRef(String(globalYear));
  // Prevents the programmatic setSelectedTeam/setSelectedYear inside loadPlayerData
  // from triggering a second profile fetch (the effect re-runs but bails early).
  const suppressNextLoadRef = useRef(false);
  // Analytics fetch is deferred until the "Advanced Analytics" tab is opened.
  // Cache per player/year so switching years back and forth doesn't re-fetch.
  const advancedProfileCacheRef = useRef(new Map());
  const advancedProfileKeyRef = useRef(null);
  const advancedProfileYearRef = useRef(null);
  const location = useLocation();
  // Always-current ref so the reset effect reads latest location.state without
  // making location a dep (would re-run effect on every navigation event)
  const _locationRef = useRef(location);
  _locationRef.current = location;
  
  // Determine which route format we're using
  const isUnifiedRoute = !!params.slug;
  const slug = params.slug || null;
  const legacyTeam = params.team || null;
  const legacyPlayerName = params.playerName || null;
  const legacyYear = params.year || null;
  
  // Unified profile data
  const [unifiedProfile, setUnifiedProfile] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  
  // Existing state — seeded from route prefill when navigating from the players list
  const [player, setPlayer] = useState(() => buildPlayerFromPrefill(location.state?.prefill));
  const [playerStats, setPlayerStats] = useState(() => buildStatsFromPrefill(location.state?.prefill));
  const [gameStats, setGameStats] = useState([]);
  const [salaryData, setSalaryData] = useState(null);
  const [cvr, setCvr] = useState(() => location.state?.prefill?.cvr || null);
  const [cvrData, setCvrData] = useState(() => {
    const pf = location.state?.prefill;
    return pf?.cvr ? { cvr: pf.cvr, source: 'prefill' } : null;
  });
  const [advancedProfile, setAdvancedProfile] = useState(null);
  const [playerTrends, setPlayerTrends] = useState(null);
  const [pitchData, setPitchData] = useState(null);
  const [pitchEvents, setPitchEvents] = useState([]);
  const [loading, setLoading] = useState(!location.state?.prefill);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [twoWayRole, setTwoWayRole] = useState('pitching'); // 'pitching' | 'hitting' — for two-way players only
  const [gameTypeFilter, setGameTypeFilter] = useState('R');
  const [dateRange, setDateRange] = useState('all');
  const [selectedGame, setSelectedGame] = useState('all');
  const [drillDownGameDate, setDrillDownGameDate] = useState('');
  const [drillDownVsPlayer, setDrillDownVsPlayer] = useState('');
  const [drillDownInning, setDrillDownInning] = useState(null);
  const [pitchDrillDialogOpen, setPitchDrillDialogOpen] = useState(false);
  const [pitchDrillDialogLoading, setPitchDrillDialogLoading] = useState(false);
  const [pitchDrillDialogAB, setPitchDrillDialogAB] = useState(null);
  const [pitchDrillDialogError, setPitchDrillDialogError] = useState('');
  const { isMobile } = useMobile();

  // Derive the current team/playerName/year from either route format
  const team = selectedTeam || legacyTeam || unifiedProfile?.currentTeam || '';
  const playerName = isUnifiedRoute 
    ? (unifiedProfile?.name?.replace(/\s+/g, '_') || slug?.replace(/-/g, '_') || '')
    : (legacyPlayerName || '');
  const year = selectedYear || legacyYear || unifiedProfile?.latestYear || String(globalYear);

  // Reset state on player navigation. If route state carries prefill data (from
  // the players list), seed instantly so no skeleton is shown.
  useEffect(() => {
    const pf = _locationRef.current?.state?.prefill || null;
    const urlTab = searchParams.get('tab');
    setGameStats([]);
    setSalaryData(null);
    setPlayerTrends(null);
    setError(null);
    setActiveTab(urlTab === 'pitch-analysis' ? 'arsenal' : (urlTab || 'overview'));
    setTwoWayRole('pitching');
    setGameTypeFilter('R');
    setDateRange('all');
    setSelectedGame('all');
    setUnifiedProfile(null);
    setSelectedTeam(null);
    setSelectedYear(null);
    window.scrollTo(0, 0);
    if (pf) {
      setPlayer(buildPlayerFromPrefill(pf));
      setPlayerStats(buildStatsFromPrefill(pf));
      setCvr(pf.cvr || null);
      setCvrData(pf.cvr ? { cvr: pf.cvr, source: 'prefill' } : null);
      setLoading(false);
    } else {
      setPlayer(null);
      setPlayerStats(null);
      setCvr(null);
      setCvrData(null);
      setLoading(true);
    }
  }, [slug, legacyTeam, legacyPlayerName, legacyYear]);

  // Sync with global year selector
  useEffect(() => {
    const currentGlobalYear = String(globalYear);
    if (prevGlobalYearRef.current !== currentGlobalYear) {
      if (isUnifiedRoute && slug) {
        setSelectedYear(currentGlobalYear);
      } else if (legacyTeam && legacyPlayerName) {
        navigate(`/players/${legacyTeam}/${legacyPlayerName}/${currentGlobalYear}`, { replace: true });
      }
    }
    prevGlobalYearRef.current = currentGlobalYear;
  }, [globalYear, isUnifiedRoute, slug, legacyTeam, legacyPlayerName, navigate]);

  const loadPlayerData = useCallback(async () => {
    try {
      setError(null);

      let playerResponse;
      let currentTeam, currentYear, formattedPlayerName;

      if (isUnifiedRoute && slug) {
        // ── UNIFIED ROUTE: load from profile API ──
        const profile = await playersApi.getUnifiedProfile(slug, {
          year: selectedYear || undefined,
          team: selectedTeam || undefined,
          mode: 'rolling',
        });
        
        // Handle disambiguation (multiple real people with same name)
        if (profile._disambig) {
          setUnifiedProfile(profile);
          setLoading(false);
          return; // Render disambiguation chooser instead
        }
        
        setUnifiedProfile(profile);
        
        // Determine which season to display (backend provides smart fallback via dataSource)
        const activeSeason = profile.activeSeason || profile.seasons[0];
        if (!activeSeason) {
          setError('No season data available for this player');
          setLoading(false);
          return;
        }
        
        currentTeam = activeSeason.team;
        currentYear = activeSeason.year;
        formattedPlayerName = profile.name.replace(/\s+/g, '_');
        
        // Set selected team/year from the backend's smart selection (not the global year).
        // Suppress the load re-trigger — these are programmatic updates, not user actions.
        if (!selectedTeam || !selectedYear) suppressNextLoadRef.current = true;
        if (!selectedTeam) setSelectedTeam(currentTeam);
        if (!selectedYear) setSelectedYear(currentYear);
        
        // Build a player response compatible with the existing rendering
        playerResponse = {
          id: activeSeason.compositeId,
          mlbId: activeSeason.mlbId || null,
          name: profile.name,
          team: currentTeam,
          year: currentYear,
          dataSource: profile.dataSource || 'regular',
          position: activeSeason.position || profile.position,
          playerType: activeSeason.playerType || profile.playerType,
          batSide: activeSeason.batSide || profile.batSide || null,
          pitchHand: activeSeason.pitchHand || profile.pitchHand || null,
          bats: activeSeason.batSide || profile.batSide || null,
          throws: activeSeason.pitchHand || profile.pitchHand || null,
          gameCount: activeSeason.gameCount,
          war: activeSeason.war,
          pwar: activeSeason.pwar || 0,
          cvr: activeSeason.cvr,
          acvr: activeSeason.acvr || 0,
          acvrTrend: activeSeason.acvrTrend || 'stable',
          salary: profile.salary,
          career: profile.career || {},
          seasonStats: {
            batting: activeSeason.batting || {},
            pitching: activeSeason.pitching || {},
            fielding: activeSeason.fielding || {},
            war: activeSeason.war,
            pwar: activeSeason.pwar || 0,
            cvr: activeSeason.cvr,
            acvr: activeSeason.acvr || 0,
            acvrTrend: activeSeason.acvrTrend || 'stable',
          },
          careerHighlights: profile.analytics?.highlights || [],
          analytics: {
            strengths: profile.analytics?.strengths || [],
            improvements: [],
            comparisons: [],
          },
          gameLog: profile.gameLog || [],
        };
      } else {
        // ── LEGACY ROUTE: load from composite ID ──
        formattedPlayerName = legacyPlayerName.replace(/_/g, '_');
        currentTeam = legacyTeam;
        currentYear = legacyYear;
        
        playerResponse = await playersApi.getPlayerById(
          `${currentTeam}-${formattedPlayerName}-${currentYear}`, 
          { year: currentYear, includeGameLog: 'true' }
        );
        
        // Also try to load the unified profile for team history sidebar
        const nameSlug = formattedPlayerName.replace(/_/g, '-').toLowerCase();
        playersApi.getUnifiedProfile(nameSlug).then(profile => {
          setUnifiedProfile(profile);
        }).catch(() => {});
      }

      // Get player splits for additional analysis (non-fatal)
      playersApi.getPlayerSplits(currentTeam, formattedPlayerName, currentYear, 'all').catch(() => null);

      setPlayer({
        ...playerResponse,
        name: playerResponse.name,
        team: currentTeam,
        position: playerResponse.position,
        year: currentYear,
        dataSource: playerResponse.dataSource || 'regular',
        springTrainingOnly: playerResponse.dataSource === 'spring_training',
      });
      
      setPlayerStats(playerResponse.seasonStats);
      const _gameLog = playerResponse.gameLog || [];
      setGameStats(_gameLog);

      // Auto-select game type: prefer R (regular season), fall back to S (spring
      // training) only if the player has NO regular season games this year — not
      // even a single game in the season stats. This prevents Hader-type cases
      // where the game log lacks explicit gameType fields but seasonStats prove
      // the player has been pitching in regular season.
      const _hasR = _gameLog.some(g => {
        const rawType = g.gameType || g.gameInfo?.gameType;
        return (rawType ? normalizeGameType(rawType) : classifyGameType(g.date || g.gameInfo?.date)) === 'R';
      }) || (
        // Season stats exist with games → definitely regular season
        ((playerResponse.seasonStats?.batting?.gamesPlayed || 0) +
         (playerResponse.seasonStats?.pitching?.gamesPlayed || 0)) > 0
      );
      const _hasS = !_hasR && _gameLog.some(g => {
        const rawType = g.gameType || g.gameInfo?.gameType;
        return (rawType ? normalizeGameType(rawType) : classifyGameType(g.date || g.gameInfo?.date)) === 'S';
      });
      setGameTypeFilter(_hasS ? 'S' : 'R');
      
      if (playerResponse.salary && playerResponse.salary.salary) {
        setSalaryData(playerResponse.salary);
      } else {
        setSalaryData(null);
      }
      
      const backendCVR = playerResponse.cvr || playerResponse.seasonStats?.cvr || 0;
      setCvr(backendCVR);
      
      if (backendCVR > 0) {
        setCvrData({
          cvr: backendCVR,
          explanation: 'Pre-computed CVR from backend (consistent with players list)',
          source: 'backend'
        });
      } else {
        setCvrData(null);
      }

      // Advanced analytics — stored in a ref map keyed by playerId so year-switching
      // doesn't refetch. Actual fetch is deferred until user opens the Analytics tab.
      const playerId = `${currentTeam}-${formattedPlayerName}-${currentYear}`;
      // Store the current playerId so the tab-open handler knows what to fetch.
      advancedProfileKeyRef.current = playerId;
      advancedProfileYearRef.current = currentYear;
      // If we already have cached data for this player/year, restore it immediately.
      if (advancedProfileCacheRef.current.has(playerId)) {
        setAdvancedProfile(advancedProfileCacheRef.current.get(playerId));
      } else {
        setAdvancedProfile(null); // clear stale previous-year data
      }

      // Fetch player trends (non-fatal)
      playersApi.getPlayerTrends(playerId, { year: currentYear, period: '30' }).then(data => {
        setPlayerTrends(data);
      }).catch(() => {
        setPlayerTrends(null);
      });

      // NOTE: pitch data + pitch events are fetched by a dedicated effect below
      // keyed on effectivePerspective, so two-way players (Ohtani) refetch the
      // correct perspective when toggling Pitcher/Hitter.

    } catch (err) {
      console.error('Error loading player data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isUnifiedRoute, slug, selectedTeam, selectedYear, legacyTeam, legacyPlayerName, legacyYear]);

  useEffect(() => {
    if (suppressNextLoadRef.current) {
      suppressNextLoadRef.current = false;
      return;
    }
    if (isUnifiedRoute ? slug : (legacyTeam && legacyPlayerName && legacyYear)) {
      loadPlayerData();
    }
  }, [isUnifiedRoute, slug, legacyTeam, legacyPlayerName, legacyYear, selectedTeam, selectedYear, loadPlayerData]);

  // Determine player type based on games played - MUST be before any early returns
  const playerType = React.useMemo(() => {
    const battingGames = playerStats?.batting?.gamesPlayed || 0;
    const pitchingGames = playerStats?.pitching?.gamesPlayed || 0;
    const atBats = playerStats?.batting?.atBats || 0;
    const inningsPitched = parseFloat(playerStats?.pitching?.inningsPitched) || 0;
    
    // Check for two-way player - meaningful stats in both categories
    const hasBattingStats = battingGames > 0 || atBats > 0;
    const hasPitchingStats = pitchingGames > 0 || inningsPitched > 0;
    
    if (hasBattingStats && hasPitchingStats) {
      return 'two-way'; // Two-way player (any meaningful stats in both)
    } else if (hasPitchingStats && !hasBattingStats) {
      return 'pitcher'; // Pure pitcher
    } else {
      return 'batter'; // Pure batter (default)
    }
  }, [playerStats]);

  // Filter games by game type and date range
  const filteredGameStats = React.useMemo(() => {
    if (!gameStats || gameStats.length === 0) return [];
    let filtered = gameStats;
    
    // Filter by game type
    if (gameTypeFilter !== 'all') {
      filtered = filtered.filter(game => {
        const rawType = game.gameType || game.gameInfo?.gameType;
        const type = rawType ? normalizeGameType(rawType) : classifyGameType(game.date || game.gameInfo?.date);
        return type === gameTypeFilter;
      });
    }
    
    // Filter by date range preset
    if (dateRange !== 'all') {
      const now = new Date();
      const fmt = d => d.toISOString().slice(0, 10);
      let startDate;
      if (dateRange === 'last7')  startDate = fmt(new Date(now - 7 * 864e5));
      if (dateRange === 'last14') startDate = fmt(new Date(now - 14 * 864e5));
      if (dateRange === 'last30') startDate = fmt(new Date(now - 30 * 864e5));
      if (startDate) {
        filtered = filtered.filter(game => {
          const date = game.date || game.gameInfo?.date || game.gameInfo?.officialDate;
          if (!date) return true;
          return date.substring(0, 10) >= startDate;
        });
      }
    }
    
    return filtered;
  }, [gameStats, gameTypeFilter, dateRange]);

  // Count games per type for the filter badges
  const gameTypeCounts = React.useMemo(() => {
    if (!gameStats || gameStats.length === 0) return {};
    const counts = { all: gameStats.length, R: 0, S: 0, P: 0 };
    gameStats.forEach(game => {
      const rawType = game.gameType || game.gameInfo?.gameType;
      const type = rawType ? normalizeGameType(rawType) : classifyGameType(game.date || game.gameInfo?.date);
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  }, [gameStats]);

  // Re-aggregate stats from filtered games so every filter reflects actual game logs
  const filteredPlayerStats = React.useMemo(() => {
    if (gameTypeFilter === 'all') {
      // Aggregate from ALL game logs so overview truly reflects every game type (S+R+P)
      if (gameStats && gameStats.length > 0) {
        return aggregateGameStats(gameStats);
      }
      return playerStats; // fallback if no game log available
    }
    if (!filteredGameStats || filteredGameStats.length === 0) return playerStats || null;
    return aggregateGameStats(filteredGameStats);
  }, [filteredGameStats, gameTypeFilter, playerStats, gameStats]);

  // Build game list from pitch events for the per-game pitch filter
  const pitchGamesList = React.useMemo(() => {
    if (!pitchEvents || pitchEvents.length === 0) return [];
    const gamesMap = new Map();
    pitchEvents.forEach(e => {
      const gid = e.gameId || e.g;
      const gd = e.gameDate || e.gd;
      const opp = e.opponent || e.opp;
      const ha = e.homeAway || e.ha;
      const gt = e.gt || e.gameType;
      if (!gid || !gd) return;
      if (!gamesMap.has(gid)) {
        gamesMap.set(gid, { gameId: gid, date: gd, opponent: opp, homeAway: ha, gameType: gt, pitchCount: 0 });
      }
      gamesMap.get(gid).pitchCount++;
    });
    // Filter by current gameTypeFilter
    let list = Array.from(gamesMap.values());
    if (gameTypeFilter && gameTypeFilter !== 'all') {
      list = list.filter(g => {
        const t = g.gameType ? normalizeGameType(g.gameType) : classifyGameType(g.date);
        return t === gameTypeFilter;
      });
    }
    // Sort by date descending (most recent first)
    return list.sort((a, b) => b.date.localeCompare(a.date));
  }, [pitchEvents, gameTypeFilter]);

  // Reset selectedGame when gameTypeFilter changes or pitch events change
  React.useEffect(() => {
    setSelectedGame('all');
  }, [gameTypeFilter]);

  // Lazy analytics fetch — only fires when user opens the "Advanced Analytics" tab.
  // Caches result per player/year so tab re-opens and year back-and-forth are instant.
  useEffect(() => {
    if (activeTab !== 'analytics') return;
    const key = advancedProfileKeyRef.current;
    const yr  = advancedProfileYearRef.current;
    if (!key) return;
    if (advancedProfileCacheRef.current.has(key)) {
      setAdvancedProfile(advancedProfileCacheRef.current.get(key));
      return;
    }
    analyticsApi.getPlayerProfile(key, yr).then(profile => {
      advancedProfileCacheRef.current.set(key, profile);
      setAdvancedProfile(profile);
    }).catch(() => {
      setAdvancedProfile(null);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, advancedProfileKeyRef.current]);

  // For two-way players, derive an effective perspective from the role toggle.
  // For pure pitchers/batters, fall back to their position.
  const effectivePerspective = playerType === 'two-way'
    ? (twoWayRole === 'pitching' ? 'pitcher' : 'batter')
    : (isPitcherPosition(player?.position) ? 'pitcher' : 'batter');

  // Fetch pitch data + pitch events for the CURRENT perspective (all game types —
  // tabs filter client-side). Keyed so two-way players refetch when toggling
  // Pitcher/Hitter and pure players fetch exactly once per player/year.
  const pitchFetchKeyRef = React.useRef('');
  useEffect(() => {
    if (loading || !player?.name || !team || !year) return;
    const formattedName = player.name.replace(/\s+/g, '_');
    const key = `${effectivePerspective}:${team}:${formattedName}:${year}`;
    if (pitchFetchKeyRef.current === key) return;
    pitchFetchKeyRef.current = key;
    setPitchData(null);
    setPitchEvents([]);
    const fetchPitch = effectivePerspective === 'pitcher'
      ? pitchSplitsApi.getPitcherPitchData(team, formattedName, year, { gameType: 'all' })
      : pitchSplitsApi.getBatterPitchData(team, formattedName, year, { gameType: 'all' });
    fetchPitch.then(data => {
      if (pitchFetchKeyRef.current === key) setPitchData(data);
    }).catch(() => {
      if (pitchFetchKeyRef.current === key) setPitchData(null);
    });
    pitchSplitsApi.getPitchEvents(effectivePerspective, team, formattedName, year, { gameType: 'all' }).then(events => {
      if (pitchFetchKeyRef.current === key) setPitchEvents(Array.isArray(events) ? events : (events?.events || []));
    }).catch(() => {
      if (pitchFetchKeyRef.current === key) setPitchEvents([]);
    });
  }, [loading, player?.name, team, year, effectivePerspective]);

  const allTabs = [
    { value: 'overview', label: 'Overview', mobileLabel: 'Overview', icon: <Person /> },
    { value: 'arsenal', label: 'Arsenal', mobileLabel: 'Arsenal', icon: <SportsBaseball />, pitcherOnly: true },
    { value: 'batted-ball', label: 'Batted Ball', mobileLabel: 'Batted', icon: <SportsBaseball />, hitterOnly: true },
    { value: 'at-bats', label: 'At-Bat Explorer', mobileLabel: 'ABs', icon: <SportsBaseball />, hitterOnly: true },
    { value: 'splits', label: 'Splits', mobileLabel: 'Splits', icon: <Assessment /> },
    { value: 'analytics', label: 'Advanced Analytics', mobileLabel: 'Analytics', icon: <Assessment /> },
    { value: 'games', label: 'Game Log', mobileLabel: 'Games', icon: <Timeline /> },
    { value: 'career', label: 'Career', mobileLabel: 'Career', icon: <TrendingUp /> },
    { value: 'compare-years', label: 'Compare Years', mobileLabel: 'Compare', icon: <Assessment /> },
  ];

  // For two-way players, hide tabs that don't apply to the current role.
  // Pure pitchers always see pitcher tabs; pure batters see batter tabs.
  const tabs = allTabs.filter(tab => {
    if (playerType === 'two-way') {
      if (tab.pitcherOnly && twoWayRole !== 'pitching') return false;
      if (tab.hitterOnly && twoWayRole !== 'hitting') return false;
    } else if (playerType === 'pitcher') {
      if (tab.hitterOnly) return false;
    } else {
      if (tab.pitcherOnly) return false;
    }
    return true;
  });

  if (loading) {
    return <PlayerDetailSkeleton />;
  }

  // Disambiguation: multiple real players share the same name
  if (unifiedProfile?._disambig) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} size="small"
          sx={{ mb: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
          Back
        </Button>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          Multiple players named "{unifiedProfile.name}"
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select which player you're looking for:
        </Typography>
        {unifiedProfile.variants.map((v) => (
          <Card key={v.mlbId} variant="outlined" sx={{ mb: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
            onClick={() => navigate(`/players/profile/${v.slug}`)}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Avatar src={getTeamLogoUrl(v.currentTeam)} sx={{ width: 40, height: 40, bgcolor: '#eee' }}
                imgProps={{ style: { objectFit: 'contain', background: 'white' } }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>{v.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {v.position || '—'} — {v.currentTeam} ({v.latestYear})
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  if (error || !player) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" variant="h6">
          {error || 'Player not found'}
        </Typography>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1400, mx: 'auto', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Button 
            startIcon={<ArrowBack />} 
            onClick={() => navigate(-1)}
            size="small"
            sx={{ mb: 1, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
          >
            Back
          </Button>
          
          <Card elevation={0} sx={{ 
            border: `1px solid ${alpha(themeUtils.getTeamColor(player?.team || team) || '#1976d2', 0.15)}`,
            borderRadius: 2,
            overflow: 'hidden',
          }}>
            {/* Accent bar */}
            <Box sx={{ height: 3, background: themeUtils.getTeamColor(player?.team || team) || '#1976d2' }} />
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}>
                {/* Player headshot + team logo */}
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <Avatar
                    src={player?.mlbId ? `https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_213,q_auto:best/v1/people/${player.mlbId}/headshot/67/current` : undefined}
                    alt={player?.name}
                    sx={{
                      width: { xs: 56, sm: 72 },
                      height: { xs: 56, sm: 72 },
                      backgroundColor: alpha(themeUtils.getTeamColor(player?.team || team) || '#1976d2', 0.1),
                      border: `2px solid ${alpha(themeUtils.getTeamColor(player?.team || team) || '#1976d2', 0.3)}`,
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      color: themeUtils.getTeamColor(player?.team || team) || '#1976d2',
                    }}
                  >
                    {player?.name ? player.name.split(' ').map(n => n[0]).join('') : ''}
                  </Avatar>
                  <Avatar
                    src={getTeamLogoUrl(player?.team || team)}
                    alt={player?.team || team}
                    sx={{
                      width: 22,
                      height: 22,
                      position: 'absolute',
                      bottom: -2,
                      right: -2,
                      border: '2px solid white',
                      backgroundColor: 'white',
                    }}
                    imgProps={{
                      style: { objectFit: 'contain' }
                    }}
                  />
                </Box>
                
                <Box sx={{ flex: 1, minWidth: { xs: 150, sm: 200 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, lineHeight: 1.1, mb: 0.25 }}>
                      {player.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); toggleFavoritePlayer({ name: player.name, team: player.team || team, slug }); }}
                      sx={{ ml: 0.5 }}
                    >
                      {isPlayerFavorite({ name: player.name, team: player.team || team }) ? (
                        <Star sx={{ color: '#FFD700', fontSize: 22 }} />
                      ) : (
                        <StarBorder sx={{ fontSize: 22, color: 'text.disabled' }} />
                      )}
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={player.position || '—'}
                      size="small"
                      sx={{
                        backgroundColor: themeUtils.getTeamColor(player?.team || team) || '#1976d2',
                        color: '#ffffff',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                    <Chip
                      label={`${player.team} • ${year}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: alpha(themeUtils.getTeamColor(player?.team || team) || '#1976d2', 0.4),
                        color: themeUtils.getTeamColor(player?.team || team) || '#1976d2',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                    {playerType === 'two-way' && (
                      <Chip label="Two-Way ⚾🏏" size="small" color="warning" sx={{ fontWeight: 700, fontSize: '0.75rem', height: 24 }} />
                    )}
                    {player.springTrainingOnly && (
                      <Chip 
                        label="Spring Training Only" 
                        size="small" 
                        sx={{ 
                          fontWeight: 700, fontSize: '0.75rem', height: 24,
                          backgroundColor: alpha('#ff9800', 0.15),
                          color: '#ff9800',
                          border: '1px solid',
                          borderColor: alpha('#ff9800', 0.3),
                        }} 
                      />
                    )}
                    {player.dataSource === 'previous_year' && (
                      <Chip 
                        label={`Showing ${player.year} Data`} 
                        size="small" 
                        sx={{ 
                          fontWeight: 700, fontSize: '0.75rem', height: 24,
                          backgroundColor: alpha('#2196f3', 0.15),
                          color: '#2196f3',
                          border: '1px solid',
                          borderColor: alpha('#2196f3', 0.3),
                        }} 
                      />
                    )}
                  </Box>
                  
                  {player.springTrainingOnly && (
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#ff9800', mb: 0.25 }}>
                      No regular season data — WAR &amp; CVR require regular season games
                    </Typography>
                  )}
                  {player.dataSource === 'previous_year' && (
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#2196f3', mb: 0.25 }}>
                      No {new Date().getFullYear()} data available — showing most recent regular season stats
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    {player.bats && `${player.bats}/${player.throws}`}
                    {player.height && ` • ${player.height}`}
                    {player.weight && `, ${player.weight}`}
                    {player.birthDate && ` • Born: ${new Date(player.birthDate).toLocaleDateString()}`}
                  </Typography>
                </Box>
                
                {/* Quick Stats - CVR / ACVR / Salary / rWAR / pWAR */}
                <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0, flexWrap: isMobile ? 'nowrap' : 'wrap', justifyContent: 'flex-end', overflowX: isMobile ? 'auto' : 'visible', maxWidth: isMobile ? '100%' : 'none', pb: isMobile ? 0.5 : 0 }}>
                  {/* CVR Badge */}
                  {(() => {
                    const cvrDisplay = getCVRDisplay(cvr);
                    return (
                      <Tooltip
                        title={<CVRExplanation player={player} cvrData={cvrData} salaryData={salaryData} advancedProfile={advancedProfile} />}
                        arrow
                        placement="bottom"
                        enterDelay={200}
                      >
                        <Box sx={{ 
                          textAlign: 'center', px: 1.25, py: 0.75,
                          borderRadius: 2,
                          border: `1px solid ${alpha(cvrDisplay.color, 0.3)}`,
                          background: alpha(cvrDisplay.color, 0.06),
                          cursor: 'help',
                          minWidth: 52,
                        }}>
                          <Typography variant="caption" sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.75rem' }, fontWeight: 700, color: cvrDisplay.color, 
                            textTransform: 'uppercase', letterSpacing: 0.5, display: 'block',
                          }}>
                            CVR
                          </Typography>
                          <Typography sx={{ 
                            fontWeight: 900, fontSize: '1.2rem', lineHeight: 1, color: cvrDisplay.color,
                          }}>
                            {cvrDisplay.value}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' }, color: 'text.secondary', display: 'block' }}>
                            {cvrDisplay.grade}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })()}
                  
                  {/* ACVR Badge (Adjusted CVR) — only when salary data available */}
                  {(() => {
                    const acvrVal = player.acvr || 0;
                    const acvrTrend = player.acvrTrend || 'stable';
                    if (acvrVal <= 0) return null;
                    const acvrDisplay = getCVRDisplay(acvrVal);
                    const trendIcon = acvrTrend === 'up' ? '▲' : acvrTrend === 'down' ? '▼' : '—';
                    const trendColor = acvrTrend === 'up' ? '#4CAF50' : acvrTrend === 'down' ? '#F44336' : 'text.disabled';
                    return (
                      <Tooltip
                        title={
                          <Box sx={{ p: 1, maxWidth: 280 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.8rem' }}>
                              Adjusted CVR (ACVR)
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                              CVR adjusted for salary efficiency and year-over-year trajectory.
                              Underpaid players get a boost; overpaid players get a penalty.
                              Trend shows if the player is improving or declining vs. last season.
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                              Base CVR: {getCVRDisplay(cvr).value} {' → '} ACVR: {acvrVal}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem', color: trendColor }}>
                              Trend: {acvrTrend === 'up' ? '📈 Trending Up' : acvrTrend === 'down' ? '📉 Trending Down' : '📊 Stable'}
                            </Typography>
                            {player.career?.avgACVR > 0 && (
                              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem', mt: 0.5 }}>
                                Career Avg ACVR: {player.career.avgACVR}
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow placement="bottom" enterDelay={200}
                      >
                        <Box sx={{ 
                          textAlign: 'center', px: 1.25, py: 0.75,
                          borderRadius: 2,
                          border: `1px solid ${alpha(acvrDisplay.color, 0.3)}`,
                          background: alpha(acvrDisplay.color, 0.06),
                          cursor: 'help',
                          minWidth: 52,
                        }}>
                          <Typography variant="caption" sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.75rem' }, fontWeight: 700, color: acvrDisplay.color, 
                            textTransform: 'uppercase', letterSpacing: 0.5, display: 'block',
                          }}>
                            ACVR
                          </Typography>
                          <Typography sx={{ 
                            fontWeight: 900, fontSize: '1.2rem', lineHeight: 1, color: acvrDisplay.color,
                          }}>
                            {acvrVal}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' }, color: trendColor, display: 'block', fontWeight: 700 }}>
                            {trendIcon} {acvrTrend}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })()}

                  {/* Salary Badge — compact */}
                  {salaryData?.salary > 0 && (() => {
                    const sal = salaryData.salary;
                    const rawWAR = parseFloat(player.war) || 0;
                    const gpPitching = playerStats?.pitching?.gamesPlayed || 0;
                    const gpBatting = playerStats?.batting?.gamesPlayed || 0;
                    const gp = Math.max(gpPitching, gpBatting);
                    const isProjected = gp > 0 && gp < 40 && rawWAR !== 0;
                    const warVal = isProjected ? Math.min(rawWAR * (162 / gp), 15) : rawWAR;
                    const surplus = (warVal * 10000000) - sal;
                    const surplusColor = surplus >= 0 ? '#4CAF50' : '#F44336';
                    const contract = player.contractDetails || salaryData;
                    // Show user-friendly contract type label instead of raw status
                    const contractTypeMap = {
                      'free-agent': 'FA Signing',
                      'extension': 'Extension',
                      'extension-arb': 'Arb Extension',
                      'pre-arb': 'Pre-Arb',
                      'arb': contract?.contractStatus?.arbYear ? `Arb ${contract.contractStatus.arbYear}` : 'Arbitration',
                      'foreign': 'Intl Signing',
                    };
                    const rawType = contract?.contractStatus?.type || contract?.type || '';
                    const statusLabel = contractTypeMap[rawType] || (contract?.contractStatus?.raw || rawType || '');
                    const serviceTimeLabel = contract?.serviceTime
                      ? `${contract.serviceTime.years || 0}.${String(contract.serviceTime.days || 0).padStart(3, '0')}`
                      : null;
                    return (
                      <Tooltip
                        title={
                          <Box sx={{ p: 1, maxWidth: 260 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.8rem' }}>
                              Salary & Contract
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                              Salary: {formatSalary(sal)}
                            </Typography>
                            {statusLabel && (
                              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                Status: {statusLabel}
                              </Typography>
                            )}
                            {serviceTimeLabel && (
                              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                Service Time: {serviceTimeLabel} yrs
                              </Typography>
                            )}
                            {(contract?.contractYearsRemaining ?? contract?.controlYears) != null && (() => {
                              const yrs = contract.contractYearsRemaining ?? contract.controlYears;
                              return (
                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                  Control: {yrs} yr{yrs !== 1 ? 's' : ''} remaining
                                </Typography>
                              );
                            })()}
                            {contract?.payrollPct != null && (
                              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                Payroll %: {contract.payrollPct.toFixed(1)}%
                              </Typography>
                            )}
                            <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                Market Value: {formatSalary(warVal * 10000000)} ({warVal.toFixed(1)} WAR{isProjected ? ` proj. from ${rawWAR.toFixed(1)}` : ''} × $10M)
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem', color: surplusColor }}>
                                Surplus: {surplus >= 0 ? '+' : ''}{formatSalary(Math.abs(surplus))}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                $/WAR: {warVal > 0 ? formatSalary(sal / warVal) : '∞'}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        arrow placement="bottom" enterDelay={200}
                      >
                        <Box sx={{ 
                          textAlign: 'center', px: 1.25, py: 0.75,
                          borderRadius: 2,
                          border: `1px solid ${alpha(surplusColor, 0.25)}`,
                          background: alpha(surplusColor, 0.04),
                          cursor: 'help',
                          minWidth: 52,
                        }}>
                          <Typography variant="caption" sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.75rem' }, fontWeight: 700, color: '#4CAF50', 
                            textTransform: 'uppercase', letterSpacing: 0.5, display: 'block',
                          }}>
                            Salary
                          </Typography>
                          <Typography sx={{ 
                            fontWeight: 900, fontSize: '1rem', lineHeight: 1, color: 'text.primary',
                          }}>
                            {formatSalary(sal)}
                          </Typography>
                          {statusLabel ? (
                            <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' }, color: 'text.secondary', display: 'block', fontWeight: 600, mt: 0.25 }}>
                              {statusLabel}
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' }, color: surplusColor, display: 'block', fontWeight: 700 }}>
                              {surplus >= 0 ? '+' : ''}{formatSalary(Math.abs(surplus))}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })()}
                  
                  {/* rWAR Badge — hover shows career WAR dropdown */}
                  {(() => {
                    const _warGP = Math.max(
                      playerStats?.pitching?.gamesPlayed || 0,
                      playerStats?.batting?.gamesPlayed || 0,
                      player.gameCount || 0,
                    );
                    const warDisplay = getWARDisplay(player.war, _warGP || 162);
                    const careerWAR = player.career?.war;
                    const warByYear = player.career?.warByYear || [];
                    return (
                      <Tooltip
                        title={
                          <Box sx={{ p: 1, maxWidth: 320 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.8rem' }}>
                              Regular Season WAR (rWAR)
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                              Wins Above Replacement — regular season only.
                            </Typography>
                            {careerWAR != null && (
                              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: '0.75rem', mb: 0.5, color: '#ff6b35' }}>
                                Career rWAR: {careerWAR.toFixed(1)}
                              </Typography>
                            )}
                            {warByYear.length > 0 && (
                              <Box sx={{ mt: 0.5, maxHeight: 200, overflowY: 'auto' }}>
                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.12)', pb: 0.25, mb: 0.25 }}>
                                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 700, width: 55 }}>Year</Typography>
                                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 700, width: 40 }}>Team</Typography>
                                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 700, width: 45, textAlign: 'right' }}>rWAR</Typography>
                                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 700, width: 45, textAlign: 'right' }}>pWAR</Typography>
                                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 700, width: 45, textAlign: 'right' }}>ACVR</Typography>
                                </Box>
                                {warByYear.map((wy, i) => (
                                  <Box key={i} sx={{ display: 'flex', py: 0.15, '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', width: 55 }}>{wy.year}</Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', width: 40 }}>{wy.team}</Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', width: 45, textAlign: 'right', fontWeight: 700, color: (wy.war || 0) >= 5 ? '#4CAF50' : (wy.war || 0) >= 2 ? '#FFC107' : 'inherit' }}>
                                      {(wy.war || 0).toFixed(1)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', width: 45, textAlign: 'right', color: (wy.pwar || 0) !== 0 ? '#9b59b6' : 'text.disabled' }}>
                                      {(wy.pwar || 0) !== 0 ? (wy.pwar || 0).toFixed(1) : '—'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', width: 45, textAlign: 'right', color: (wy.acvr || 0) >= 80 ? '#4CAF50' : (wy.acvr || 0) >= 50 ? '#FFC107' : 'text.disabled' }}>
                                      {(wy.acvr || 0) > 0 ? wy.acvr : '—'}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        }
                        arrow placement="bottom" enterDelay={200}
                      >
                        <Box sx={{ 
                          textAlign: 'center', px: 1.25, py: 0.75,
                          borderRadius: 2,
                          border: `1px solid ${alpha(warDisplay.color, 0.3)}`,
                          background: alpha(warDisplay.color, 0.06),
                          cursor: 'help',
                          minWidth: 52,
                        }}>
                          <Typography variant="caption" sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.75rem' }, fontWeight: 700, color: warDisplay.color, 
                            textTransform: 'uppercase', letterSpacing: 0.5, display: 'block',
                          }}>
                            rWAR
                          </Typography>
                          <Typography sx={{ 
                            fontWeight: 900, fontSize: '1.2rem', lineHeight: 1, color: warDisplay.color,
                          }}>
                            {warDisplay.value}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' }, color: 'text.secondary', display: 'block' }}>
                            {warDisplay.grade}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })()}
                  
                  {/* pWAR Badge — hover shows career pWAR */}
                  {(() => {
                    const pwarVal = player.pwar || 0;
                    const pwarDisplay = getWARDisplay(pwarVal);
                    const careerPWAR = player.career?.pwar;
                    const warByYear = player.career?.warByYear || [];
                    const postseasonYears = warByYear.filter(wy => (wy.pwar || 0) !== 0);
                    return (
                      <Tooltip
                        title={
                          <Box sx={{ p: 1, maxWidth: 280 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.8rem' }}>
                              Playoff WAR (pWAR)
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                              WAR calculated from postseason games only.
                              Separates clutch performers from regular-season-only stars.
                            </Typography>
                            {careerPWAR != null && (
                              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: '0.75rem', mb: 0.5, color: '#9b59b6' }}>
                                Career pWAR: {careerPWAR.toFixed(1)}
                              </Typography>
                            )}
                            {postseasonYears.length > 0 ? (
                              <Box sx={{ mt: 0.5 }}>
                                {postseasonYears.slice(0, 6).map((wy, i) => (
                                  <Typography key={i} variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                    {wy.year} {wy.team}: {(wy.pwar || 0).toFixed(1)} pWAR
                                  </Typography>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                                No postseason data available
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow placement="bottom" enterDelay={200}
                      >
                        <Box sx={{ 
                          textAlign: 'center', px: 1.25, py: 0.75,
                          borderRadius: 2,
                          border: `1px solid ${alpha('#9b59b6', 0.25)}`,
                          background: alpha('#9b59b6', 0.04),
                          cursor: 'help',
                          minWidth: 52,
                        }}>
                          <Typography variant="caption" sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.75rem' }, fontWeight: 700, color: '#9b59b6', 
                            textTransform: 'uppercase', letterSpacing: 0.5, display: 'block',
                          }}>
                            pWAR
                          </Typography>
                          <Typography sx={{ 
                            fontWeight: 900, fontSize: '1.2rem', lineHeight: 1, color: pwarVal !== 0 ? '#9b59b6' : 'text.disabled',
                          }}>
                            {pwarVal !== 0 ? pwarVal.toFixed(1) : '—'}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' }, color: 'text.secondary', display: 'block' }}>
                            {pwarVal !== 0 ? pwarDisplay.grade : 'No PS'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })()}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Age Badge */}
          {player.age && (
            <Card elevation={0} sx={{ 
              mt: 1.5, borderRadius: 2, overflow: 'hidden',
              border: '1px solid', borderColor: 'divider',
              p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.75rem', color: 'text.secondary' }}>
                  Age
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', lineHeight: 1 }}>
                  {player.age}
                </Typography>
                {player.birthDate && (
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                    ({new Date(player.birthDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
                  </Typography>
                )}
              </Box>
            </Card>
          )}

          {/* Team History Selector — grouped by team */}
          {unifiedProfile && unifiedProfile.teams && unifiedProfile.teams.length > 0 && (
            <Card elevation={0} sx={{ 
              mt: 1.5, borderRadius: 2, overflow: 'hidden',
              border: '1px solid', borderColor: 'divider',
            }}>
              <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.75rem', color: 'text.secondary' }}>
                  Team History
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                  {unifiedProfile.career?.seasonsPlayed || 0} yr{(unifiedProfile.career?.seasonsPlayed || 0) !== 1 ? 's' : ''}
                  {' · '}
                  {unifiedProfile.career?.teamsPlayed || 0} tm{(unifiedProfile.career?.teamsPlayed || 0) !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Box sx={{ px: 1.5, pb: 1.25 }}>
                {unifiedProfile.teams.map((t, tIdx) => {
                  const teamColor = themeUtils.getTeamColor(t.team) || '#1976d2';
                  return (
                    <Box key={t.team} sx={{ 
                      display: 'flex', alignItems: 'center', gap: 1,
                      py: 0.5,
                      ...(tIdx < unifiedProfile.teams.length - 1 && {
                        borderBottom: '1px solid', borderColor: 'divider',
                      }),
                    }}>
                      {/* Team badge */}
                      <Box sx={{ 
                        width: 36, minWidth: 36, height: 22, borderRadius: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: alpha(teamColor, 0.12),
                      }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: teamColor, letterSpacing: 0.5 }}>
                          {t.team}
                        </Typography>
                      </Box>
                      {/* Year chips */}
                      <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', flex: 1 }}>
                        {t.years.map((y) => {
                          const isActive = team === t.team && year === y;
                          return (
                            <Chip
                              key={`${t.team}-${y}`}
                              label={String(y).slice(-2)}
                              size="small"
                              variant={isActive ? 'filled' : 'outlined'}
                              onClick={() => {
                                if (isUnifiedRoute) {
                                  setSelectedTeam(t.team);
                                  setSelectedYear(y);
                                } else {
                                  navigate(`/players/profile/${unifiedProfile.slug}?team=${t.team}&year=${y}`);
                                }
                              }}
                              sx={{
                                fontWeight: isActive ? 800 : 600,
                                fontSize: { xs: '0.75rem', sm: '0.75rem' },
                                height: { xs: 32, sm: 20 }, minWidth: { xs: 36, sm: 28 },
                                '& .MuiChip-label': { px: 0.6 },
                                ...(isActive ? {
                                  backgroundColor: teamColor,
                                  color: '#fff',
                                  '&:hover': { backgroundColor: alpha(teamColor, 0.85) },
                                } : {
                                  borderColor: alpha(teamColor, 0.25),
                                  color: 'text.secondary',
                                  '&:hover': { backgroundColor: alpha(teamColor, 0.08), borderColor: alpha(teamColor, 0.5) },
                                }),
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Card>
          )}
        </Box>

        {/* Two-Way Role Toggle — only shown for two-way players */}
        {playerType === 'two-way' && (
          <Card elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.75rem', color: 'text.secondary', flexShrink: 0 }}>
              Viewing as
            </Typography>
            <ToggleButtonGroup
              value={twoWayRole}
              exclusive
              onChange={(_, val) => {
                if (!val) return;
                // If the currently active tab is invisible in the new role, reset to overview
                const newTabs = allTabs.filter(tab => {
                  if (tab.pitcherOnly && val !== 'pitching') return false;
                  if (tab.hitterOnly && val !== 'hitting') return false;
                  return true;
                });
                if (!newTabs.find(t => t.value === activeTab)) setActiveTab('overview');
                setTwoWayRole(val);
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none', fontWeight: 700, px: 2, py: 0.75, fontSize: '0.8rem',
                  borderRadius: '8px !important', border: '1px solid', borderColor: 'divider',
                  '&.Mui-selected': {
                    backgroundColor: alpha(themeUtils.getTeamColor(player?.team || team) || '#1976d2', 0.14),
                    borderColor: alpha(themeUtils.getTeamColor(player?.team || team) || '#1976d2', 0.5),
                    color: themeUtils.getTeamColor(player?.team || team) || '#1976d2',
                  },
                },
              }}
            >
              <ToggleButton value="pitching">⚾ Pitcher</ToggleButton>
              <ToggleButton value="hitting">🏏 Hitter</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {twoWayRole === 'pitching'
                ? 'Showing pitching stats, pitch analysis & splits as a pitcher'
                : 'Showing batting stats, spray chart & splits as a hitter'}
            </Typography>
          </Card>
        )}

        {/* Navigation Tabs + Filters */}
        <Card elevation={0} sx={{ 
          mb: 2, 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => {
              // Clear drill-down filters when user manually switches tabs
              if (newValue !== 'at-bats') { setDrillDownGameDate(''); setDrillDownVsPlayer(''); setDrillDownInning(null); }
              setActiveTab(newValue);
            }}
            variant={isMobile ? 'fullWidth' : 'scrollable'}
            scrollButtons={isMobile ? false : 'auto'}
            sx={{
              px: isMobile ? 0 : 1,
              minHeight: 44,
              '& .MuiTab-root': {
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: isMobile ? '0.7rem' : '0.8rem',
                py: 0,
                px: isMobile ? 0.5 : 1.5,
                gap: 0.5,
                minWidth: isMobile ? 0 : undefined,
                '& .MuiSvgIcon-root': { fontSize: '0.9rem' },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                backgroundColor: themeUtils.getTeamColor(player?.team || team) || '#1976d2',
              },
              '& .Mui-selected': {
                color: `${themeUtils.getTeamColor(player?.team || team) || '#1976d2'} !important`,
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={isMobile ? tab.mobileLabel : tab.label}
                icon={isMobile ? undefined : tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
          
          <Divider />
          
          {/* Compact filter bar — hidden for tabs with their own built-in filters */}
          <Box sx={{ px: 2, py: 1.5, display: ['splits', 'career', 'analytics'].includes(activeTab) ? 'none' : 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={gameTypeFilter}
              exclusive
              onChange={(_, val) => val && setGameTypeFilter(val)}
              size="small"
              sx={{
                flexWrap: 'wrap',
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  px: { xs: 1, sm: 1.5 },
                  py: 0.5,
                  minHeight: 44,
                  fontSize: { xs: '0.75rem', sm: '0.75rem' },
                  borderRadius: '8px !important',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    fontWeight: 700,
                    backgroundColor: alpha(themeUtils.getTeamColor(player?.team || team) || '#1976d2', 0.12),
                    borderColor: alpha(themeUtils.getTeamColor(player?.team || team) || '#1976d2', 0.4),
                    color: themeUtils.getTeamColor(player?.team || team) || '#1976d2',
                  }
                }
              }}
            >
              {GAME_TYPE_OPTIONS.map(opt => (
                <ToggleButton key={opt.value} value={opt.value} disabled={!gameTypeCounts[opt.value]}>
                  {opt.emoji} {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
            
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                onChange={e => setDateRange(e.target.value)}
                label="Date Range"
                sx={{ height: 36, fontSize: '0.75rem' }}
              >
                <MenuItem value="all">Full Season</MenuItem>
                <MenuItem value="last7">Last 7 Days</MenuItem>
                <MenuItem value="last14">Last 14 Days</MenuItem>
                <MenuItem value="last30">Last 30 Days</MenuItem>
              </Select>
            </FormControl>

            {/* Per-game pitch filter — only visible on Arsenal tab */}
            {activeTab === 'arsenal' && pitchGamesList.length > 0 && (
              <>
                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                  <InputLabel>Game</InputLabel>
                  <Select
                    value={selectedGame}
                    onChange={e => setSelectedGame(e.target.value)}
                    label="Game"
                    sx={{ height: 36, fontSize: '0.75rem' }}
                  >
                    <MenuItem value="all">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>All Games ({pitchGamesList.length})</Typography>
                      </Box>
                    </MenuItem>
                    {pitchGamesList.map(g => {
                      const dateLabel = g.date ? new Date(g.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
                      const ha = g.homeAway === 'away' ? '@' : 'vs';
                      return (
                        <MenuItem key={g.gameId} value={g.gameId}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{dateLabel}</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{ha} {g.opponent}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', ml: 0.5 }}>({g.pitchCount} pitches)</Typography>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </Card>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <PlayerOverview player={player} team={team} playerName={playerName} seasonStats={filteredPlayerStats} recentGames={filteredGameStats} playerType={playerType} salaryData={salaryData} war={player.war} year={year} playerTrends={playerTrends} gameTypeFilter={gameTypeFilter} />
        )}

        {activeTab === 'arsenal' && (
          <>
            <ArsenalTab
              player={player}
              pitchData={pitchData}
              pitchEvents={pitchEvents}
              year={year || String(globalYear)}
              gameTypeFilter={gameTypeFilter}
            />
            <Box sx={{ mt: 3 }}>
              <PitchAnalysisTab
                pitchData={pitchData}
                pitchEvents={pitchEvents}
                playerType={playerType}
                player={player}
                year={year}
                gameTypeFilter={gameTypeFilter}
                selectedGame={selectedGame}
                pitchGamesList={pitchGamesList}
                onDrillDown={({ gameId, inning, gameDate, opponentName }) => {
                  setPitchDrillDialogOpen(true);
                  setPitchDrillDialogLoading(true);
                  setPitchDrillDialogAB(null);
                  setPitchDrillDialogError('');
                  const filters = { page: 1, pageSize: 50 };
                  if (gameTypeFilter && gameTypeFilter !== 'all') filters.gameType = gameTypeFilter;
                  if (gameDate) filters.gameDate = gameDate;
                  if (opponentName) filters.vsPlayer = opponentName.replace(/_/g, ' ');
                  if (inning) filters.inning = inning;
                  pitchSplitsApi.getAtBats(effectivePerspective, player?.team || team, player?.name || playerName?.replace(/-/g, ' '), year || String(globalYear), filters)
                    .then(res => {
                      const abs = res?.atBats || [];
                      const match = abs.find(ab =>
                        (!gameId || ab.gameId === gameId) &&
                        (!inning || ab.inning === inning)
                      ) || abs[0] || null;
                      if (!match) setPitchDrillDialogError('No matching at-bat found.');
                      else setPitchDrillDialogAB(match);
                    })
                    .catch(() => setPitchDrillDialogError('Failed to load at-bat details.'))
                    .finally(() => setPitchDrillDialogLoading(false));
                }}
              />
            </Box>
          </>
        )}

        {activeTab === 'splits' && (
          <SplitsExplorer
            key={`splits-${team}-${playerName}-${year}-${effectivePerspective}`}
            team={team}
            playerName={player?.name || playerName?.replace(/-/g, ' ')}
            season={year || String(globalYear)}
            perspective={effectivePerspective}
            gameTypeFilter={gameTypeFilter}
            sharedPitchEvents={pitchEvents}
          />
        )}

        {activeTab === 'batted-ball' && (
          <BattedBallTab
            player={player}
            pitchData={pitchData}
            pitchEvents={pitchEvents}
            year={year}
          />
        )}

        {activeTab === 'at-bats' && (
          <AtBatExplorer
            key={`at-bats-${team}-${playerName}-${year}-${effectivePerspective}`}
            team={team}
            playerName={player?.name || playerName?.replace(/-/g, ' ')}
            season={year || String(globalYear)}
            perspective={effectivePerspective}
            playerType={playerType}
            gameTypeFilter={gameTypeFilter}
            initialGameDate={drillDownGameDate}
            initialVsPlayer={drillDownVsPlayer}
            initialInning={drillDownInning}
          />
        )}

        {activeTab === 'analytics' && (
          <AdvancedAnalyticsTab profile={advancedProfile} player={player} playerType={playerType} seasonStats={filteredPlayerStats} gameTypeFilter={gameTypeFilter} />
        )}
        
        {activeTab === 'games' && (
          <PlayerGameLog games={filteredGameStats} playerType={playerType} />
        )}
        
        {activeTab === 'career' && (
          <PlayerCareer player={player} currentYear={year} playerType={playerType} unifiedProfile={unifiedProfile} />
        )}

        {activeTab === 'compare-years' && (
          <CompareYears player={player} currentYear={year} playerType={playerType} unifiedProfile={unifiedProfile} />
        )}
      </Box>
    </motion.div>

    <AtBatDetailDialog
      open={pitchDrillDialogOpen}
      onClose={() => setPitchDrillDialogOpen(false)}
      atBat={pitchDrillDialogAB}
      loading={pitchDrillDialogLoading}
      error={pitchDrillDialogError}
    />
    </>
  );
};

// Player overview component
const PlayerOverview = ({ player, team, playerName, seasonStats, recentGames, playerType, salaryData, war, year, playerTrends, gameTypeFilter }) => {
  const theme = useTheme();
  const perspective = isPitcherPosition(player?.position) ? 'pitcher' : 'batter';

  if (!seasonStats) {
    return (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No games found</Typography>
          <Typography variant="body2" color="text.disabled">No data available for the selected game type / date range.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={2}>
      {(playerType === 'pitcher' || playerType === 'two-way') && seasonStats?.pitching?.gamesPlayed > 0 && (
        <Grid item xs={12}>
          <PitcherSummaryCard player={player} seasonStats={seasonStats} gameLog={recentGames} year={year} />
        </Grid>
      )}
      {(playerType === 'batter' || playerType === 'two-way') && seasonStats?.batting && (
        <Grid item xs={12}>
          <HitterSummaryCard player={player} seasonStats={seasonStats} gameLog={recentGames} year={year} />
        </Grid>
      )}

      {/* League percentile rankings — Savant-style rails */}
      {(team || player?.team) && player?.name && (
        <Grid item xs={12}>
          <PercentileRails
            team={team || player.team}
            playerName={player.name}
            year={year}
            playerType={playerType}
          />
        </Grid>
      )}

      {/* Expected Stats — xBA, xSLG, barrel%, hard hit% */}
      {(team || player?.team) && player?.name && (
        <Grid item xs={12}>
          <ExpectedStatsCard
            team={team || player.team}
            playerName={player.name}
            season={year}
            perspective={perspective}
            gameType={gameTypeFilter !== 'all' ? gameTypeFilter : undefined}
          />
        </Grid>
      )}


    </Grid>
  );
};

// Player stats component — modern chart-driven layout
const PlayerStats = ({ stats, playerType }) => {
  const theme = useTheme();

  // Computed advanced stats
  const batting = stats?.batting || {};
  const pitching = stats?.pitching || {};
  const fielding = stats?.fielding || {};
  const ip = parseInningsPitched(pitching.inningsPitched);
  const avg = batting.atBats > 0 ? batting.hits / batting.atBats : 0;
  const obp = (batting.atBats + (batting.baseOnBalls||0) + (batting.hitByPitch||0) + (batting.sacrificeFlies||0)) > 0
    ? (batting.hits + (batting.baseOnBalls||0) + (batting.hitByPitch||0)) / (batting.atBats + (batting.baseOnBalls||0) + (batting.hitByPitch||0) + (batting.sacrificeFlies||0)) : 0;
  const tb = (batting.hits||0) + (batting.doubles||0) + 2*(batting.triples||0) + 3*(batting.homeRuns||0);
  const slg = batting.atBats > 0 ? tb / batting.atBats : 0;
  const ops = obp + slg;
  const iso = slg - avg;
  const era = ip > 0 ? (pitching.earnedRuns * 9) / ip : 0;
  const whip = ip > 0 ? ((pitching.hits||0) + (pitching.baseOnBalls||0)) / ip : 0;
  const k9 = ip > 0 ? ((pitching.strikeOuts||0) * 9) / ip : 0;
  const bb9 = ip > 0 ? ((pitching.baseOnBalls||0) * 9) / ip : 0;

  const showBatting = (playerType === 'batter' || playerType === 'two-way') && stats?.batting;
  const showPitching = (playerType === 'pitcher' || playerType === 'two-way') && pitching.gamesPlayed > 0;

  // Stat bar: visualize a value within a range
  const StatBar = ({ label, value, displayValue, max, color = 'primary', invert = false }) => {
    const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
    const barColor = typeof color === 'string' && theme.palette[color] ? theme.palette[color].main : color;
    return (
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>{label}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.75rem' }}>{displayValue}</Typography>
        </Box>
        <Box sx={{ height: 6, borderRadius: 3, bgcolor: alpha(barColor, 0.12), overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${invert ? 100 - pct : pct}%`, borderRadius: 3, bgcolor: barColor, transition: 'width 0.6s ease' }} />
        </Box>
      </Box>
    );
  };

  // Grade dot
  const GradeDot = ({ value, thresholds, lowerBetter = false }) => {
    const grade = calculateGrade(value, thresholds, lowerBetter);
    const colors = { 'A+': '#00c853', 'A': '#2e7d32', 'B+': '#558b2f', 'B': '#9e9d24', 'C+': '#f9a825', 'C': '#ef6c00', 'D': '#d84315', 'F': '#b71c1c' };
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', bgcolor: alpha(colors[grade] || '#666', 0.15), border: `2px solid ${colors[grade] || '#666'}`, ml: 1, flexShrink: 0 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: colors[grade] || '#666' }}>{grade}</Typography>
      </Box>
    );
  };

  return (
    <Grid container spacing={2}>
      {/* Batting — Rate Stats with Grades */}
      {showBatting && (
        <Grid item xs={12} md={showPitching ? 6 : 8}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 2 }}>
                Batting — Rate Stats
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1 }}><StatBar label="AVG" value={avg} displayValue={avg.toFixed(3)} max={0.400} color="#2196F3" /></Box>
                <GradeDot value={avg} thresholds={STAT_THRESHOLDS.batting.avg} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1 }}><StatBar label="OBP" value={obp} displayValue={obp.toFixed(3)} max={0.500} color="#4CAF50" /></Box>
                <GradeDot value={obp} thresholds={STAT_THRESHOLDS.batting.obp} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1 }}><StatBar label="SLG" value={slg} displayValue={slg.toFixed(3)} max={0.700} color="#FF9800" /></Box>
                <GradeDot value={slg} thresholds={STAT_THRESHOLDS.batting.slg} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1 }}><StatBar label="OPS" value={ops} displayValue={ops.toFixed(3)} max={1.200} color="#E91E63" /></Box>
                <GradeDot value={ops} thresholds={STAT_THRESHOLDS.batting.ops} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1 }}><StatBar label="ISO" value={iso} displayValue={iso.toFixed(3)} max={0.350} color="#9C27B0" /></Box>
                <GradeDot value={iso} thresholds={STAT_THRESHOLDS.batting.iso} />
              </Box>
              {batting.wOBA > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ flex: 1 }}><StatBar label="wOBA" value={batting.wOBA} displayValue={batting.wOBA.toFixed(3)} max={0.500} color="#00BCD4" /></Box>
                  <GradeDot value={batting.wOBA} thresholds={STAT_THRESHOLDS.batting.woba} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Batting — Counting Stats */}
      {showBatting && (
        <Grid item xs={12} md={showPitching ? 6 : 4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                Batting — Counting
              </Typography>
              {[
                { label: 'Games', value: batting.gamesPlayed || 0, max: 162 },
                { label: 'AB', value: batting.atBats || 0, max: 650 },
                { label: 'H', value: batting.hits || 0, max: 200 },
                { label: 'HR', value: batting.homeRuns || 0, max: 50, color: '#E91E63' },
                { label: 'RBI', value: batting.rbi || 0, max: 130, color: '#FF9800' },
                { label: 'R', value: batting.runs || 0, max: 130, color: '#4CAF50' },
                { label: '2B', value: batting.doubles || 0, max: 50 },
                { label: '3B', value: batting.triples || 0, max: 15 },
                { label: 'SB', value: batting.stolenBases || 0, max: 50, color: '#00BCD4' },
                { label: 'BB', value: batting.baseOnBalls || 0, max: 100, color: '#2196F3' },
                { label: 'K', value: batting.strikeOuts || 0, max: 200, color: '#F44336' },
              ].map((s) => (
                <StatBar key={s.label} label={s.label} value={s.value} displayValue={String(s.value)} max={s.max} color={s.color || 'primary'} />
              ))}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Pitching — Rate Stats with Grades */}
      {showPitching && (
        <Grid item xs={12} md={showBatting ? 6 : 8}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 2 }}>
                Pitching — Rate Stats
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1 }}><StatBar label="ERA" value={era} displayValue={era.toFixed(2)} max={6.0} color="#E91E63" invert /></Box>
                <GradeDot value={era} thresholds={STAT_THRESHOLDS.pitching.era} lowerBetter />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1 }}><StatBar label="WHIP" value={whip} displayValue={whip.toFixed(2)} max={2.0} color="#FF9800" invert /></Box>
                <GradeDot value={whip} thresholds={STAT_THRESHOLDS.pitching.whip} lowerBetter />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1 }}><StatBar label="K/9" value={k9} displayValue={k9.toFixed(1)} max={14.0} color="#4CAF50" /></Box>
                <GradeDot value={k9} thresholds={STAT_THRESHOLDS.pitching.k9} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ flex: 1 }}><StatBar label="BB/9" value={bb9} displayValue={bb9.toFixed(1)} max={6.0} color="#2196F3" invert /></Box>
                <GradeDot value={bb9} thresholds={STAT_THRESHOLDS.pitching.bb9} lowerBetter />
              </Box>
              {pitching.fip > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Box sx={{ flex: 1 }}><StatBar label="FIP" value={pitching.fip} displayValue={pitching.fip.toFixed(2)} max={6.0} color="#9C27B0" invert /></Box>
                  <GradeDot value={pitching.fip} thresholds={STAT_THRESHOLDS.pitching.fip} lowerBetter />
                </Box>
              )}
              {pitching.babip > 0 && (
                <StatBar label="BABIP" value={pitching.babip} displayValue={pitching.babip.toFixed(3)} max={0.400} color="#607D8B" />
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Pitching — Counting Stats */}
      {showPitching && (
        <Grid item xs={12} md={showBatting ? 6 : 4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                Pitching — Counting
              </Typography>
              {[
                { label: 'W-L', value: (pitching.wins||0) + (pitching.losses||0), displayValue: `${pitching.wins||0}-${pitching.losses||0}`, max: 35 },
                { label: 'IP', value: ip, displayValue: formatInningsPitched(ip), max: 220, color: '#2196F3' },
                { label: 'K', value: pitching.strikeOuts || 0, max: 300, color: '#4CAF50' },
                { label: 'BB', value: pitching.baseOnBalls || 0, max: 80, color: '#FF9800' },
                { label: 'H', value: pitching.hits || 0, max: 200, color: '#F44336' },
                { label: 'ER', value: pitching.earnedRuns || 0, max: 80, color: '#E91E63' },
                { label: 'HR', value: pitching.homeRuns || 0, max: 35, color: '#9C27B0' },
                { label: 'SV', value: pitching.saves || 0, max: 40, color: '#00BCD4' },
                { label: 'HLD', value: pitching.holds || 0, max: 30, color: '#607D8B' },
                { label: 'GS', value: pitching.gamesStarted || 0, max: 34 },
                { label: 'BF', value: pitching.battersFaced || 0, max: 900 },
              ].map((s) => (
                <StatBar key={s.label} label={s.label} value={s.value} displayValue={s.displayValue || String(s.value)} max={s.max} color={s.color || 'primary'} />
              ))}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Fielding */}
      {fielding && (fielding.assists > 0 || fielding.putOuts > 0 || fielding.errors > 0) && (
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                Fielding
              </Typography>
              <StatBar label="Fielding %" value={fielding.fieldingPercentage || 0} displayValue={(fielding.fieldingPercentage || 0).toFixed(3)} max={1.0} color="#4CAF50" />
              <StatBar label="Assists" value={fielding.assists || 0} displayValue={String(fielding.assists || 0)} max={400} color="#2196F3" />
              <StatBar label="Put Outs" value={fielding.putOuts || 0} displayValue={String(fielding.putOuts || 0)} max={1200} color="#FF9800" />
              <StatBar label="Errors" value={fielding.errors || 0} displayValue={String(fielding.errors || 0)} max={25} color="#F44336" />
              <StatBar label="Total Chances" value={fielding.chances || 0} displayValue={String(fielding.chances || 0)} max={1500} />
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

// Player game log component - Modern visual design
const PlayerGameLog = ({ games, playerType }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMobile } = useMobile();

  // Boxscore modal state
  const [boxscoreOpen, setBoxscoreOpen] = useState(false);
  const [boxscoreGame, setBoxscoreGame] = useState(null);
  const [boxscoreLoading, setBoxscoreLoading] = useState(false);

  const openBoxscoreModal = async (gameId, date) => {
    if (!gameId || !date) return;
    setBoxscoreOpen(true);
    setBoxscoreLoading(true);
    try {
      // Try live MLB data first
      const response = await boxscoreApiClient.get(`/v2/mlb-live/schedule/${date}`);
      const found = response.data?.games?.find(g => g.id.toString() === gameId.toString());
      if (found) { setBoxscoreGame(found); setBoxscoreLoading(false); return; }
    } catch (e) { /* fallback */ }
    try {
      const response = await gamesApi.getRecentGames({ limit: 100 });
      const found = response.games?.find(g => g.id.toString() === gameId.toString());
      if (found) { setBoxscoreGame(found); }
      else { setBoxscoreGame(null); }
    } catch (e) { setBoxscoreGame(null); }
    setBoxscoreLoading(false);
  };

  const formatDate = (dateString, game = {}) => {
    try {
      const official = game.gameInfo?.officialDate || game.officialDate || null;
      const startLocal = game.gameInfo?.startLocal || game.startLocal || null;
      const src = official || (startLocal && startLocal.split(/[ ,]/)[0]) || dateString;
      return new Date(src + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return dateString; }
  };

  // Compute game-by-game headline stat for sparkline
  const sparkData = React.useMemo(() => {
    if (!games || games.length === 0) return [];
    return games.map((g) => {
      if (playerType === 'pitcher') {
        const ip = parseInningsPitched(g.stats?.pitching?.inningsPitched);
        const er = g.stats?.pitching?.earnedRuns || 0;
        return { era: ip > 0 ? (er * 9) / ip : null, k: g.stats?.pitching?.strikeOuts || 0, ip };
      }
      const ab = g.stats?.batting?.atBats || 0;
      const h = g.stats?.batting?.hits || 0;
      return { avg: ab > 0 ? h / ab : null, h, rbi: g.stats?.batting?.rbi || 0 };
    });
  }, [games, playerType]);

  // Rolling average (5-game window)
  const rollingData = React.useMemo(() => {
    if (sparkData.length < 2) return [];
    const key = playerType === 'pitcher' ? 'era' : 'avg';
    const window = 5;
    return sparkData.map((d, i) => {
      const start = Math.max(0, i - window + 1);
      const slice = sparkData.slice(start, i + 1).filter(s => s[key] !== null);
      const roll = slice.length > 0 ? slice.reduce((a, s) => a + s[key], 0) / slice.length : null;
      return { i, val: d[key], roll };
    });
  }, [sparkData, playerType]);

  // Season totals
  const totals = React.useMemo(() => {
    if (!games || games.length === 0) return null;
    if (playerType === 'pitcher') {
      return games.reduce((a, g) => {
        const p = g.stats?.pitching || {};
        const ip = parseInningsPitched(p.inningsPitched);
        return { gp: a.gp + (ip > 0 ? 1 : 0), ip: a.ip + ip, h: a.h + (p.hits || 0), er: a.er + (p.earnedRuns || 0), bb: a.bb + (p.baseOnBalls || 0), k: a.k + (p.strikeOuts || 0), w: a.w + (p.wins || 0), l: a.l + (p.losses || 0), sv: a.sv + (p.saves || 0) };
      }, { gp: 0, ip: 0, h: 0, er: 0, bb: 0, k: 0, w: 0, l: 0, sv: 0 });
    }
    return games.reduce((a, g) => {
      const b = g.stats?.batting || {};
      return { gp: a.gp + (b.atBats > 0 ? 1 : 0), ab: a.ab + (b.atBats || 0), h: a.h + (b.hits || 0), r: a.r + (b.runs || 0), rbi: a.rbi + (b.rbi || 0), hr: a.hr + (b.homeRuns || 0), bb: a.bb + (b.baseOnBalls || 0), k: a.k + (b.strikeOuts || 0) };
    }, { gp: 0, ab: 0, h: 0, r: 0, rbi: 0, hr: 0, bb: 0, k: 0 });
  }, [games, playerType]);

  // Sparkline renderer (inline SVG)
  const renderSparkline = () => {
    const valid = rollingData.filter(d => d.val !== null);
    if (valid.length < 3) return null;
    const W = 320, H = 64, pad = 4;
    const vals = valid.map(d => d.val);
    const rolls = valid.map(d => d.roll).filter(r => r !== null);
    const allVals = [...vals, ...rolls];
    const mn = Math.min(...allVals), mx = Math.max(...allVals);
    const range = mx - mn || 1;
    const yScale = (v) => pad + (1 - (v - mn) / range) * (H - 2 * pad);
    const xStep = (W - 2 * pad) / (valid.length - 1);

    const linePts = valid.map((d, i) => `${pad + i * xStep},${yScale(d.val)}`).join(' ');
    const rollPts = valid.filter(d => d.roll !== null).map((d, i) => `${pad + i * xStep},${yScale(d.roll)}`).join(' ');
    const areaPath = `M ${rollPts} L ${pad + (valid.length - 1) * xStep},${H} L ${pad},${H} Z`;

    const mainColor = playerType === 'pitcher' ? '#ef5350' : '#42a5f5';
    return (
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        <path d={areaPath} fill={alpha(mainColor, 0.10)} />
        <polyline points={linePts} fill="none" stroke={alpha(mainColor, 0.35)} strokeWidth="1" />
        <polyline points={rollPts} fill="none" stroke={mainColor} strokeWidth="2" />
      </svg>
    );
  };

  // Game quality score for color-coding (0-1)
  const gameQuality = (game) => {
    if (playerType === 'pitcher') {
      const p = game.stats?.pitching || {};
      const ip = parseInningsPitched(p.inningsPitched);
      if (ip === 0) return 0.5;
      const era = ((p.earnedRuns || 0) * 9) / ip;
      const kPer9 = ((p.strikeOuts || 0) * 9) / ip;
      return Math.min(1, Math.max(0, (1 - era / 9) * 0.6 + (kPer9 / 12) * 0.4));
    }
    const b = game.stats?.batting || {};
    const ab = b.atBats || 0;
    if (ab === 0) return 0.5;
    const avg = (b.hits || 0) / ab;
    const xbh = (b.doubles || 0) + (b.triples || 0) + (b.homeRuns || 0);
    return Math.min(1, avg * 1.5 + xbh * 0.15 + (b.rbi || 0) * 0.05);
  };

  const qualityColor = (q) => {
    if (q >= 0.7) return theme.palette.success.main;
    if (q >= 0.4) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const decisionBadge = (game) => {
    const p = game.stats?.pitching || {};
    if (p.wins) return { label: 'W', color: '#4caf50' };
    if (p.losses) return { label: 'L', color: '#f44336' };
    if (p.saves) return { label: 'SV', color: '#2196f3' };
    if (p.holds) return { label: 'H', color: '#ff9800' };
    return null;
  };

  if (!games || games.length === 0) {
    return (
      <Card elevation={0}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No game data available</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Sparkline Performance Trend */}
      <Card elevation={0}>
        <CardContent sx={{ p: 2, pb: '12px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {playerType === 'pitcher' ? 'Game ERA Trend' : 'Game AVG Trend'}
            </Typography>
            <Typography variant="caption" color="text.secondary">5-game rolling avg</Typography>
          </Box>
          {renderSparkline()}
        </CardContent>
      </Card>

      {/* Summary Chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Chip size="small" label={`${totals.gp} GP`} sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.10) }} />
        {playerType === 'pitcher' ? (
          <>
            <Chip size="small" label={`${totals.w}W-${totals.l}L`} sx={{ fontWeight: 600 }} />
            <Chip size="small" label={`${formatInningsPitched(totals.ip)} IP`} />
            <Chip size="small" label={`${totals.k} K`} sx={{ fontWeight: 600, color: '#ef5350' }} />
            <Chip size="small" label={`${totals.ip > 0 ? ((totals.er * 9) / totals.ip).toFixed(2) : '—'} ERA`} />
            <Chip size="small" label={`${totals.sv} SV`} />
          </>
        ) : (
          <>
            <Chip size="small" label={`${totals.ab > 0 ? (totals.h / totals.ab).toFixed(3) : '—'} AVG`} sx={{ fontWeight: 600 }} />
            <Chip size="small" label={`${totals.hr} HR`} sx={{ fontWeight: 600, color: '#42a5f5' }} />
            <Chip size="small" label={`${totals.rbi} RBI`} />
            <Chip size="small" label={`${totals.r} R`} />
            <Chip size="small" label={`${totals.h}/${totals.ab}`} />
          </>
        )}
      </Box>

      {/* Game Cards */}
      <Card elevation={0}>
        <CardContent sx={{ p: 0 }}>
          <Typography variant="h6" fontWeight={700} sx={{ px: 2.5, pt: 2, pb: 1 }}>
            Game Log ({games.length})
          </Typography>
          {games.map((game, idx) => {
            const q = gameQuality(game);
            const qc = qualityColor(q);
            const isHome = game.gameInfo?.homeAway === 'home';
            const opp = game.gameInfo?.opponent || '—';
            const gameDate = game.gameInfo?.officialDate || game.gameInfo?.date || game.date;
            const gId = game.gameInfo?.gameId || game.gamePk;
            const canLink = gId && gameDate;
            const handleGameClick = () => canLink && openBoxscoreModal(gId, gameDate);

            if (playerType === 'pitcher') {
              const p = game.stats?.pitching || {};
              const ip = parseInningsPitched(p.inningsPitched);
              const badge = decisionBadge(game);
              return (
                <Box key={idx} onClick={handleGameClick} sx={{
                  display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 1.5, px: isMobile ? 1.5 : 2.5, py: 1.2,
                  borderLeft: `3px solid ${qc}`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  transition: 'background 0.15s',
                  cursor: canLink ? 'pointer' : 'default',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                }}>
                  {/* Date + Opponent */}
                  <Box sx={{ minWidth: isMobile ? 70 : 100, flexShrink: 0 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', color: canLink ? 'primary.main' : 'text.primary' }}>{formatDate(game.date, game)}</Typography>
                    <Typography variant="caption" color="text.secondary">{isHome ? 'vs' : '@'} {opp}</Typography>
                  </Box>
                  {/* Decision badge */}
                  <Box sx={{ minWidth: 32, textAlign: 'center' }}>
                    {badge ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', bgcolor: alpha(badge.color, 0.15), color: badge.color, fontWeight: 800, fontSize: '0.75rem' }}>
                        {badge.label}
                      </Box>
                    ) : <Box sx={{ width: 28 }} />}
                  </Box>
                  {/* Stats row */}
                  <Box sx={{ display: 'flex', gap: isMobile ? 1 : 1.5, flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
                    {[
                      { label: 'IP', val: ip > 0 ? formatInningsPitched(ip) : '—' },
                      { label: 'H', val: p.hits || 0, mobileHide: true },
                      { label: 'ER', val: p.earnedRuns || 0, bad: (p.earnedRuns || 0) >= 4, mobileHide: true },
                      { label: 'BB', val: p.baseOnBalls || 0, bad: (p.baseOnBalls || 0) >= 4, mobileHide: true },
                      { label: 'K', val: p.strikeOuts || 0, good: (p.strikeOuts || 0) >= 8 },
                      { label: 'ERA', val: ip > 0 ? ((p.earnedRuns || 0) * 9 / ip).toFixed(2) : '—' },
                    ].filter(s => !isMobile || !s.mobileHide).map((s) => (
                      <Box key={s.label} sx={{ textAlign: 'center', minWidth: 32 }}>
                        <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' }, color: 'text.secondary', display: 'block', lineHeight: 1 }}>{s.label}</Typography>
                        <Typography variant="body2" fontWeight={700} sx={{
                          fontSize: '0.8rem',
                          color: s.good ? theme.palette.success.main : s.bad ? theme.palette.error.main : 'text.primary',
                        }}>{s.val}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            }

            // Batter row
            const b = game.stats?.batting || {};
            const ab = b.atBats || 0;
            const hits = b.hits || 0;
            const multiHit = hits >= 2;
            const homer = (b.homeRuns || 0) > 0;
            return (
              <Box key={idx} onClick={handleGameClick} sx={{
                display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 1.5, px: isMobile ? 1.5 : 2.5, py: 1.2,
                borderLeft: `3px solid ${qc}`,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                transition: 'background 0.15s',
                cursor: canLink ? 'pointer' : 'default',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
              }}>
                {/* Date + Opponent */}
                <Box sx={{ minWidth: isMobile ? 70 : 100, flexShrink: 0 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', color: canLink ? 'primary.main' : 'text.primary' }}>{formatDate(game.date, game)}</Typography>
                  <Typography variant="caption" color="text.secondary">{isHome ? 'vs' : '@'} {opp}</Typography>
                </Box>
                {/* Hit result badge */}
                <Box sx={{ minWidth: 44, textAlign: 'center' }}>
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    px: 0.8, py: 0.2, borderRadius: 1,
                    bgcolor: homer ? alpha('#f44336', 0.15) : multiHit ? alpha('#4caf50', 0.12) : hits === 0 ? alpha('#9e9e9e', 0.08) : 'transparent',
                    minWidth: 38,
                  }}>
                    <Typography variant="body2" fontWeight={700} sx={{
                      fontSize: '0.8rem',
                      color: homer ? '#f44336' : multiHit ? '#4caf50' : hits === 0 ? 'text.secondary' : 'text.primary',
                    }}>{hits}-{ab}</Typography>
                  </Box>
                </Box>
                {/* Stats row */}
                <Box sx={{ display: 'flex', gap: isMobile ? 1 : 1.5, flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
                  {[
                    { label: 'R', val: b.runs || 0, good: (b.runs || 0) >= 2 },
                    { label: 'RBI', val: b.rbi || 0, good: (b.rbi || 0) >= 3 },
                    { label: 'HR', val: b.homeRuns || 0, good: homer },
                    { label: 'BB', val: b.baseOnBalls || 0, mobileHide: true },
                    { label: 'K', val: b.strikeOuts || 0, bad: (b.strikeOuts || 0) >= 3, mobileHide: true },
                    { label: 'AVG', val: ab > 0 ? (hits / ab).toFixed(3) : '—' },
                  ].filter(s => !isMobile || !s.mobileHide).map((s) => (
                    <Box key={s.label} sx={{ textAlign: 'center', minWidth: 32 }}>
                      <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' }, color: 'text.secondary', display: 'block', lineHeight: 1 }}>{s.label}</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{
                        fontSize: '0.8rem',
                        color: s.good ? theme.palette.success.main : s.bad ? theme.palette.error.main : 'text.primary',
                      }}>{s.val}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </CardContent>
      </Card>

      {/* Boxscore Modal */}
      <Dialog
        open={boxscoreOpen}
        onClose={() => { setBoxscoreOpen(false); setBoxscoreGame(null); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            bgcolor: 'background.default',
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Game Boxscore
          </Typography>
          <IconButton size="small" onClick={() => { setBoxscoreOpen(false); setBoxscoreGame(null); }}>
            <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>✕</span>
          </IconButton>
        </Box>
        <DialogContent sx={{ px: 1, pt: 0, pb: 2 }}>
          {boxscoreLoading ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress size={36} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading boxscore...</Typography>
            </Box>
          ) : boxscoreGame ? (
            <Boxscore
              game={boxscoreGame}
              open={true}
              onClose={() => { setBoxscoreOpen(false); setBoxscoreGame(null); }}
              embedded={true}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <SportsBaseball sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Boxscore data not available for this game.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Player career stats component - fetches multi-year data
const PlayerCareer = ({ player, currentYear, playerType, unifiedProfile }) => {
  const theme = useTheme();
  const [yearlyData, setYearlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [careerTotals, setCareerTotals] = useState(null);
  const [bSort, setBSort] = useState({ col: 'year', dir: 'asc' });
  const [pSort, setPSort] = useState({ col: 'year', dir: 'asc' });

  useEffect(() => {
    const loadCareerData = async () => {
      if (!player) return;
      setLoading(true);
      
      // If we have a unified profile, use allMlbSeasons for full career view
      const careerSeasons = unifiedProfile?.allMlbSeasons || unifiedProfile?.seasons || [];
      if (careerSeasons.length > 0) {
        // Filter to seasons with regular season games, use regularSeason stats when available
        const yearlyStats = careerSeasons
          .filter(s => {
            // Exclude seasons that only have spring training / no regular season games
            if (s.springTrainingOnly) return false;
            if (s.gameTypeBreakdown && Object.keys(s.gameTypeBreakdown).length > 0) {
              return (s.gameTypeBreakdown.R || 0) > 0;
            }
            // No breakdown — for completed past seasons, assume regular
            const currentYr = new Date().getFullYear().toString();
            if (String(s.year) < currentYr && (s.gameCount || 0) > 0) return true;
            // Current year with no breakdown: stats heuristic (spring training rarely >30 AB / 15 IP)
            const ab = s.batting?.atBats || s.regularSeason?.batting?.atBats || 0;
            const ip = parseFloat(s.pitching?.inningsPitched || s.regularSeason?.pitching?.inningsPitched || 0);
            return ab >= 50 || ip >= 20;
          })
          .map(s => {
            // Prefer regularSeason sub-object (excludes postseason+spring) over raw totals
            const rs = s.regularSeason || {};
            const batting = rs.batting || s.batting || {};
            const pitching = rs.pitching || s.pitching || {};
            const fielding = rs.fielding || s.fielding || {};
            const games = rs.gameCount ?? s.gameTypeBreakdown?.R ?? s.gameCount ?? batting.gamesPlayed ?? pitching.gamesPlayed ?? 0;
            return {
              year: parseInt(s.year),
              stats: { batting, pitching, fielding },
              team: s.team,
              position: s.position || player.position,
              games,
              war: s.war || 0,
              pwar: s.pwar || 0,
              acvr: s.acvr || 0,
            };
          })
          .sort((a, b) => a.year - b.year);
        
        setYearlyData(yearlyStats);
        
        // Use pre-computed career totals from unified profile (already regular-season-only)
        if (unifiedProfile.career) {
          setCareerTotals({
            batting: unifiedProfile.career.batting || {},
            pitching: unifiedProfile.career.pitching || {},
            years: unifiedProfile.career.seasonsPlayed || yearlyStats.length,
          });
        }
        
        setLoading(false);
        return;
      }
      
      // Fallback: Legacy approach - scan years with N API calls
      const years = [];
      const currentYearNum = parseInt(currentYear) || new Date().getFullYear();
      for (let y = 2015; y <= currentYearNum + 1; y++) years.push(y);
      
      const results = await Promise.allSettled(
        years.map(async (yr) => {
          const playerKey = `${player.team}-${player.name.replace(/\s+/g, '_')}-${yr}`;
          const data = await playersApi.getPlayerById(playerKey, { year: yr });
          if (!data?.seasonStats) throw new Error('No stats');
          return { 
            year: yr, 
            stats: data.seasonStats, 
            team: data.team || player.team,
            position: data.position || player.position,
            games: data.gameLog?.length || data.seasonStats?.batting?.gamesPlayed || data.seasonStats?.pitching?.gamesPlayed || 0
          };
        })
      );
      
      const yearlyStats = results
        .filter(r => r.status === 'fulfilled')
        .map(r => r.value)
        .sort((a, b) => a.year - b.year);
      
      setYearlyData(yearlyStats);
      
      // Calculate career totals
      if (yearlyStats.length > 0) {
        const totals = { batting: {}, pitching: {}, years: yearlyStats.length };
        const battingKeys = ['gamesPlayed', 'atBats', 'hits', 'runs', 'rbi', 'homeRuns', 'doubles', 'triples', 'baseOnBalls', 'strikeOuts', 'stolenBases', 'caughtStealing', 'hitByPitch', 'plateAppearances', 'totalBases', 'sacrificeFlies'];
        const pitchingKeys = ['gamesPlayed', 'gamesStarted', 'wins', 'losses', 'saves', 'holds', 'hits', 'runs', 'earnedRuns', 'homeRuns', 'baseOnBalls', 'strikeOuts', 'hitByPitch', 'battersFaced'];
        
        battingKeys.forEach(k => { totals.batting[k] = 0; });
        pitchingKeys.forEach(k => { totals.pitching[k] = 0; });
        totals.pitching.inningsPitched = 0;
        
        yearlyStats.forEach(yd => {
          if (yd.stats?.batting) {
            battingKeys.forEach(k => { totals.batting[k] += (yd.stats.batting[k] || 0); });
          }
          if (yd.stats?.pitching) {
            pitchingKeys.forEach(k => { totals.pitching[k] += (yd.stats.pitching[k] || 0); });
            totals.pitching.inningsPitched += parseInningsPitched(yd.stats.pitching.inningsPitched);
          }
        });
        
        setCareerTotals(totals);
      }
      
      setLoading(false);
    };
    
    loadCareerData();
  }, [player, unifiedProfile]);

  if (loading) {
    return (
      <Card elevation={0}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Loading career data across all seasons...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (yearlyData.length === 0) {
    return (
      <Card elevation={0}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No multi-year data available for this player.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const showBatting = playerType === 'batter' || playerType === 'two-way';
  const showPitching = playerType === 'pitcher' || playerType === 'two-way';

  // ── helpers ──────────────────────────────────────────────────────────────
  const MONO = '"Roboto Mono","Courier New",monospace';

  const mkSort = (setter) => (col) => setter(prev =>
    prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }
  );
  const sortBat = mkSort(setBSort);
  const sortPit = mkSort(setPSort);

  const SortTh = ({ col, sort, onSort, align = 'center', colSpan, children, sx = {} }) => {
    const active = sort.col === col;
    return (
      <TableCell
        colSpan={colSpan}
        align={align}
        onClick={() => onSort(col)}
        sx={{
          cursor: 'pointer', fontWeight: 700, fontSize: '0.72rem',
          whiteSpace: 'nowrap', userSelect: 'none', py: 0.6, px: 0.75,
          color: 'text.primary',
          '&:hover': { color: 'primary.main' },
          ...sx,
        }}
      >
        {children}{active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
      </TableCell>
    );
  };

  const GroupTh = ({ children, colSpan, color = 'primary' }) => (
    <TableCell
      colSpan={colSpan}
      align="center"
      sx={{
        fontWeight: 800, fontSize: '0.65rem', textTransform: 'uppercase',
        letterSpacing: 1, py: 0.4, px: 0.5, borderBottom: 'none',
        bgcolor: alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.10),
        color: `${color}.main`,
      }}
    >
      {children}
    </TableCell>
  );

  const numCell = (v, opts = {}) => (
    <TableCell
      align="center"
      sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.5, px: 0.75, ...opts.sx }}
    >
      {v ?? '—'}
    </TableCell>
  );

  // Compute batting row stats
  const battingRow = (b) => {
    const avg  = b.atBats > 0 ? b.hits / b.atBats : null;
    const pa   = b.plateAppearances || (b.atBats + (b.baseOnBalls||0) + (b.hitByPitch||0) + (b.sacrificeFlies||0));
    const obp  = (b.atBats + (b.baseOnBalls||0) + (b.hitByPitch||0) + (b.sacrificeFlies||0)) > 0
      ? ((b.hits||0) + (b.baseOnBalls||0) + (b.hitByPitch||0)) / (b.atBats + (b.baseOnBalls||0) + (b.hitByPitch||0) + (b.sacrificeFlies||0))
      : null;
    const tb   = (b.hits||0) + (b.doubles||0) + 2*(b.triples||0) + 3*(b.homeRuns||0);
    const slg  = b.atBats > 0 ? tb / b.atBats : null;
    const ops  = obp != null && slg != null ? obp + slg : null;
    const iso  = avg != null && slg != null ? slg - avg : null;
    const babip = (b.atBats - (b.strikeOuts||0) - (b.homeRuns||0) + (b.sacrificeFlies||0)) > 0
      ? ((b.hits||0) - (b.homeRuns||0)) / (b.atBats - (b.strikeOuts||0) - (b.homeRuns||0) + (b.sacrificeFlies||0))
      : null;
    return { avg, pa, obp, slg, ops, iso, babip, tb };
  };

  // Sort batting data
  const sortedBatting = [...yearlyData.filter(yd => (yd.stats?.batting?.atBats || 0) > 0)]
    .sort((a, b) => {
      const col = bSort.col;
      let av, bv;
      if (col === 'year')  { av = a.year; bv = b.year; }
      else if (col === 'G') { av = a.stats.batting.gamesPlayed||0; bv = b.stats.batting.gamesPlayed||0; }
      else if (col === 'AB') { av = a.stats.batting.atBats||0; bv = b.stats.batting.atBats||0; }
      else if (col === 'HR') { av = a.stats.batting.homeRuns||0; bv = b.stats.batting.homeRuns||0; }
      else if (col === 'RBI') { av = a.stats.batting.rbi||0; bv = b.stats.batting.rbi||0; }
      else if (col === 'AVG') { const ar=battingRow(a.stats.batting), br=battingRow(b.stats.batting); av=ar.avg||0; bv=br.avg||0; }
      else if (col === 'OPS') { const ar=battingRow(a.stats.batting), br=battingRow(b.stats.batting); av=ar.ops||0; bv=br.ops||0; }
      else if (col === 'WAR') { av = a.war||0; bv = b.war||0; }
      else { av = a.year; bv = b.year; }
      return bSort.dir === 'asc' ? av - bv : bv - av;
    });

  // Sort pitching data
  const sortedPitching = [...yearlyData.filter(yd => (yd.stats?.pitching?.gamesPlayed || 0) > 0)]
    .sort((a, b) => {
      const col = pSort.col;
      let av, bv;
      const aip = parseInningsPitched(a.stats.pitching.inningsPitched);
      const bip = parseInningsPitched(b.stats.pitching.inningsPitched);
      if (col === 'year')  { av = a.year; bv = b.year; }
      else if (col === 'G') { av = a.stats.pitching.gamesPlayed||0; bv = b.stats.pitching.gamesPlayed||0; }
      else if (col === 'K') { av = a.stats.pitching.strikeOuts||0; bv = b.stats.pitching.strikeOuts||0; }
      else if (col === 'ERA') { av = aip>0?((a.stats.pitching.earnedRuns||0)*9)/aip:99; bv = bip>0?((b.stats.pitching.earnedRuns||0)*9)/bip:99; }
      else if (col === 'WAR') { av = a.war||0; bv = b.war||0; }
      else { av = a.year; bv = b.year; }
      return pSort.dir === 'asc' ? av - bv : bv - av;
    });

  // Group header background tokens — neutral only
  const GRP = {
    count:    'transparent',
    rate:     'transparent',
    advanced: 'transparent',
    war:      'transparent',
  };
  const HDR_BG = alpha(theme.palette.primary.main, 0.10);

  return (
    <Grid container spacing={2}>
      {/* Career Summary Header */}
      <Grid item xs={12}>
        <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <TrendingUp sx={{ mr: 1, fontSize: '1.1rem', color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                Career Summary
              </Typography>
              <Chip label={`${yearlyData.length} Seasons`} size="small" color="primary" sx={{ ml: 1.5, fontWeight: 700, height: 20, fontSize: '0.75rem' }} />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                {yearlyData[0]?.year}—{yearlyData[yearlyData.length - 1]?.year}
              </Typography>
            </Box>
            
            {careerTotals && (
              <Grid container spacing={2}>
                {showBatting && careerTotals.batting.atBats > 0 && (
                  <>
                    {[
                      { label: 'Games', value: careerTotals.batting.gamesPlayed },
                      { label: 'AVG', value: careerTotals.batting.atBats > 0 ? (careerTotals.batting.hits / careerTotals.batting.atBats).toFixed(3) : '—' },
                      { label: 'HR', value: careerTotals.batting.homeRuns },
                      { label: 'RBI', value: careerTotals.batting.rbi },
                      { label: 'Hits', value: careerTotals.batting.hits },
                      { label: 'OPS', value: (() => {
                        const b = careerTotals.batting;
                        const obp = (b.atBats + b.baseOnBalls + b.hitByPitch + (b.sacrificeFlies || 0)) > 0
                          ? (b.hits + b.baseOnBalls + b.hitByPitch) / (b.atBats + b.baseOnBalls + b.hitByPitch + (b.sacrificeFlies || 0))
                          : 0;
                        const tb = b.hits + b.doubles + (2 * b.triples) + (3 * b.homeRuns);
                        const slg = b.atBats > 0 ? tb / b.atBats : 0;
                        return (obp + slg).toFixed(3);
                      })() },
                      { label: 'SB', value: careerTotals.batting.stolenBases },
                      { label: 'BB', value: careerTotals.batting.baseOnBalls }
                    ].map((stat, i) => (
                      <Grid item xs={6} sm={3} md={1.5} key={i}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                          <Typography variant="subtitle1" fontWeight={800} color="primary">{stat.value}</Typography>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.75rem' }}>{stat.label}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </>
                )}
                {showPitching && careerTotals.pitching.inningsPitched > 0 && (
                  <>
                    {[
                      { label: 'Games', value: careerTotals.pitching.gamesPlayed },
                      { label: 'ERA', value: careerTotals.pitching.inningsPitched > 0 ? ((careerTotals.pitching.earnedRuns * 9) / careerTotals.pitching.inningsPitched).toFixed(2) : '—' },
                      { label: 'W-L', value: `${careerTotals.pitching.wins}-${careerTotals.pitching.losses}` },
                      { label: 'K', value: careerTotals.pitching.strikeOuts },
                      { label: 'IP', value: formatInningsPitched(careerTotals.pitching.inningsPitched) },
                      { label: 'WHIP', value: careerTotals.pitching.inningsPitched > 0 ? ((careerTotals.pitching.hits + careerTotals.pitching.baseOnBalls) / careerTotals.pitching.inningsPitched).toFixed(2) : '—' },
                      { label: 'SV', value: careerTotals.pitching.saves },
                      { label: 'K/9', value: careerTotals.pitching.inningsPitched > 0 ? ((careerTotals.pitching.strikeOuts * 9) / careerTotals.pitching.inningsPitched).toFixed(1) : '—' }
                    ].map((stat, i) => (
                      <Grid item xs={6} sm={3} md={1.5} key={`p-${i}`}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.04) }}>
                          <Typography variant="subtitle1" fontWeight={800} color="warning.main">{stat.value}</Typography>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.75rem' }}>{stat.label}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </>
                )}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Year-by-Year Batting — BBRef style */}
      {showBatting && (
        <>
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.25, background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'white', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Year-by-Year Batting
                </Typography>
              </Box>
              <CardContent sx={{ p: 0 }}>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 1020, borderCollapse: 'collapse' }}>
                    <TableHead>
                      {/* Column header row */}
                      <TableRow sx={{ '& th': { bgcolor: HDR_BG, borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.35)}` } }}>
                        <SortTh col="year" sort={bSort} onSort={sortBat} align="left" sx={{ pl: 1, minWidth: 46 }}>Year</SortTh>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.6, minWidth: 40 }}>Tm</TableCell>
                        <SortTh col="G"   sort={bSort} onSort={sortBat} sx={{ minWidth: 32 }}>G</SortTh>
                        <SortTh col="PA"  sort={bSort} onSort={sortBat} sx={{ minWidth: 36 }}>PA</SortTh>
                        <SortTh col="AB"  sort={bSort} onSort={sortBat} sx={{ minWidth: 36 }}>AB</SortTh>
                        <SortTh col="H"   sort={bSort} onSort={sortBat} sx={{ minWidth: 32 }}>H</SortTh>
                        <SortTh col="2B"  sort={bSort} onSort={sortBat} sx={{ minWidth: 28 }}>2B</SortTh>
                        <SortTh col="3B"  sort={bSort} onSort={sortBat} sx={{ minWidth: 28 }}>3B</SortTh>
                        <SortTh col="HR"  sort={bSort} onSort={sortBat} sx={{ minWidth: 30 }}>HR</SortTh>
                        <SortTh col="RBI" sort={bSort} onSort={sortBat} sx={{ minWidth: 34 }}>RBI</SortTh>
                        <SortTh col="R"   sort={bSort} onSort={sortBat} sx={{ minWidth: 30 }}>R</SortTh>
                        <SortTh col="BB"  sort={bSort} onSort={sortBat} sx={{ minWidth: 30 }}>BB</SortTh>
                        <SortTh col="SO"  sort={bSort} onSort={sortBat} sx={{ minWidth: 30 }}>SO</SortTh>
                        <SortTh col="SB"  sort={bSort} onSort={sortBat} sx={{ minWidth: 30 }}>SB</SortTh>
                        <SortTh col="AVG"   sort={bSort} onSort={sortBat} sx={{ minWidth: 44, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>AVG</SortTh>
                        <SortTh col="OBP"   sort={bSort} onSort={sortBat} sx={{ minWidth: 44 }}>OBP</SortTh>
                        <SortTh col="SLG"   sort={bSort} onSort={sortBat} sx={{ minWidth: 44 }}>SLG</SortTh>
                        <SortTh col="OPS"   sort={bSort} onSort={sortBat} sx={{ minWidth: 50, fontWeight: 800 }}>OPS</SortTh>
                        <SortTh col="ISO"   sort={bSort} onSort={sortBat} sx={{ minWidth: 42, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>ISO</SortTh>
                        <SortTh col="BABIP" sort={bSort} onSort={sortBat} sx={{ minWidth: 48 }}>BABIP</SortTh>
                        <SortTh col="wOBA"  sort={bSort} onSort={sortBat} sx={{ minWidth: 48, fontWeight: 800 }}>wOBA</SortTh>
                        <SortTh col="WAR"   sort={bSort} onSort={sortBat} sx={{ minWidth: 38, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}`, fontWeight: 800 }}>WAR</SortTh>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedBatting.map((yd, idx) => {
                        const b   = yd.stats.batting;
                        const r   = battingRow(b);
                        const isCurrent = String(yd.year) === String(currentYear);
                        const rowBg = idx % 2 === 1 ? alpha(theme.palette.action.hover, 0.18) : 'transparent';
                        const fmt3 = v => v != null ? v.toFixed(3).replace(/^0/, '') : '—';
                        return (
                          <TableRow key={`${yd.year}-${yd.team}-b`} sx={{ bgcolor: rowBg, '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.35) } }}>
                            <TableCell sx={{ fontFamily: MONO, fontSize: '0.78rem', fontWeight: isCurrent ? 800 : 600, py: 0.45, pl: 1, color: isCurrent ? 'primary.main' : 'text.primary', borderLeft: isCurrent ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent' }}>{yd.year}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, py: 0.45 }}>{yd.team || '—'}</TableCell>
                            {numCell(b.gamesPlayed||0)}
                            {numCell(r.pa||0)}
                            {numCell(b.atBats||0)}
                            {numCell(b.hits||0)}
                            {numCell(b.doubles||0)}
                            {numCell(b.triples||0)}
                            {numCell(b.homeRuns||0, { sx: { fontWeight: 700 } })}
                            {numCell(b.rbi||0, { sx: { fontWeight: 700 } })}
                            {numCell(b.runs||0)}
                            {numCell(b.baseOnBalls||0)}
                            {numCell(b.strikeOuts||0)}
                            {numCell(b.stolenBases||0)}
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45, fontWeight: 700, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.25)}` }}>{fmt3(r.avg)}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45 }}>{fmt3(r.obp)}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45 }}>{fmt3(r.slg)}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45, fontWeight: 800 }}>{fmt3(r.ops)}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.25)}` }}>{fmt3(r.iso)}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45 }}>{fmt3(r.babip)}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45, fontWeight: 700 }}>{b.wOBA != null ? fmt3(b.wOBA) : '—'}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45, fontWeight: 700, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.25)}`, color: (yd.war||0) >= 5 ? 'success.main' : (yd.war||0) >= 2 ? 'text.primary' : 'text.secondary' }}>
                              {(yd.war||0).toFixed(1)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Career totals row */}
                      {careerTotals && careerTotals.batting.atBats > 0 && (() => {
                        const b = careerTotals.batting;
                        const r = battingRow(b);
                        const fmt3 = v => v != null ? v.toFixed(3).replace(/^0/, '') : '—';
                        const borderTop = `2px solid ${alpha(theme.palette.divider, 0.7)}`;
                        const totalBg  = alpha(theme.palette.action.selected, 0.07);
                        return (
                          <TableRow sx={{ '& td': { borderTop, bgcolor: totalBg, fontWeight: 800, fontFamily: MONO, fontSize: '0.78rem', py: 0.55 } }}>
                            <TableCell sx={{ pl: 1, color: 'text.primary', fontSize: '0.78rem !important', fontFamily: `inherit !important` }}>Career</TableCell>
                            <TableCell sx={{ fontFamily: 'inherit !important' }}>—</TableCell>
                            <TableCell align="center">{b.gamesPlayed||0}</TableCell>
                            <TableCell align="center">{r.pa||0}</TableCell>
                            <TableCell align="center">{b.atBats||0}</TableCell>
                            <TableCell align="center">{b.hits||0}</TableCell>
                            <TableCell align="center">{b.doubles||0}</TableCell>
                            <TableCell align="center">{b.triples||0}</TableCell>
                            <TableCell align="center">{b.homeRuns||0}</TableCell>
                            <TableCell align="center">{b.rbi||0}</TableCell>
                            <TableCell align="center">{b.runs||0}</TableCell>
                            <TableCell align="center">{b.baseOnBalls||0}</TableCell>
                            <TableCell align="center">{b.strikeOuts||0}</TableCell>
                            <TableCell align="center">{b.stolenBases||0}</TableCell>
                            <TableCell align="center" sx={{ borderLeft: `1px solid ${alpha(theme.palette.divider, 0.35)} !important` }}>{fmt3(r.avg)}</TableCell>
                            <TableCell align="center">{fmt3(r.obp)}</TableCell>
                            <TableCell align="center">{fmt3(r.slg)}</TableCell>
                            <TableCell align="center">{fmt3(r.ops)}</TableCell>
                            <TableCell align="center" sx={{ borderLeft: `1px solid ${alpha(theme.palette.divider, 0.35)} !important` }}>{fmt3(r.iso)}</TableCell>
                            <TableCell align="center">{fmt3(r.babip)}</TableCell>
                            <TableCell align="center">—</TableCell>
                            <TableCell align="center" sx={{ borderLeft: `1px solid ${alpha(theme.palette.divider, 0.35)} !important` }}>
                              {(unifiedProfile?.career?.war||0).toFixed(1)}
                            </TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </>
      )}

      {/* Year-by-Year Pitching — BBRef style */}
      {showPitching && (
        <>
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.25, background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.82rem', color: 'white', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Year-by-Year Pitching
                </Typography>
              </Box>
              <CardContent sx={{ p: 0 }}>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 980, borderCollapse: 'collapse' }}>
                    <TableHead>
                      {/* Column header row */}
                      <TableRow sx={{ '& th': { bgcolor: HDR_BG, borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.35)}` } }}>
                        <SortTh col="year" sort={pSort} onSort={sortPit} align="left" sx={{ pl: 1, minWidth: 46 }}>Year</SortTh>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.6, minWidth: 40 }}>Tm</TableCell>
                        <SortTh col="G"   sort={pSort} onSort={sortPit} sx={{ minWidth: 28 }}>G</SortTh>
                        <SortTh col="GS"  sort={pSort} onSort={sortPit} sx={{ minWidth: 28 }}>GS</SortTh>
                        <SortTh col="W"   sort={pSort} onSort={sortPit} sx={{ minWidth: 28 }}>W</SortTh>
                        <SortTh col="L"   sort={pSort} onSort={sortPit} sx={{ minWidth: 28 }}>L</SortTh>
                        <SortTh col="SV"  sort={pSort} onSort={sortPit} sx={{ minWidth: 28 }}>SV</SortTh>
                        <SortTh col="HLD" sort={pSort} onSort={sortPit} sx={{ minWidth: 30 }}>HLD</SortTh>
                        <SortTh col="IP"  sort={pSort} onSort={sortPit} sx={{ minWidth: 40 }}>IP</SortTh>
                        <SortTh col="H"   sort={pSort} onSort={sortPit} sx={{ minWidth: 28 }}>H</SortTh>
                        <SortTh col="ER"  sort={pSort} onSort={sortPit} sx={{ minWidth: 28 }}>ER</SortTh>
                        <SortTh col="HR"  sort={pSort} onSort={sortPit} sx={{ minWidth: 28 }}>HR</SortTh>
                        <SortTh col="BB"  sort={pSort} onSort={sortPit} sx={{ minWidth: 28 }}>BB</SortTh>
                        <SortTh col="K"   sort={pSort} onSort={sortPit} sx={{ minWidth: 28 }}>K</SortTh>
                        <SortTh col="ERA"  sort={pSort} onSort={sortPit} sx={{ minWidth: 44, fontWeight: 800, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>ERA</SortTh>
                        <SortTh col="WHIP" sort={pSort} onSort={sortPit} sx={{ minWidth: 44 }}>WHIP</SortTh>
                        <SortTh col="K9"   sort={pSort} onSort={sortPit} sx={{ minWidth: 40 }}>K/9</SortTh>
                        <SortTh col="BB9"  sort={pSort} onSort={sortPit} sx={{ minWidth: 40 }}>BB/9</SortTh>
                        <SortTh col="FIP"  sort={pSort} onSort={sortPit} sx={{ minWidth: 40, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>FIP</SortTh>
                        <SortTh col="BABIP" sort={pSort} onSort={sortPit} sx={{ minWidth: 46 }}>BABIP</SortTh>
                        <SortTh col="WAR"  sort={pSort} onSort={sortPit} sx={{ minWidth: 38, fontWeight: 800, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>WAR</SortTh>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedPitching.map((yd, idx) => {
                        const p = yd.stats.pitching;
                        const ip   = parseInningsPitched(p.inningsPitched);
                        const era  = ip > 0 ? ((p.earnedRuns||0) * 9) / ip : null;
                        const whip = ip > 0 ? ((p.hits||0) + (p.baseOnBalls||0)) / ip : null;
                        const k9   = ip > 0 ? ((p.strikeOuts||0) * 9) / ip : null;
                        const bb9  = ip > 0 ? ((p.baseOnBalls||0) * 9) / ip : null;
                        // pitching BABIP: (H - HR) / (BF - K - HR - BB) approximation
                        const bf   = p.battersFaced || 0;
                        const pbabip = bf > 0
                          ? ((p.hits||0) - (p.homeRuns||0)) / (bf - (p.strikeOuts||0) - (p.homeRuns||0) - (p.baseOnBalls||0) + 0.001)
                          : null;
                        const isCurrent = String(yd.year) === String(currentYear);
                        const rowBg = idx % 2 === 1 ? alpha(theme.palette.action.hover, 0.18) : 'transparent';
                        return (
                          <TableRow key={`${yd.year}-${yd.team}-p`} sx={{ bgcolor: rowBg, '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.35) } }}>
                            <TableCell sx={{ fontFamily: MONO, fontSize: '0.78rem', fontWeight: isCurrent ? 800 : 600, py: 0.45, pl: 1, color: isCurrent ? 'primary.main' : 'text.primary', borderLeft: isCurrent ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent' }}>{yd.year}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, py: 0.45 }}>{yd.team || '—'}</TableCell>
                            {numCell(p.gamesPlayed||0)}
                            {numCell(p.gamesStarted||0)}
                            {numCell(p.wins||0, { sx: { fontWeight: 700 } })}
                            {numCell(p.losses||0)}
                            {numCell(p.saves||0)}
                            {numCell(p.holds||0)}
                            {numCell(formatInningsPitched(ip))}
                            {numCell(p.hits||0)}
                            {numCell(p.earnedRuns||0)}
                            {numCell(p.homeRuns||0)}
                            {numCell(p.baseOnBalls||0)}
                            {numCell(p.strikeOuts||0, { sx: { fontWeight: 700 } })}
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45, fontWeight: 800, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.25)}`, color: era != null && era < 3.5 ? 'success.main' : era != null && era > 5 ? 'error.main' : 'text.primary' }}>{era != null ? era.toFixed(2) : '—'}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45, fontWeight: 700 }}>{whip != null ? whip.toFixed(2) : '—'}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45 }}>{k9 != null ? k9.toFixed(1) : '—'}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45 }}>{bb9 != null ? bb9.toFixed(1) : '—'}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.25)}` }}>{p.fip != null ? parseFloat(p.fip).toFixed(2) : '—'}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45 }}>{pbabip != null && pbabip > 0 ? pbabip.toFixed(3).replace(/^0/,'') : '—'}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: MONO, fontSize: '0.78rem', py: 0.45, fontWeight: 700, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.25)}`, color: (yd.war||0) >= 4 ? 'success.main' : (yd.war||0) >= 2 ? 'text.primary' : 'text.secondary' }}>
                              {(yd.war||0).toFixed(1)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Career totals row */}
                      {careerTotals && careerTotals.pitching.inningsPitched > 0 && (() => {
                        const p = careerTotals.pitching;
                        const ip   = p.inningsPitched;
                        const era  = ip > 0 ? (p.earnedRuns * 9) / ip : null;
                        const whip = ip > 0 ? ((p.hits||0) + (p.baseOnBalls||0)) / ip : null;
                        const k9   = ip > 0 ? ((p.strikeOuts||0) * 9) / ip : null;
                        const bb9  = ip > 0 ? ((p.baseOnBalls||0) * 9) / ip : null;
                        const borderTop = `2px solid ${alpha(theme.palette.divider, 0.7)}`;
                        const totalBg  = alpha(theme.palette.action.selected, 0.07);
                        return (
                          <TableRow sx={{ '& td': { borderTop, bgcolor: totalBg, fontWeight: 800, fontFamily: MONO, fontSize: '0.78rem', py: 0.55 } }}>
                            <TableCell sx={{ pl: 1, color: 'text.primary', fontSize: '0.78rem !important', fontFamily: 'inherit !important' }}>Career</TableCell>
                            <TableCell sx={{ fontFamily: 'inherit !important' }}>—</TableCell>
                            <TableCell align="center">{p.gamesPlayed||0}</TableCell>
                            <TableCell align="center">{p.gamesStarted||0}</TableCell>
                            <TableCell align="center">{p.wins||0}</TableCell>
                            <TableCell align="center">{p.losses||0}</TableCell>
                            <TableCell align="center">{p.saves||0}</TableCell>
                            <TableCell align="center">{p.holds||0}</TableCell>
                            <TableCell align="center">{formatInningsPitched(ip)}</TableCell>
                            <TableCell align="center">{p.hits||0}</TableCell>
                            <TableCell align="center">{p.earnedRuns||0}</TableCell>
                            <TableCell align="center">{p.homeRuns||0}</TableCell>
                            <TableCell align="center">{p.baseOnBalls||0}</TableCell>
                            <TableCell align="center">{p.strikeOuts||0}</TableCell>
                            <TableCell align="center" sx={{ borderLeft: `1px solid ${alpha(theme.palette.divider, 0.35)} !important` }}>{era != null ? era.toFixed(2) : '—'}</TableCell>
                            <TableCell align="center">{whip != null ? whip.toFixed(2) : '—'}</TableCell>
                            <TableCell align="center">{k9 != null ? k9.toFixed(1) : '—'}</TableCell>
                            <TableCell align="center">{bb9 != null ? bb9.toFixed(1) : '—'}</TableCell>
                            <TableCell align="center" sx={{ borderLeft: `1px solid ${alpha(theme.palette.divider, 0.35)} !important` }}>—</TableCell>
                            <TableCell align="center">—</TableCell>
                            <TableCell align="center" sx={{ borderLeft: `1px solid ${alpha(theme.palette.divider, 0.35)} !important` }}>
                              {(unifiedProfile?.career?.war||0).toFixed(1)}
                            </TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </>
      )}
    </Grid>
  );
};

// ============================================================================
// ADVANCED ANALYTICS TAB — Polished, data-dense analytics dashboard
// ============================================================================

// Stat definitions: label, league avg, elite, poor, format, invert (lower=better), grade thresholds
const STAT_DEFS = {
  // Batting
  woba:    { label: 'wOBA',  avg: 0.315, elite: 0.380, poor: 0.260, fmt: v => v?.toFixed(3)?.replace(/^0/,''), unit: '', invert: false, cat: 'bat' },
  iso:     { label: 'ISO',   avg: 0.151, elite: 0.250, poor: 0.080, fmt: v => v?.toFixed(3)?.replace(/^0/,''), unit: '', invert: false, cat: 'bat' },
  babip:   { label: 'BABIP', avg: 0.300, elite: 0.340, poor: 0.260, fmt: v => v?.toFixed(3)?.replace(/^0/,''), unit: '', invert: false, cat: 'bat' },
  kRate:   { label: 'K%',    avg: 0.225, elite: 0.140, poor: 0.320, fmt: v => (v*100).toFixed(1)+'%', unit: '', invert: true, cat: 'bat' },
  bbRate:  { label: 'BB%',   avg: 0.082, elite: 0.130, poor: 0.040, fmt: v => (v*100).toFixed(1)+'%', unit: '', invert: false, cat: 'bat' },
  opsPlus: { label: 'OPS+',  avg: 100, elite: 145, poor: 75, fmt: v => Math.round(v), unit: '', invert: false, cat: 'bat' },
  wrcPlus: { label: 'wRC+',  avg: 100, elite: 145, poor: 75, fmt: v => Math.round(v), unit: '', invert: false, cat: 'bat' },
  contactRate: { label: 'Contact%', avg: 0.775, elite: 0.870, poor: 0.680, fmt: v => (v*100).toFixed(1)+'%', unit: '', invert: false, cat: 'bat' },
  kbbRatio: { label: 'BB/K', avg: 0.36, elite: 0.70, poor: 0.15, fmt: v => v?.toFixed(2), unit: '', invert: false, cat: 'bat' },
  powerSpeed: { label: 'Pwr-Spd', avg: 8, elite: 25, poor: 2, fmt: v => v?.toFixed(1), unit: '', invert: false, cat: 'bat' },
  // Pitching
  fip:      { label: 'FIP',   avg: 4.10, elite: 2.80, poor: 5.40, fmt: v => v?.toFixed(2), unit: '', invert: true, cat: 'pit' },
  xfip:     { label: 'xFIP',  avg: 4.10, elite: 3.00, poor: 5.40, fmt: v => v?.toFixed(2), unit: '', invert: true, cat: 'pit' },
  siera:    { label: 'SIERA', avg: 4.10, elite: 2.80, poor: 5.40, fmt: v => v?.toFixed(2), unit: '', invert: true, cat: 'pit' },
  eraMinus: { label: 'ERA+',  avg: 100, elite: 160, poor: 70, fmt: v => Math.round(v), unit: '', invert: false, cat: 'pit' },
  fipMinus: { label: 'FIP-',  avg: 100, elite: 65, poor: 130, fmt: v => Math.round(v), unit: '', invert: true, cat: 'pit' },
  k9:       { label: 'K/9',   avg: 8.5, elite: 11.5, poor: 5.5, fmt: v => v?.toFixed(1), unit: '', invert: false, cat: 'pit' },
  bb9:      { label: 'BB/9',  avg: 3.2, elite: 1.8, poor: 4.8, fmt: v => v?.toFixed(1), unit: '', invert: true, cat: 'pit' },
  hr9:      { label: 'HR/9',  avg: 1.25, elite: 0.60, poor: 1.80, fmt: v => v?.toFixed(2), unit: '', invert: true, cat: 'pit' },
  pitchKbbRatio: { label: 'K/BB', avg: 2.7, elite: 5.0, poor: 1.5, fmt: v => v?.toFixed(2), unit: '', invert: false, cat: 'pit' },
};

const getStatPct = (value, def) => {
  if (def == null || value == null) return 50;
  const { avg, elite, invert } = def;
  let pct;
  if (invert) {
    pct = ((avg - value) / (avg - elite)) * 50 + 50;
  } else {
    pct = ((value - avg) / (elite - avg)) * 50 + 50;
  }
  return Math.max(2, Math.min(98, pct));
};

const GRADE_COLORS_AA = {
  'A+': '#00e676', A: '#4caf50', 'A-': '#66bb6a',
  'B+': '#8bc34a', B: '#cddc39', 'B-': '#fdd835',
  'C+': '#ffc107', C: '#ff9800', 'C-': '#ff7043',
  D: '#f44336', F: '#d32f2f',
};

const pctToGrade = (pct) => {
  if (pct >= 95) return 'A+';
  if (pct >= 85) return 'A';
  if (pct >= 75) return 'A-';
  if (pct >= 67) return 'B+';
  if (pct >= 58) return 'B';
  if (pct >= 50) return 'B-';
  if (pct >= 42) return 'C+';
  if (pct >= 35) return 'C';
  if (pct >= 28) return 'C-';
  if (pct >= 18) return 'D';
  return 'F';
};

const ordinal = (n) => {
  const r = Math.round(n);
  const s = ['th', 'st', 'nd', 'rd'];
  const v = r % 100;
  return r + (s[(v - 20) % 10] || s[v] || s[0]);
};

const AdvancedAnalyticsTab = ({ profile, player, playerType, seasonStats, gameTypeFilter }) => {
  const theme = useTheme();
  const isFiltered = gameTypeFilter && gameTypeFilter !== 'all';
  const filterLabel = gameTypeFilter === 'S' ? 'Spring Training' : gameTypeFilter === 'P' ? 'Postseason' : gameTypeFilter === 'R' ? 'Regular Season' : null;

  const profileAdvanced = profile?.profile?.advanced || {};
  const profileParkAdj = profile?.profile?.parkAdjusted || {};

  // ── Compute advanced stats from filtered seasonStats when a game type filter is active ──
  const { advanced, parkAdjusted } = React.useMemo(() => {
    // No filter or 'all' → use profile (full season) stats as-is
    if (!isFiltered || !seasonStats) {
      return { advanced: profileAdvanced, parkAdjusted: profileParkAdj };
    }
    // Compute what we can from the filtered seasonStats
    const b = seasonStats.batting || {};
    const p = seasonStats.pitching || {};
    const computed = {};
    // Batting advanced stats
    const pa = (b.atBats || 0) + (b.baseOnBalls || 0) + (b.hitByPitch || 0) + (b.sacFlies || b.sacrificeFlies || 0);
    if (pa > 0) {
      computed.kRate = +((b.strikeOuts || 0) / pa).toFixed(3);
      computed.bbRate = +((b.baseOnBalls || 0) / pa).toFixed(3);
      if (computed.bbRate > 0) computed.kbbRatio = +(computed.kRate / computed.bbRate).toFixed(2);
    }
    if (b.atBats > 0) {
      const avg = (b.hits || 0) / b.atBats;
      const tb = (b.hits || 0) + (b.doubles || 0) + 2 * (b.triples || 0) + 3 * (b.homeRuns || 0);
      const slg = tb / b.atBats;
      computed.iso = +(slg - avg).toFixed(3);
      // BABIP = (H - HR) / (AB - K - HR + SF)
      const babipDenom = b.atBats - (b.strikeOuts || 0) - (b.homeRuns || 0) + (b.sacFlies || b.sacrificeFlies || 0);
      if (babipDenom > 0) {
        computed.babip = +(((b.hits || 0) - (b.homeRuns || 0)) / babipDenom).toFixed(3);
      }
    }
    if (pa > 0) {
      // wOBA using standard linear weights (2024 season constants)
      const uBB = (b.baseOnBalls || 0) - (b.intentionalWalks || 0);
      const hbp = b.hitByPitch || 0;
      const singles = (b.hits || 0) - (b.doubles || 0) - (b.triples || 0) - (b.homeRuns || 0);
      const woba = (0.69 * uBB + 0.72 * hbp + 0.888 * singles + 1.271 * (b.doubles || 0) + 1.616 * (b.triples || 0) + 2.101 * (b.homeRuns || 0)) / pa;
      if (!isNaN(woba)) computed.woba = +woba.toFixed(3);
    }
    // Pitching advanced stats
    const ip = parseFloat(p.inningsPitched) || 0;
    if (ip > 0) {
      computed.k9 = +((((p.strikeOuts || 0) * 9) / ip).toFixed(2));
      computed.bb9 = +((((p.baseOnBalls || 0) * 9) / ip).toFixed(2));
      computed.hr9 = +((((p.homeRuns || 0) * 9) / ip).toFixed(2));
      if ((p.baseOnBalls || 0) > 0) computed.kbbRatio = +((p.strikeOuts || 0) / p.baseOnBalls).toFixed(2);
      // FIP = ((13*HR + 3*BB - 2*K) / IP) + constant (~3.10)
      const FIP_CONSTANT = 3.10;
      computed.fip = +((13 * (p.homeRuns || 0) + 3 * (p.baseOnBalls || 0) - 2 * (p.strikeOuts || 0)) / ip + FIP_CONSTANT).toFixed(2);
    }
    // Merge: filtered computed stats take priority, fall back to profile for complex metrics
    const merged = { ...profileAdvanced, ...computed };
    // Park-adjusted stats can't be recomputed client-side — use profile values
    return { advanced: merged, parkAdjusted: profileParkAdj };
  }, [isFiltered, seasonStats, profileAdvanced, profileParkAdj]);

  if (!profile) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, p: 4, textAlign: 'center' }}>
        <Assessment sx={{ fontSize: 40, color: 'text.secondary', mb: 1.5 }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
          Advanced Analytics Loading...
        </Typography>
        <LinearProgress sx={{ maxWidth: 240, mx: 'auto', borderRadius: 2 }} />
      </Card>
    );
  }

  const cvrData = profile.cvr;
  const warV2 = profile.war;
  const tradeValue = profile.tradeValue;
  const parkFactors = profile.parkFactors;
  const streaks = profile.streaks;
  const traditional = profile.profile?.traditional || {};
  const isPitcher = profile.profile?.isPitcher;

  // ── Build hero stat entries ──
  const heroStats = [];
  if (!isPitcher) {
    if (parkAdjusted.wrcPlus) heroStats.push({ key: 'wrcPlus', value: parkAdjusted.wrcPlus });
    if (parkAdjusted.opsPlus) heroStats.push({ key: 'opsPlus', value: parkAdjusted.opsPlus });
    if (advanced.woba) heroStats.push({ key: 'woba', value: advanced.woba });
    if (advanced.iso) heroStats.push({ key: 'iso', value: advanced.iso });
    if (advanced.kRate != null) heroStats.push({ key: 'kRate', value: advanced.kRate });
    if (advanced.bbRate != null) heroStats.push({ key: 'bbRate', value: advanced.bbRate });
  } else {
    if (parkAdjusted.eraPlus) heroStats.push({ key: 'eraMinus', value: parkAdjusted.eraPlus });
    if (parkAdjusted.fipMinus) heroStats.push({ key: 'fipMinus', value: parkAdjusted.fipMinus });
    if (advanced.fip) heroStats.push({ key: 'fip', value: advanced.fip });
    if (advanced.xfip) heroStats.push({ key: 'xfip', value: advanced.xfip });
    if (advanced.k9) heroStats.push({ key: 'k9', value: advanced.k9 });
    if (advanced.bb9) heroStats.push({ key: 'bb9', value: advanced.bb9 });
  }

  // ── Build sabermetric bar data ──
  const batBarStats = [];
  const pitBarStats = [];
  // Batting
  if (advanced.woba) batBarStats.push({ key: 'woba', value: advanced.woba });
  if (advanced.iso) batBarStats.push({ key: 'iso', value: advanced.iso });
  if (advanced.babip) batBarStats.push({ key: 'babip', value: advanced.babip });
  if (advanced.kRate != null) batBarStats.push({ key: 'kRate', value: advanced.kRate });
  if (advanced.bbRate != null) batBarStats.push({ key: 'bbRate', value: advanced.bbRate });
  if (advanced.contactRate != null && advanced.contactRate > 0) batBarStats.push({ key: 'contactRate', value: advanced.contactRate });
  if (advanced.kbbRatio != null && advanced.kbbRatio > 0) batBarStats.push({ key: 'kbbRatio', value: advanced.kbbRatio });
  if (advanced.powerSpeed != null && advanced.powerSpeed > 0) batBarStats.push({ key: 'powerSpeed', value: advanced.powerSpeed });
  if (parkAdjusted.opsPlus) batBarStats.push({ key: 'opsPlus', value: parkAdjusted.opsPlus });
  if (parkAdjusted.wrcPlus) batBarStats.push({ key: 'wrcPlus', value: parkAdjusted.wrcPlus });
  // Pitching
  if (advanced.fip) pitBarStats.push({ key: 'fip', value: advanced.fip });
  if (advanced.xfip) pitBarStats.push({ key: 'xfip', value: advanced.xfip });
  if (advanced.siera) pitBarStats.push({ key: 'siera', value: advanced.siera });
  if (advanced.k9) pitBarStats.push({ key: 'k9', value: advanced.k9 });
  if (advanced.bb9) pitBarStats.push({ key: 'bb9', value: advanced.bb9 });
  if (advanced.hr9) pitBarStats.push({ key: 'hr9', value: advanced.hr9 });
  if (advanced.kbbRatio != null && advanced.kbbRatio > 0 && isPitcher) pitBarStats.push({ key: 'pitchKbbRatio', value: advanced.kbbRatio });
  if (parkAdjusted.eraPlus) pitBarStats.push({ key: 'eraMinus', value: parkAdjusted.eraPlus });
  if (parkAdjusted.fipMinus) pitBarStats.push({ key: 'fipMinus', value: parkAdjusted.fipMinus });

  const hasBat = batBarStats.length > 0;
  const hasPit = pitBarStats.length > 0;

  // ── Shared styles ──
  const cardSx = {
    height: '100%', borderRadius: 2, overflow: 'hidden',
    border: `1px solid ${alpha(theme.palette.divider, 0.10)}`,
    bgcolor: alpha(theme.palette.background.paper, 0.85),
  };

  const AccentBar = ({ color }) => (
    <Box sx={{ height: 3, background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.3)})` }} />
  );

  const SectionLabel = ({ children, icon, accent }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
      {icon && <Box sx={{ color: accent || 'primary.main', display: 'flex', fontSize: 16 }}>{icon}</Box>}
      <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, color: 'text.primary' }}>
        {children}
      </Typography>
    </Box>
  );

  // ── Hero stat tile ──
  const HeroTile = ({ statKey, value }) => {
    const def = STAT_DEFS[statKey];
    if (!def || value == null) return null;
    const pct = getStatPct(value, def);
    const grade = pctToGrade(pct);
    const gradeColor = GRADE_COLORS_AA[grade] || '#888';
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <Box sx={{
          textAlign: 'center', p: 1.5, borderRadius: 2, position: 'relative', overflow: 'hidden',
          border: `1px solid ${alpha(gradeColor, 0.25)}`,
          background: `linear-gradient(135deg, ${alpha(gradeColor, 0.06)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
          minWidth: 90,
        }}>
          {/* Background percentile fill */}
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: `${pct * 0.4}%`,
            background: `linear-gradient(0deg, ${alpha(gradeColor, 0.08)}, transparent)`,
            pointerEvents: 'none',
          }} />
          <Typography variant="h5" fontWeight={800} sx={{ color: gradeColor, lineHeight: 1, mb: 0.25, position: 'relative' }}>
            {def.fmt(value)}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', display: 'block', mb: 0.5, position: 'relative' }}>
            {def.label}
          </Typography>
          <Chip
            label={grade}
            size="small"
            sx={{
              height: 20, fontSize: '0.75rem', fontWeight: 800,
              bgcolor: alpha(gradeColor, 0.15), color: gradeColor,
              border: `1px solid ${alpha(gradeColor, 0.3)}`,
              position: 'relative',
            }}
          />
        </Box>
      </motion.div>
    );
  };

  // ── Sabermetric bar row ──
  const StatBar = ({ statKey, value, delay = 0 }) => {
    const def = STAT_DEFS[statKey];
    if (!def || value == null) return null;
    const pct = getStatPct(value, def);
    const grade = pctToGrade(pct);
    const gradeColor = GRADE_COLORS_AA[grade] || '#888';
    const percentileLabel = ordinal(pct);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 1 }}>
        {/* Label */}
        <Typography variant="caption" sx={{ minWidth: 56, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right', letterSpacing: 0.3 }}>
          {def.label}
        </Typography>
        {/* Bar container */}
        <Box sx={{ flex: 1, position: 'relative', height: 22, borderRadius: 2, bgcolor: alpha(theme.palette.divider, 0.08), overflow: 'hidden' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.7, delay: delay * 0.05, ease: 'easeOut' }}
            style={{
              height: '100%', borderRadius: 8,
              background: `linear-gradient(90deg, ${alpha(gradeColor, 0.5)}, ${alpha(gradeColor, 0.25)})`,
            }}
          />
          {/* League avg dashed line at 50% */}
          <Box sx={{
            position: 'absolute', top: 2, bottom: 2, left: '50%',
            borderLeft: `1.5px dashed ${alpha(theme.palette.text.secondary, 0.25)}`,
          }} />
        </Box>
        {/* Value */}
        <Typography variant="caption" sx={{ minWidth: 48, textAlign: 'right', fontWeight: 800, fontSize: '0.75rem', color: gradeColor }}>
          {def.fmt(value)}
        </Typography>
        {/* Grade pill */}
        <Box sx={{
          minWidth: 26, height: 18, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(gradeColor, 0.15), border: `1px solid ${alpha(gradeColor, 0.3)}`,
        }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: gradeColor, lineHeight: 1 }}>{grade}</Typography>
        </Box>
        {/* Percentile */}
        <Typography variant="caption" sx={{ minWidth: 32, fontSize: '0.75rem', fontWeight: 600, color: alpha(theme.palette.text.secondary, 0.6), textAlign: 'right' }}>
          {percentileLabel}
        </Typography>
      </Box>
    );
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* ── Game type filter notice ── */}
        {isFiltered && (
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 2, py: 0.5, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>
            Showing {filterLabel ? <strong>{filterLabel}</strong> : 'filtered'} advanced stats (K%, BB%, ISO, BABIP, K/9, BB/9, FIP recalculated). Park-adjusted metrics (OPS+, wRC+, wOBA) reflect full-season data.
          </Alert>
        )}

        {/* ── ROW 0: Hero Metrics At-a-Glance ── */}
        {heroStats.length > 0 && (
          <Card elevation={0} sx={{ ...cardSx, overflow: 'visible' }}>
            <AccentBar color={theme.palette.primary.main} />
            <CardContent sx={{ p: 2, pb: 1.5 }}>
              <SectionLabel icon={<TrendingUp sx={{ fontSize: 16 }} />} accent={theme.palette.primary.main}>
                {isPitcher ? 'Pitching Metrics' : 'Batting Metrics'} — At a Glance
              </SectionLabel>
              <Grid container spacing={1.5}>
                {heroStats.map(({ key, value }) => (
                  <Grid item xs={4} sm={2} key={key}>
                    <HeroTile statKey={key} value={value} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* ── ROW 1: CVR + WAR + Trade Value ── */}
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={cardSx}>
              <AccentBar color="#ff6b35" />
              <CardContent sx={{ p: 2 }}>
                <SectionLabel accent="#ff6b35">CVR — Composite</SectionLabel>
                <CVRRadarChart cvrData={cvrData} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={cardSx}>
              <AccentBar color="#42a5f5" />
              <CardContent sx={{ p: 2 }}>
                <SectionLabel accent="#42a5f5">WAR — Wins Above Replacement</SectionLabel>
                <WARDisplay
                  warData={warV2}
                  gamesPlayed={Math.max(
                    seasonStats?.batting?.gamesPlayed || 0,
                    seasonStats?.pitching?.gamesPlayed || 0,
                    player?.gameCount || 0,
                  ) || 162}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ ...cardSx, display: 'flex', flexDirection: 'column' }}>
              <AccentBar color="#66bb6a" />
              <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                <SectionLabel accent="#66bb6a">Trade Value</SectionLabel>
                <TradeValueGauge
                  value={tradeValue?.tradeValue || tradeValue?.total || 0}
                  surplusValue={tradeValue?.components?.contract?.surplus || tradeValue?.surplusValue}
                />
                {(tradeValue?.tier || tradeValue?.components) && (
                  <Chip
                    label={`${typeof tradeValue.tier === 'object' ? tradeValue.tier.label : tradeValue.tier} Asset`}
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1.5, fontWeight: 700, fontSize: '0.75rem', height: 24, borderColor: alpha(theme.palette.success.main, 0.4) }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── ROW 2: Sabermetric Profile Bars ── */}
        {(hasBat || hasPit) && (
          <Grid container spacing={1.5}>
            {hasBat && (
              <Grid item xs={12} md={hasPit ? 6 : 12}>
                <Card elevation={0} sx={cardSx}>
                  <AccentBar color={theme.palette.info.main} />
                  <CardContent sx={{ p: 2 }}>
                    <SectionLabel icon={<Assessment sx={{ fontSize: 16 }} />} accent={theme.palette.info.main}>
                      Batting — Advanced Profile
                    </SectionLabel>
                    {batBarStats.map((s, i) => (
                      <StatBar key={s.key} statKey={s.key} value={s.value} delay={i} />
                    ))}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, gap: 0.75, opacity: 0.45 }}>
                      <Box sx={{ width: 14, height: 0, borderTop: '1.5px dashed', borderColor: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', letterSpacing: 0.3 }}>League Average</Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Grade scale: A+ elite → F poor</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {hasPit && (
              <Grid item xs={12} md={hasBat ? 6 : 12}>
                <Card elevation={0} sx={cardSx}>
                  <AccentBar color={theme.palette.warning.main} />
                  <CardContent sx={{ p: 2 }}>
                    <SectionLabel icon={<Assessment sx={{ fontSize: 16 }} />} accent={theme.palette.warning.main}>
                      Pitching — Fielding Independent
                    </SectionLabel>
                    {pitBarStats.map((s, i) => (
                      <StatBar key={s.key} statKey={s.key} value={s.value} delay={i} />
                    ))}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, gap: 0.75, opacity: 0.45 }}>
                      <Box sx={{ width: 14, height: 0, borderTop: '1.5px dashed', borderColor: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', letterSpacing: 0.3 }}>League Average</Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Lower is better for ERA/FIP/xFIP</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}

        {/* ── ROW 3: Park Factors + Recent Form ── */}
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={cardSx}>
              <AccentBar color={theme.palette.secondary.main} />
              <CardContent sx={{ p: 2 }}>
                <SectionLabel icon={<SportsBaseball sx={{ fontSize: 16 }} />} accent={theme.palette.secondary.main}>
                  Park Factor Context
                </SectionLabel>
                {parkFactors ? (
                  <Box>
                    {parkFactors.name && (
                      <Chip
                        icon={<SportsBaseball sx={{ fontSize: 13 }} />}
                        label={parkFactors.name}
                        size="small"
                        sx={{
                          mb: 1.5, fontWeight: 600, fontSize: '0.75rem', height: 24,
                          bgcolor: alpha(theme.palette.secondary.main, 0.08),
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                        }}
                      />
                    )}
                    {Object.entries(parkFactors)
                      .filter(([, v]) => typeof v === 'number')
                      .map(([key, value]) => {
                        const barPct = Math.min(100, Math.max(5, (value - 80) * (100 / 40)));
                        const deviation = value - 100;
                        const barColor =
                          Math.abs(deviation) <= 3 ? theme.palette.text.secondary :
                          deviation > 8 ? theme.palette.error.main :
                          deviation > 0 ? theme.palette.warning.main :
                          deviation < -8 ? theme.palette.info.main :
                          theme.palette.success.main;
                        return (
                          <Box key={key} sx={{ display: 'flex', alignItems: 'center', mb: 0.6, gap: 1 }}>
                            <Typography variant="caption" sx={{ minWidth: 56, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                              {{ hr: 'Home Runs', runs: 'Runs', hits: 'Hits', doubles: 'Doubles', triples: 'Triples', walks: 'Walks', strikeouts: 'Strikeouts' }[key] || key.charAt(0).toUpperCase() + key.slice(1)}
                            </Typography>
                            <Box sx={{ flex: 1, position: 'relative', height: 14, borderRadius: 1.5, bgcolor: alpha(theme.palette.divider, 0.08), overflow: 'hidden' }}>
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${barPct}%` }}
                                transition={{ duration: 0.5, ease: 'easeOut' }}
                                style={{
                                  height: '100%', borderRadius: 6,
                                  background: `linear-gradient(90deg, ${alpha(barColor, 0.45)}, ${alpha(barColor, 0.15)})`,
                                }}
                              />
                              {/* Neutral (100) marker */}
                              <Box sx={{ position: 'absolute', top: 1, bottom: 1, left: '50%', borderLeft: `1.5px dashed ${alpha(theme.palette.text.secondary, 0.25)}` }} />
                            </Box>
                            <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', minWidth: 30, textAlign: 'right', color: barColor }}>
                              {value}
                            </Typography>
                          </Box>
                        );
                      })}
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, fontSize: '0.75rem', opacity: 0.4, letterSpacing: 0.2 }}>
                      100 = neutral park; {'>'} 100 favors hitters
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ py: 3, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">No park factor data available</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            {/* ── PLAYER DNA — signature strengths/weaknesses derived from actual stats ── */}
            {(() => {
              // Build a ranked list of stats compared to league average.
              // We compute a "percentile" proxy: how far the value is from avg relative to the elite<>poor range.
              const allDnaStats = isPitcher
                ? [
                    { key: 'K/9',      val: advanced.k9,       avg: 8.5,  elite: 12.0, poor: 5.5,  invert: false, fmt: v => v?.toFixed(1) },
                    { key: 'BB/9',     val: advanced.bb9,      avg: 3.0,  elite: 1.5,  poor: 5.0,  invert: true,  fmt: v => v?.toFixed(1) },
                    { key: 'K/BB',     val: advanced.kbbRatio, avg: 2.8,  elite: 5.0,  poor: 1.2,  invert: false, fmt: v => v?.toFixed(2) },
                    { key: 'FIP',      val: advanced.fip,      avg: 4.20, elite: 2.80, poor: 5.50, invert: true,  fmt: v => v?.toFixed(2) },
                    { key: 'xFIP',     val: advanced.xfip,     avg: 4.20, elite: 2.80, poor: 5.50, invert: true,  fmt: v => v?.toFixed(2) },
                    { key: 'ERA+',     val: parkAdjusted.eraPlus, avg: 100, elite: 160, poor: 60, invert: false, fmt: v => Math.round(v) },
                    { key: 'FIP-',     val: parkAdjusted.fipMinus, avg: 100, elite: 60, poor: 140, invert: true, fmt: v => Math.round(v) },
                    { key: 'HR/9',     val: advanced.hr9,      avg: 1.3,  elite: 0.5,  poor: 2.2,  invert: true,  fmt: v => v?.toFixed(2) },
                  ]
                : [
                    { key: 'wOBA',     val: advanced.woba,     avg: 0.315, elite: 0.380, poor: 0.260, invert: false, fmt: v => v?.toFixed(3)?.replace(/^0/,'') },
                    { key: 'ISO',      val: advanced.iso,      avg: 0.151, elite: 0.260, poor: 0.070, invert: false, fmt: v => v?.toFixed(3)?.replace(/^0/,'') },
                    { key: 'BABIP',    val: advanced.babip,    avg: 0.300, elite: 0.360, poor: 0.240, invert: false, fmt: v => v?.toFixed(3)?.replace(/^0/,'') },
                    { key: 'K%',       val: advanced.kRate,    avg: 0.225, elite: 0.120, poor: 0.320, invert: true,  fmt: v => (v*100).toFixed(0)+'%' },
                    { key: 'BB%',      val: advanced.bbRate,   avg: 0.082, elite: 0.140, poor: 0.040, invert: false, fmt: v => (v*100).toFixed(0)+'%' },
                    { key: 'OPS+',     val: parkAdjusted.opsPlus, avg: 100, elite: 150, poor: 65, invert: false, fmt: v => Math.round(v) },
                    { key: 'wRC+',     val: parkAdjusted.wrcPlus, avg: 100, elite: 150, poor: 65, invert: false, fmt: v => Math.round(v) },
                    { key: 'Contact%', val: advanced.contactRate, avg: 0.775, elite: 0.870, poor: 0.640, invert: false, fmt: v => (v*100).toFixed(1)+'%' },
                  ];

              // Score each stat: 0–100 percentile proxy. 50 = league avg.
              const scored = allDnaStats
                .filter(s => s.val != null && !isNaN(s.val))
                .map(s => {
                  const range = Math.abs(s.elite - s.poor);
                  const raw = s.invert
                    ? (s.poor - s.val) / range
                    : (s.val - s.poor) / range;
                  const pct = Math.round(Math.min(99, Math.max(1, raw * 100)));
                  return { ...s, pct };
                })
                .sort((a, b) => b.pct - a.pct);

              if (scored.length === 0) return (
                <Card elevation={0} sx={cardSx}>
                  <AccentBar color="#ab47bc" />
                  <CardContent sx={{ p: 2 }}>
                    <SectionLabel icon={<Assessment sx={{ fontSize: 16 }} />} accent="#ab47bc">Player DNA</SectionLabel>
                    <Box sx={{ py: 3, textAlign: 'center' }}><Typography variant="caption" color="text.secondary">No data yet</Typography></Box>
                  </CardContent>
                </Card>
              );

              const top = scored[0];
              const bottom = scored[scored.length - 1];
              const elite5 = scored.filter(s => s.pct >= 80);
              const weak5  = scored.filter(s => s.pct <= 30);

              const pctColor = (pct) =>
                pct >= 80 ? '#ff6b35' :
                pct >= 60 ? '#ffd23f' :
                pct >= 40 ? '#42a5f5' :
                pct >= 20 ? '#78909c' : '#ef5350';

              const pctLabel = (pct) =>
                pct >= 90 ? 'Elite' :
                pct >= 75 ? 'Great' :
                pct >= 55 ? 'Above Avg' :
                pct >= 40 ? 'Average' :
                pct >= 25 ? 'Below Avg' : 'Poor';

              return (
                <Card elevation={0} sx={cardSx}>
                  <AccentBar color="#ab47bc" />
                  <CardContent sx={{ p: 2 }}>
                    <SectionLabel icon={<Assessment sx={{ fontSize: 16 }} />} accent="#ab47bc">Player DNA</SectionLabel>

                    {/* Signature strength line */}
                    <Box sx={{
                      mb: 1.5, p: 1.25, borderRadius: 1.5,
                      bgcolor: alpha('#ab47bc', 0.06),
                      border: `1px solid ${alpha('#ab47bc', 0.15)}`,
                    }}>
                      <Typography variant="caption" sx={{ fontSize: '0.78rem', lineHeight: 1.4, color: 'text.primary', fontWeight: 500 }}>
                        {elite5.length > 0
                          ? <>Elite in <strong>{elite5.slice(0,3).map(s=>s.key).join(', ')}</strong>{weak5.length > 0 ? <> · Struggles with <strong>{weak5.slice(-2).map(s=>s.key).join(', ')}</strong></> : ''}</>
                          : <>Best tool: <strong>{top.key}</strong> ({pctLabel(top.pct)}){weak5.length > 0 ? <> · Exposed by <strong>{bottom.key}</strong></> : ''}</>
                        }
                      </Typography>
                    </Box>

                    {/* Percentile bars — all available stats */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.55 }}>
                      {scored.map(s => {
                        const c = pctColor(s.pct);
                        return (
                          <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ minWidth: 60, fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary', textAlign: 'right' }}>
                              {s.key}
                            </Typography>
                            <Box sx={{ flex: 1, height: 13, borderRadius: 1, bgcolor: alpha(theme.palette.divider, 0.07), overflow: 'hidden', position: 'relative' }}>
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${s.pct}%` }}
                                transition={{ duration: 0.5, ease: 'easeOut', delay: 0.05 }}
                                style={{ height: '100%', borderRadius: 4, background: `linear-gradient(90deg, ${alpha(c, 0.7)}, ${alpha(c, 0.35)})` }}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 700, minWidth: 22, textAlign: 'right', color: c }}>
                              {s.pct}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.72rem', minWidth: 46, color: 'text.secondary' }}>
                              {s.fmt(s.val)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>

                    <Typography variant="caption" sx={{ display: 'block', mt: 1, fontSize: '0.7rem', opacity: 0.35, letterSpacing: 0.2 }}>
                      Percentile vs. MLB average · 50 = league avg
                    </Typography>
                  </CardContent>
                </Card>
              );
            })()}
          </Grid>
        </Grid>

      </Box>
    </motion.div>
  );
};

// ============================================================================
// Pitch Analysis Tab — ALL filtering is client-side using classifyGameType(date)
// Same logic the game log uses. Fetch all events once, filter instantly.
// ============================================================================
const PitchAnalysisTab = ({ pitchData, pitchEvents, playerType, player, year, gameTypeFilter, selectedGame, pitchGamesList, onDrillDown }) => {
  const perspective = isPitcherPosition(player?.position) ? 'pitcher' : 'batter';
  const isPerGame = selectedGame && selectedGame !== 'all';
  const selectedGameInfo = isPerGame ? pitchGamesList?.find(g => g.gameId === selectedGame) : null;

  // ── 1. Filter raw pitch events by game type AND specific game ──
  const filteredPitchEvents = React.useMemo(() => {
    if (!Array.isArray(pitchEvents) || pitchEvents.length === 0) return [];
    let events = pitchEvents;

    // Filter by game type
    if (gameTypeFilter && gameTypeFilter !== 'all') {
      events = events.filter(e => {
        if (e.gt) return normalizeGameType(e.gt) === gameTypeFilter;
        if (e.gameType) return normalizeGameType(e.gameType) === gameTypeFilter;
        const date = e.gameDate || e.gd;
        if (!date) return gameTypeFilter === 'R';
        return classifyGameType(date) === gameTypeFilter;
      });
    }

    // Filter by specific game
    if (isPerGame) {
      events = events.filter(e => {
        const gid = String(e.gameId || e.g);
        return gid === String(selectedGame);
      });
    }

    return events;
  }, [pitchEvents, gameTypeFilter, selectedGame, isPerGame]);

  // ── 2. Re-aggregate pitch type breakdown from filtered events ──
  const filteredPitchTypes = React.useMemo(() => {
    // Always re-aggregate from events so the numbers match the actual filtered event set
    // (using pitchData.pitchTypes shortcut caused mismatches when gameType='all' returned pre-aggregated career-level buckets)
    if (filteredPitchEvents.length === 0) {
      // Fallback to server pre-aggregated data only when no events are available at all
      if ((!gameTypeFilter || gameTypeFilter === 'all') && !isPerGame && pitchData?.pitchTypes) return pitchData.pitchTypes;
      return null;
    }
    const totalPitches = filteredPitchEvents.length;
    const types = {};
    for (const e of filteredPitchEvents) {
      const pt = e.pitchType || e.pt;
      if (!pt) continue;
      if (!types[pt]) types[pt] = {
        code: pt, name: e.pitchName || pt, count: 0, strikes: 0, balls: 0,
        swingAndMiss: 0, calledStrikes: 0, fouls: 0, inPlay: 0, hits: 0, outs: 0,
        totalVelo: 0, veloCount: 0, maxVelo: 0, totalSpin: 0, spinCount: 0,
        totalHorizBreak: 0, totalVertBreak: 0, breakCount: 0,
        avgVelo: 0, avgSpin: 0, avgHorizBreak: 0, avgVertBreak: 0,
        whiffRate: 0, swingRate: 0, contactRate: 0, usage: 0, battingAvg: 0,
      };
      const t = types[pt];
      const result = e.result || e.r;
      const velo = e.velocity || e.v || 0;
      const spin = e.spinRate || e.sp || 0;
      const hBreak = e.horizBreak ?? e.hB ?? null;
      const vBreak = e.vertBreak ?? e.vB ?? null;
      const isLast = e.isLastPitch || e.last;
      const abResult = (e.atBatResult || e.abr || '').toLowerCase();
      t.count++;
      if (velo > 0) { t.totalVelo += velo; t.veloCount++; if (velo > t.maxVelo) t.maxVelo = velo; }
      if (spin > 0) { t.totalSpin += spin; t.spinCount++; }
      if (hBreak != null && vBreak != null) { t.totalHorizBreak += hBreak; t.totalVertBreak += vBreak; t.breakCount++; }
      if (result === 'swstr') { t.swingAndMiss++; t.strikes++; }
      else if (result === 'called') { t.calledStrikes++; t.strikes++; }
      else if (result === 'foul') { t.fouls++; t.strikes++; }
      else if (result === 'ball') t.balls++;
      else if (result === 'inplay') {
        t.inPlay++; t.strikes++;
        if (abResult && ['single','double','triple','home_run','home run'].includes(abResult)) t.hits++;
        if (isLast) t.outs++;
      }
    }
    for (const t of Object.values(types)) {
      t.avgVelo = t.veloCount > 0 ? +(t.totalVelo / t.veloCount).toFixed(1) : 0;
      t.avgSpin = t.spinCount > 0 ? Math.round(t.totalSpin / t.spinCount) : 0;
      t.avgHorizBreak = t.breakCount > 0 ? +(t.totalHorizBreak / t.breakCount).toFixed(2) : 0;
      t.avgVertBreak = t.breakCount > 0 ? +(t.totalVertBreak / t.breakCount).toFixed(2) : 0;
      const swings = t.swingAndMiss + t.fouls + t.inPlay;
      t.whiffRate = swings > 0 ? +((t.swingAndMiss / swings) * 100).toFixed(1) : 0;
      t.swingRate = t.count > 0 ? +((swings / t.count) * 100).toFixed(1) : 0;
      t.contactRate = swings > 0 ? +(((t.fouls + t.inPlay) / swings) * 100).toFixed(1) : 0;
      t.usage = totalPitches > 0 ? +((t.count / totalPitches) * 100).toFixed(1) : 0;
      const atBats = t.inPlay; // at-bats ≈ pitches put in play
      t.battingAvg = atBats > 0 ? +(t.hits / atBats).toFixed(3) : 0;
    }
    return Object.keys(types).length > 0 ? types : null;
  }, [filteredPitchEvents, pitchData, gameTypeFilter, isPerGame]);

  // ── 3. Re-aggregate zone data from filtered events ──
  // ZoneHeatmap expects an ARRAY of { zone, pitches, swings, whiffs, inPlay, hits, swingRate, whiffRate, hitRate }
  const filteredZones = React.useMemo(() => {
    // Always re-aggregate from events to match the actual filtered event set
    if (filteredPitchEvents.length === 0) {
      // Fallback to server data only when no events available
      if ((!gameTypeFilter || gameTypeFilter === 'all') && !isPerGame && pitchData?.zones) {
        const z = pitchData.zones;
        if (Array.isArray(z)) return z;
        return Object.values(z);
      }
      return null;
    }
    const zones = {};
    for (const e of filteredPitchEvents) {
      const z = e.zone ?? e.z;
      if (z == null) continue;
      const result = e.result || e.r;
      const abResult = (e.atBatResult || e.abr || '').toLowerCase();
      if (!zones[z]) zones[z] = { zone: z, pitches: 0, swings: 0, whiffs: 0, fouls: 0, calledStrikes: 0, balls: 0, inPlay: 0, hits: 0, swingRate: 0, whiffRate: 0, hitRate: 0 };
      const zd = zones[z];
      zd.pitches++;
      if (result === 'ball') { zd.balls++; }
      else if (result === 'called') { zd.calledStrikes++; }
      else if (result === 'foul') { zd.fouls++; zd.swings++; }
      else if (result === 'swstr') { zd.whiffs++; zd.swings++; }
      else if (result === 'inplay') {
        zd.inPlay++; zd.swings++;
        if (abResult && ['single','double','triple','home_run','home run'].includes(abResult)) zd.hits++;
      }
    }
    // Compute derived rates
    for (const zd of Object.values(zones)) {
      zd.swingRate = zd.pitches > 0 ? +((zd.swings / zd.pitches) * 100).toFixed(1) : 0;
      zd.whiffRate = zd.swings > 0 ? +((zd.whiffs / zd.swings) * 100).toFixed(1) : 0;
      zd.hitRate = zd.inPlay > 0 ? +((zd.hits / zd.inPlay) * 100).toFixed(1) : 0;
    }
    const result = Object.values(zones);
    return result.length > 0 ? result : null;
  }, [filteredPitchEvents, pitchData, gameTypeFilter, isPerGame]);

  // ── 4. Batted ball data — re-aggregate from filtered in-play events so stats
  //       always match the active game-type / per-game filter (events carry
  //       exitVelocity / launchAngle / distance / trajectory / sprayAngle).
  const filteredBattedBall = React.useMemo(() => {
    const inPlay = filteredPitchEvents.filter(e => {
      const r = (e.result || e.r || '').toLowerCase();
      const isLast = e.isLastPitch !== undefined ? e.isLastPitch !== false : (e.last !== false);
      return r === 'inplay' && isLast;
    });
    if (inPlay.length === 0) {
      // Fallback to server pre-aggregated data only when no events available
      if ((!gameTypeFilter || gameTypeFilter === 'all') && !isPerGame) return pitchData?.battedBall || null;
      return null;
    }
    const isHit = (abr) => abr && ['single', 'double', 'triple', 'home_run', 'home run'].some(h => abr.toLowerCase().includes(h));
    let total = 0, gb = 0, fb = 0, ld = 0, pu = 0;
    let hardHit = 0, barrels = 0, totalEV = 0, evCount = 0;
    let totalLA = 0, laCount = 0, totalDist = 0, distCount = 0;
    const spray = { pull: { count: 0, hits: 0 }, center: { count: 0, hits: 0 }, oppo: { count: 0, hits: 0 } };

    for (const e of inPlay) {
      total++;
      const ev = parseFloat(e.exitVelocity ?? e.ev ?? 0) || 0;
      const la = (e.launchAngle ?? e.la) != null ? parseFloat(e.launchAngle ?? e.la) : null;
      const dist = parseFloat(e.distance ?? e.dist ?? 0) || 0;
      const traj = (e.trajectory || e.traj || '').toLowerCase();
      const hit = isHit(e.atBatResult || e.abr || '');

      // Trajectory: prefer Statcast label, fall back to LA bins
      if (traj.includes('ground')) gb++;
      else if (traj.includes('line')) ld++;
      else if (traj.includes('fly')) fb++;
      else if (traj.includes('pop')) pu++;
      else if (la != null) {
        if (la < 10) gb++;
        else if (la < 25) ld++;
        else if (la < 50) fb++;
        else pu++;
      }

      if (ev > 0) {
        totalEV += ev; evCount++;
        if (ev >= 95) hardHit++;
        if (la != null && ev >= 98 && la >= 26 && la <= 30) barrels++;
      }
      if (la != null) { totalLA += la; laCount++; }
      if (dist > 0) { totalDist += dist; distCount++; }

      // Spray direction from real spray angle when present (hand-aware:
      // pull = negative sa for RHB, positive sa for LHB)
      const sa = (e.sprayAngle ?? e.sa) != null ? parseFloat(e.sprayAngle ?? e.sa) : null;
      if (sa != null) {
        let batterHand;
        if (perspective === 'pitcher') batterHand = e.opponentHand || e.h || 'R';
        else {
          batterHand = player?.batSide || player?.bats || 'R';
          if (batterHand === 'S') batterHand = (e.opponentHand || e.h) === 'R' ? 'L' : 'R';
        }
        let dir;
        if (Math.abs(sa) <= 15) dir = 'center';
        else if (sa < 0) dir = batterHand === 'L' ? 'oppo' : 'pull';
        else dir = batterHand === 'L' ? 'pull' : 'oppo';
        spray[dir].count++;
        if (hit) spray[dir].hits++;
      }
    }

    return {
      total,
      groundBall: gb, flyBall: fb, lineDrive: ld, popup: pu,
      hardHit, barrels,
      avgExitVelo: evCount > 0 ? +(totalEV / evCount).toFixed(1) : 0,
      avgLaunchAngle: laCount > 0 ? +(totalLA / laCount).toFixed(1) : 0,
      avgDistance: distCount > 0 ? +(totalDist / distCount).toFixed(0) : 0,
      hardHitRate: evCount > 0 ? +((hardHit / evCount) * 100).toFixed(1) : 0,
      barrelRate: total > 0 ? +((barrels / total) * 100).toFixed(1) : 0,
      groundBallRate: total > 0 ? +((gb / total) * 100).toFixed(1) : 0,
      flyBallRate: total > 0 ? +((fb / total) * 100).toFixed(1) : 0,
      lineDriveRate: total > 0 ? +((ld / total) * 100).toFixed(1) : 0,
      spray,
    };
  }, [filteredPitchEvents, pitchData, gameTypeFilter, isPerGame, perspective, player]);

  // ── 4b. Raw scatter events for SprayChart dots — derive sprayAngle/normDist
  //        from zone column + distance, mirroring SprayChartExplorer's logic. ──
  const sprayRawEvents = React.useMemo(() => {
    if (!filteredPitchEvents || filteredPitchEvents.length === 0) return [];
    const inPlay = filteredPitchEvents.filter(e => {
      const r = (e.result || e.r || '').toLowerCase();
      return r === 'inplay' || r === 'in_play' || r === 'in play' || r === 'hit_into_play';
    });
    if (inPlay.length === 0) return [];
    return inPlay.map((e) => {
      const zone = e.zone ?? e.z ?? 0;
      const dist = parseFloat(e.distance ?? e.dist ?? e.hitDistance ?? 0) || 0;
      const ev = parseFloat(e.exitVelocity ?? e.ev ?? e.launchSpeed ?? 0) || 0;
      const la = parseFloat(e.launchAngle ?? e.la ?? 0) || 0;
      const result = (e.atBatResult || e.abr || e.outcome || e.result || '').toLowerCase();
      const realSA = (e.sprayAngle ?? e.sa) != null ? parseFloat(e.sprayAngle ?? e.sa) : null;
      let sprayAngle;
      if (realSA != null && !isNaN(realSA)) {
        sprayAngle = realSA;
      } else if (zone >= 1 && zone <= 9) {
        const col = (zone - 1) % 3;
        sprayAngle = (col - 1) * 35 + (Math.random() - 0.5) * 12;
      } else {
        sprayAngle = (Math.random() - 0.5) * 70;
      }
      const normDist = dist > 0 ? Math.min(dist / 450, 1) : (0.25 + Math.random() * 0.35);
      return {
        sprayAngle, normDist,
        exitVelocity: ev, launchAngle: la, distance: dist,
        result: result || 'out',
        gameId: e.gameId || e.g || null,
        gameDate: e.gameDate || e.gd || null,
        inning: e.inning || e.in || null,
        opponent: e.opponent || e.opp || null,
        opponentName: e.opponentName || e.oppName || null,
        pitchType: e.pitchType || e.pt || null,
      };
    });
  }, [filteredPitchEvents]);

  const hasPitchData = (filteredPitchTypes || filteredZones || filteredBattedBall);
  const hasPitchEvents = filteredPitchEvents.length > 0;
  const gameTypeLabel = GAME_TYPE_OPTIONS.find(o => o.value === gameTypeFilter)?.label || '';
  const gameLabel = selectedGameInfo ? (() => {
    const d = selectedGameInfo.date ? new Date(selectedGameInfo.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    const ha = selectedGameInfo.homeAway === 'away' ? '@' : 'vs';
    return `${d} ${ha} ${selectedGameInfo.opponent}`;
  })() : '';

  if (!hasPitchData && !hasPitchEvents) {
    // Check if the FULL (unfiltered) dataset has events — if yes, this game type simply has none
    const hasAnyEvents = Array.isArray(pitchEvents) && pitchEvents.length > 0;
    // No pitch data at all — the Arsenal section above already shows its own empty state
    if (!hasAnyEvents) return null;
    return (
      <Box>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 4, textAlign: 'center', mb: 3 }}>
          <SportsBaseball sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {gameTypeFilter && gameTypeFilter !== 'all'
              ? `No Pitch Data for ${gameTypeLabel}`
              : 'Pitch Data Loading'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {gameTypeFilter && gameTypeFilter !== 'all'
              ? `No pitch-level data is available for ${player?.name || 'this player'} during ${gameTypeLabel} (${year}). Try selecting a different game type.`
              : `Pitch-level analysis data is not yet available for ${player?.name || 'this player'} (${year}).`}
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Per-game context banner */}
      {isPerGame && selectedGameInfo && (
        <Grid item xs={12}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 3, p: 1.5, background: (theme) => alpha(theme.palette.primary.main, 0.04) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <SportsBaseball sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Game View: {gameLabel}
              </Typography>
              <Chip size="small" label={`${filteredPitchEvents.length} pitches`} variant="outlined" />
            </Box>
          </Card>
        </Grid>
      )}

      {/* Stuff+ profile card removed — duplicated by the Arsenal section above */}

      {/* Interactive Zone Chart — scatter/heatmap of individual pitches */}
      {hasPitchEvents && (
        <Grid item xs={12} sm={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2, height: '100%' }}>
            <InteractiveZoneChart
              pitchEvents={filteredPitchEvents}
              zones={filteredZones || {}}
              title={`${player?.name || ''} — Pitch Locations${isPerGame ? ` (${gameLabel})` : gameTypeLabel ? ` (${gameTypeLabel})` : ''}`}
              perspective={perspective}
              playerHand={perspective === 'pitcher' ? (player?.throws || player?.pitchHand || 'R') : (player?.bats || player?.batSide || 'R')}
              size={460}
              onDrillDown={onDrillDown}
            />
          </Card>
        </Grid>
      )}

      {/* Zone Heatmap — 3x3 strike zone with chase zones */}
      {filteredZones && Array.isArray(filteredZones) && filteredZones.length > 0 && (
        <Grid item xs={12} sm={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2, height: '100%' }}>
            <ZoneHeatmap
              zones={filteredZones}
              title={`Strike Zone Heatmap${isPerGame ? ` (${gameLabel})` : gameTypeLabel ? ` (${gameTypeLabel})` : ''}`}
              perspective={perspective}
              playerHand={perspective === 'pitcher' ? (player?.pitchHand || player?.throws || 'R') : (player?.batSide || player?.bats || 'R')}
            />
          </Card>
        </Grid>
      )}

      {/* Spray Chart — batted ball distribution, filtered by game type.
          Full-width row (same treatment as the hitter Batted Ball tab) so the
          field SVG + stat panel are readable. */}
      {(filteredBattedBall || sprayRawEvents.length > 0) && (
        <Grid item xs={12}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2, height: '100%' }}>
            <SprayChart
              battedBall={filteredBattedBall || { total: sprayRawEvents.length, spray: { pull: { count: 0, hits: 0 }, center: { count: 0, hits: 0 }, oppo: { count: 0, hits: 0 } } }}
              batterHand={player?.batSide || player?.bats || pitchData?.playerHand || 'R'}
              perspective={perspective}
              rawEvents={sprayRawEvents}
              onDotClick={onDrillDown ? (ev) => onDrillDown({
                gameId: ev.gameId,
                inning: ev.inning,
                gameDate: ev.gameDate,
                opponentName: ev.opponentName,
              }) : undefined}
            />
          </Card>
        </Grid>
      )}

      {/* Sequencing + Tunneling now live in the Arsenal tab */}
    </Grid>
  );
};

// Loading skeleton
const PlayerDetailSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width={100} height={40} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={200} sx={{ mb: 3, borderRadius: 2 }} />
      <Skeleton variant="rectangular" height={60} sx={{ mb: 3, borderRadius: 2 }} />
      
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// COMPARE YEARS — Side-by-side multi-year comparison
// ============================================================================

const YEAR_COLORS = ['#1976d2', '#ff6f00', '#4caf50', '#e91e63', '#9c27b0', '#00bcd4'];

const PITCH_TYPE_NAMES_CY = {
  FF: 'Four-Seam', SI: 'Sinker', FC: 'Cutter', SL: 'Slider',
  CU: 'Curveball', KC: 'Knuckle Curve', CH: 'Changeup', FS: 'Splitter',
  KN: 'Knuckleball', EP: 'Eephus', ST: 'Sweeper', SV: 'Slurve',
  CS: 'Slow Curve', FO: 'Forkball', FA: 'Fastball', UN: 'Unknown',
};
const PITCH_COLORS_CY = {
  FF: '#e74c3c', SI: '#e67e22', FC: '#f39c12', SL: '#f1c40f',
  CU: '#2ecc71', KC: '#1abc9c', CH: '#3498db', FS: '#9b59b6',
  ST: '#e91e63', SV: '#00bcd4', KN: '#795548', EP: '#607d8b',
  CS: '#4caf50', FO: '#8e24aa', FA: '#d32f2f', UN: '#9e9e9e',
};

// Zone heatmap colors
const ZONE_HEAT = [
  'rgba(33,150,243,0.15)', 'rgba(33,150,243,0.40)', 'rgba(76,175,80,0.45)',
  'rgba(255,235,59,0.55)', 'rgba(255,152,0,0.65)', 'rgba(244,67,54,0.75)', 'rgba(211,47,47,0.90)',
];
const zoneHeatColor = (val, max) => {
  if (!max) return ZONE_HEAT[0];
  const idx = Math.min(Math.floor(Math.min(val / max, 1) * (ZONE_HEAT.length - 1)), ZONE_HEAT.length - 1);
  return ZONE_HEAT[idx];
};

const CompareYears = ({ player, currentYear, playerType, unifiedProfile }) => {
  const theme = useTheme();
  const { isMobile } = useMobile();
  const [yearlyData, setYearlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYears, setSelectedYears] = useState([]);
  const [pitchDataByYear, setPitchDataByYear] = useState({});
  const [pitchLoading, setPitchLoading] = useState(false);
  const [compareMode, setCompareMode] = useState('stats'); // 'stats' | 'pitch'

  useEffect(() => {
    const loadCareerData = async () => {
      if (!player) return;
      setLoading(true);

      const careerSeasons = unifiedProfile?.allMlbSeasons || unifiedProfile?.seasons || [];
      if (careerSeasons.length > 0) {
        const yearlyStats = careerSeasons
          .filter(s => {
            if (s.springTrainingOnly) return false;
            if (s.gameTypeBreakdown && Object.keys(s.gameTypeBreakdown).length > 0) {
              return (s.gameTypeBreakdown.R || 0) > 0;
            }
            const currentYr = new Date().getFullYear().toString();
            if (String(s.year) < currentYr && (s.gameCount || 0) > 0) return true;
            const ab = s.batting?.atBats || s.regularSeason?.batting?.atBats || 0;
            const ip = parseFloat(s.pitching?.inningsPitched || s.regularSeason?.pitching?.inningsPitched || 0);
            return ab >= 50 || ip >= 20;
          })
          .map(s => {
            const rs = s.regularSeason || {};
            const batting = rs.batting || s.batting || {};
            const pitching = rs.pitching || s.pitching || {};
            const games = rs.gameCount ?? s.gameTypeBreakdown?.R ?? s.gameCount ?? batting.gamesPlayed ?? pitching.gamesPlayed ?? 0;
            return {
              year: parseInt(s.year),
              stats: { batting, pitching },
              team: s.team,
              games,
              war: s.war || 0,
            };
          })
          .sort((a, b) => b.year - a.year); // newest first

        setYearlyData(yearlyStats);
        // Default: select the 2 most recent years
        const defaultYears = yearlyStats.slice(0, Math.min(2, yearlyStats.length)).map(y => y.year);
        setSelectedYears(defaultYears);
      } else {
        // Fallback for legacy
        const years = [];
        const currentYearNum = parseInt(currentYear) || new Date().getFullYear();
        for (let y = 2015; y <= currentYearNum + 1; y++) years.push(y);

        const results = await Promise.allSettled(
          years.map(async (yr) => {
            const playerKey = `${player.team}-${player.name.replace(/\s+/g, '_')}-${yr}`;
            const data = await playersApi.getPlayerById(playerKey, { year: yr });
            if (!data?.seasonStats) throw new Error('No stats');
            return {
              year: yr,
              stats: data.seasonStats,
              team: data.team || player.team,
              games: data.gameLog?.length || data.seasonStats?.batting?.gamesPlayed || data.seasonStats?.pitching?.gamesPlayed || 0,
              war: 0,
            };
          })
        );

        const yearlyStats = results
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value)
          .sort((a, b) => b.year - a.year);

        setYearlyData(yearlyStats);
        const defaultYears = yearlyStats.slice(0, Math.min(2, yearlyStats.length)).map(y => y.year);
        setSelectedYears(defaultYears);
      }
      setLoading(false);
    };

    loadCareerData();
  }, [player, unifiedProfile, currentYear]);

  // ---- Fetch pitch-level data for selected years ----
  useEffect(() => {
    if (!player || selectedYears.length < 2) return;
    const isPitcher = playerType === 'pitcher';
    const fetchFn = isPitcher ? pitchSplitsApi.getPitcherPitchData : pitchSplitsApi.getBatterPitchData;
    const playerName = player.name?.replace(/\s+/g, '_') || '';

    const yearsToFetch = selectedYears.filter(yr => !pitchDataByYear[yr]);
    if (yearsToFetch.length === 0) return;

    setPitchLoading(true);
    Promise.allSettled(
      yearsToFetch.map(async (yr) => {
        // Find team for that year from yearlyData
        const yd = yearlyData.find(y => y.year === yr);
        const team = yd?.team || player.team;
        const data = await fetchFn(team, playerName, yr, { gameType: 'R' });
        return { yr, data };
      })
    ).then(results => {
      const newData = { ...pitchDataByYear };
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value.data) {
          newData[r.value.yr] = r.value.data;
        }
      });
      setPitchDataByYear(newData);
      setPitchLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYears, player, playerType, yearlyData]);

  const toggleYear = (yr) => {
    setSelectedYears(prev =>
      prev.includes(yr) ? prev.filter(y => y !== yr) : [...prev, yr].sort((a, b) => a - b)
    );
  };

  const comparedData = yearlyData.filter(yd => selectedYears.includes(yd.year));
  const showBatting = playerType === 'batter' || playerType === 'two-way';
  const showPitching = playerType === 'pitcher' || playerType === 'two-way';

  // Stat configs for comparison
  const battingCompareStats = [
    { key: 'gamesPlayed', label: 'G', fmt: v => v || 0 },
    { key: 'atBats', label: 'AB', fmt: v => v || 0 },
    { key: 'hits', label: 'H', fmt: v => v || 0 },
    { key: 'homeRuns', label: 'HR', fmt: v => v || 0, highlight: true },
    { key: 'rbi', label: 'RBI', fmt: v => v || 0, highlight: true },
    { key: 'runs', label: 'R', fmt: v => v || 0 },
    { key: 'stolenBases', label: 'SB', fmt: v => v || 0 },
    { key: 'baseOnBalls', label: 'BB', fmt: v => v || 0 },
    { key: 'strikeOuts', label: 'K', fmt: v => v || 0 },
    { key: 'avg', label: 'AVG', fmt: v => v?.toFixed(3) || '.000', rate: true },
    { key: 'obp', label: 'OBP', fmt: v => v?.toFixed(3) || '.000', rate: true },
    { key: 'slg', label: 'SLG', fmt: v => v?.toFixed(3) || '.000', rate: true },
    { key: 'ops', label: 'OPS', fmt: v => v?.toFixed(3) || '.000', rate: true, highlight: true },
  ];

  const pitchingCompareStats = [
    { key: 'gamesPlayed', label: 'G', fmt: v => v || 0 },
    { key: 'gamesStarted', label: 'GS', fmt: v => v || 0 },
    { key: 'wins', label: 'W', fmt: v => v || 0 },
    { key: 'losses', label: 'L', fmt: v => v || 0 },
    { key: 'saves', label: 'SV', fmt: v => v || 0 },
    { key: 'inningsPitched', label: 'IP', fmt: v => v || '0.0' },
    { key: 'strikeOuts', label: 'K', fmt: v => v || 0, highlight: true },
    { key: 'era', label: 'ERA', fmt: v => v?.toFixed(2) || '0.00', rate: true, highlight: true, lower: true },
    { key: 'whip', label: 'WHIP', fmt: v => v?.toFixed(2) || '0.00', rate: true, lower: true },
    { key: 'strikeoutsPer9Inn', label: 'K/9', fmt: v => v?.toFixed(1) || '0.0', rate: true },
    { key: 'walksPer9Inn', label: 'BB/9', fmt: v => v?.toFixed(1) || '0.0', rate: true, lower: true },
  ];

  // Calculate rate stats for comparison
  const getCompareBatting = (yd) => {
    const b = yd.stats?.batting || {};
    const avg = b.atBats > 0 ? b.hits / b.atBats : 0;
    const pa = (b.atBats || 0) + (b.baseOnBalls || 0) + (b.hitByPitch || 0) + (b.sacrificeFlies || 0);
    const obp = pa > 0 ? ((b.hits || 0) + (b.baseOnBalls || 0) + (b.hitByPitch || 0)) / pa : 0;
    const tb = (b.hits || 0) + (b.doubles || 0) + 2 * (b.triples || 0) + 3 * (b.homeRuns || 0);
    const slg = b.atBats > 0 ? tb / b.atBats : 0;
    return { ...b, avg, obp, slg, ops: obp + slg };
  };

  const getComparePitching = (yd) => {
    const p = yd.stats?.pitching || {};
    const ip = parseInningsPitched(p.inningsPitched);
    const era = ip > 0 ? (p.earnedRuns || 0) * 9 / ip : 0;
    const whip = ip > 0 ? ((p.hits || 0) + (p.baseOnBalls || 0)) / ip : 0;
    const k9 = ip > 0 ? (p.strikeOuts || 0) * 9 / ip : 0;
    const bb9 = ip > 0 ? (p.baseOnBalls || 0) * 9 / ip : 0;
    return { ...p, era, whip, strikeoutsPer9Inn: k9, walksPer9Inn: bb9 };
  };

  // Find the best value for a stat across compared years (for highlighting)
  const getBestIdx = (values, lower = false) => {
    if (values.length === 0) return -1;
    const numeric = values.map(v => typeof v === 'number' ? v : parseFloat(v) || 0);
    if (lower) return numeric.indexOf(Math.min(...numeric));
    return numeric.indexOf(Math.max(...numeric));
  };

  // ---- Pitch data helpers (must be before early returns for hooks) ----
  const hasPitchData = selectedYears.some(yr => pitchDataByYear[yr]);
  const comparedPitchYears = comparedData.filter(yd => pitchDataByYear[yd.year]);

  // Gather all pitch types across all selected years
  const allPitchTypes = useMemo(() => {
    const typeSet = new Set();
    comparedPitchYears.forEach(yd => {
      const pd = pitchDataByYear[yd.year];
      if (pd?.pitchTypes) Object.keys(pd.pitchTypes).forEach(t => typeSet.add(t));
    });
    return [...typeSet].sort((a, b) => {
      const order = { FF: 0, SI: 1, FC: 2, FA: 3, SL: 4, ST: 5, SV: 6, CU: 7, KC: 8, CS: 9, CH: 10, FS: 11, FO: 12, KN: 13, EP: 14, UN: 99 };
      return (order[a] ?? 50) - (order[b] ?? 50);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(comparedPitchYears.map(y => y.year)), pitchDataByYear]);

  // Build zone data for each year
  const zoneDataByYear = useMemo(() => {
    const result = {};
    comparedPitchYears.forEach(yd => {
      const pd = pitchDataByYear[yd.year];
      if (!pd?.zones) return;
      const zones = {};
      Object.entries(pd.zones).forEach(([zoneId, z]) => {
        const swings = z.swings || 0;
        const whiffs = z.whiffs || 0;
        const pitches = z.pitches || 0;
        const hits = z.hits || 0;
        const inPlay = z.inPlay || 0;
        zones[zoneId] = {
          zone: parseInt(zoneId),
          pitches, swings, whiffs, inPlay, hits,
          swingRate: pitches > 0 ? parseFloat((swings / pitches * 100).toFixed(1)) : 0,
          whiffRate: swings > 0 ? parseFloat((whiffs / swings * 100).toFixed(1)) : 0,
          hitRate: inPlay > 0 ? parseFloat((hits / inPlay * 100).toFixed(1)) : 0,
          contactRate: swings > 0 ? parseFloat(((swings - whiffs) / swings * 100).toFixed(1)) : 0,
        };
      });
      result[yd.year] = zones;
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(comparedPitchYears.map(y => y.year)), pitchDataByYear]);

  // Get pitch type data for a year
  const getPitchTypeForYear = (yr, typeCode) => {
    const pd = pitchDataByYear[yr];
    if (!pd?.pitchTypes?.[typeCode]) return null;
    return pd.pitchTypes[typeCode];
  };

  const compareStats = showBatting ? battingCompareStats : pitchingCompareStats;

  if (loading) {
    return (
      <Card elevation={0}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2 }} color="text.secondary">Loading career data...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (yearlyData.length < 2) {
    return (
      <Card elevation={0}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Need at least 2 seasons to compare.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Year Selector */}
      <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
              Select Seasons to Compare
            </Typography>
            {/* Mode toggle */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Chip
                label="Stats"
                size="small"
                variant={compareMode === 'stats' ? 'filled' : 'outlined'}
                color={compareMode === 'stats' ? 'primary' : 'default'}
                onClick={() => setCompareMode('stats')}
                sx={{ fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}
              />
              <Chip
                label="Pitch Analysis"
                size="small"
                variant={compareMode === 'pitch' ? 'filled' : 'outlined'}
                color={compareMode === 'pitch' ? 'secondary' : 'default'}
                onClick={() => setCompareMode('pitch')}
                sx={{ fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {yearlyData.map((yd) => (
              <Chip
                key={yd.year}
                label={`${yd.year} (${yd.team})`}
                onClick={() => toggleYear(yd.year)}
                variant={selectedYears.includes(yd.year) ? 'filled' : 'outlined'}
                color={selectedYears.includes(yd.year) ? 'primary' : 'default'}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              />
            ))}
          </Box>
          {selectedYears.length < 2 && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
              Select at least 2 seasons to compare
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* ================================================================ */}
      {/* STATS MODE */}
      {/* ================================================================ */}
      {compareMode === 'stats' && comparedData.length >= 2 && (
        <>
          {/* Side-by-Side Comparison Table */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                {showBatting ? 'Batting' : 'Pitching'} Comparison
              </Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 60, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                        Stat
                      </TableCell>
                      {comparedData.map((yd) => (
                        <TableCell
                          key={yd.year}
                          align="center"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            minWidth: 80,
                            borderBottom: `3px solid ${
                              String(yd.year) === String(currentYear) ? theme.palette.primary.main : alpha(theme.palette.divider, 0.3)
                            }`,
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={800} sx={{ fontSize: '0.85rem' }}>{yd.year}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{yd.team}</Typography>
                          </Box>
                        </TableCell>
                      ))}
                      {comparedData.length >= 2 && (
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 60, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                          Diff
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {compareStats.map((stat) => {
                      const values = comparedData.map(yd => {
                        const data = showBatting ? getCompareBatting(yd) : getComparePitching(yd);
                        return data[stat.key];
                      });
                      const formattedValues = values.map(v => stat.fmt(v));
                      const bestIdx = stat.rate || stat.highlight ? getBestIdx(values, stat.lower) : -1;
                      let diff = null;
                      if (values.length >= 2) {
                        const first = typeof values[0] === 'number' ? values[0] : parseFloat(values[0]) || 0;
                        const last = typeof values[values.length - 1] === 'number' ? values[values.length - 1] : parseFloat(values[values.length - 1]) || 0;
                        diff = last - first;
                      }
                      return (
                        <TableRow key={stat.key} hover>
                          <TableCell sx={{
                            fontWeight: stat.highlight ? 700 : 600, fontSize: '0.78rem',
                            color: stat.highlight ? 'primary.main' : 'text.primary',
                            position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1,
                          }}>{stat.label}</TableCell>
                          {formattedValues.map((v, i) => (
                            <TableCell key={i} align="center" sx={{
                              fontFamily: 'monospace', fontWeight: i === bestIdx ? 800 : 400, fontSize: '0.85rem',
                              color: i === bestIdx ? 'success.main' : 'text.primary',
                              bgcolor: i === bestIdx ? alpha(theme.palette.success.main, 0.06) : 'transparent',
                            }}>{v}</TableCell>
                          ))}
                          {diff !== null && (
                            <TableCell align="center" sx={{
                              fontFamily: 'monospace', fontWeight: 600, fontSize: '0.78rem',
                              bgcolor: alpha(theme.palette.warning.main, 0.05),
                              color: stat.lower
                                ? (diff < 0 ? 'success.main' : diff > 0 ? 'error.main' : 'text.secondary')
                                : (diff > 0 ? 'success.main' : diff < 0 ? 'error.main' : 'text.secondary'),
                            }}>
                              {diff > 0 ? '+' : ''}{stat.rate ? diff.toFixed(3) : Math.round(diff)}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    {/* WAR row */}
                    {comparedData.some(yd => yd.war > 0) && (
                      <TableRow sx={{ bgcolor: alpha('#FFD700', 0.04) }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#ff6b35', position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>WAR</TableCell>
                        {comparedData.map((yd, i) => {
                          const warValues = comparedData.map(y => y.war || 0);
                          const bestWarIdx = getBestIdx(warValues);
                          return (
                            <TableCell key={yd.year} align="center" sx={{
                              fontFamily: 'monospace', fontWeight: i === bestWarIdx ? 800 : 600, fontSize: '0.85rem',
                              color: i === bestWarIdx ? '#ff6b35' : 'text.primary',
                              bgcolor: i === bestWarIdx ? alpha('#FFD700', 0.08) : 'transparent',
                            }}>{(yd.war || 0).toFixed(1)}</TableCell>
                          );
                        })}
                        {comparedData.length >= 2 && (() => {
                          const diff = (comparedData[comparedData.length - 1].war || 0) - (comparedData[0].war || 0);
                          return (
                            <TableCell align="center" sx={{
                              fontFamily: 'monospace', fontWeight: 600, fontSize: '0.78rem',
                              bgcolor: alpha(theme.palette.warning.main, 0.05),
                              color: diff > 0 ? 'success.main' : diff < 0 ? 'error.main' : 'text.secondary',
                            }}>{diff > 0 ? '+' : ''}{diff.toFixed(1)}</TableCell>
                          );
                        })()}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Visual bar comparison */}
          <Card elevation={0} sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, fontSize: '0.85rem' }}>Visual Comparison</Typography>
              {(showBatting
                ? [{ key: 'homeRuns', label: 'HR' }, { key: 'rbi', label: 'RBI' }, { key: 'runs', label: 'R' }, { key: 'stolenBases', label: 'SB' }, { key: 'hits', label: 'H' }]
                : [{ key: 'strikeOuts', label: 'K' }, { key: 'wins', label: 'W' }, { key: 'saves', label: 'SV' }]
              ).map((stat) => {
                const values = comparedData.map(yd => {
                  const data = showBatting ? (yd.stats?.batting || {}) : (yd.stats?.pitching || {});
                  return typeof data[stat.key] === 'number' ? data[stat.key] : parseFloat(data[stat.key]) || 0;
                });
                const max = Math.max(...values, 1);
                return (
                  <Box key={stat.key} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', mb: 0.5, display: 'block' }}>{stat.label}</Typography>
                    {comparedData.map((yd, i) => (
                      <Box key={yd.year} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" sx={{ width: 36, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>{yd.year}</Typography>
                        <Box sx={{ flex: 1, height: 16, bgcolor: alpha(theme.palette.divider, 0.1), borderRadius: 1, overflow: 'hidden' }}>
                          <Box sx={{ width: `${(values[i] / max) * 100}%`, height: '100%', bgcolor: YEAR_COLORS[i % YEAR_COLORS.length], borderRadius: 1, transition: 'width 0.6s ease-out' }} />
                        </Box>
                        <Typography variant="caption" sx={{ width: 32, fontWeight: 700, fontSize: '0.75rem', fontFamily: 'monospace', textAlign: 'right' }}>{values[i]}</Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </>
      )}

      {/* ================================================================ */}
      {/* PITCH ANALYSIS MODE */}
      {/* ================================================================ */}
      {compareMode === 'pitch' && comparedData.length >= 2 && (
        <>
          {pitchLoading && (
            <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={32} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Loading pitch data...</Typography>
              </CardContent>
            </Card>
          )}

          {!pitchLoading && comparedPitchYears.length < 2 && (
            <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary" variant="body2">
                  Pitch data not available for enough selected seasons.
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* ---- Pitch Type Comparison Table ---- */}
          {!pitchLoading && comparedPitchYears.length >= 2 && allPitchTypes.length > 0 && (
            <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                    Pitch Type Breakdown by Season
                  </Typography>
                  <Chip label="Regular Season" size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: alpha('#1976d2', 0.1), color: '#1976d2', fontWeight: 700 }} />
                </Box>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 85, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                          Pitch
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 40 }}>Year</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>#</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Usage%</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Velo</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Spin</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#e74c3c' }}>Whiff%</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#2ecc71' }}>Contact%</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>CSW%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allPitchTypes.map((typeCode) => {
                        const rows = comparedPitchYears.map((yd, i) => {
                          const pt = getPitchTypeForYear(yd.year, typeCode);
                          if (!pt) return null;
                          const totalPitches = Object.values(pitchDataByYear[yd.year]?.pitchTypes || {}).reduce((s, p) => s + (p.count || 0), 0);
                          const usage = totalPitches > 0 ? ((pt.count / totalPitches) * 100).toFixed(1) : '0.0';
                          const avgVelo = pt.veloCount > 0 ? (pt.totalVelo / pt.veloCount).toFixed(1) : '—';
                          const avgSpin = pt.spinCount > 0 ? Math.round(pt.totalSpin / pt.spinCount) : '—';
                          const swings = (pt.swingAndMiss || 0) + (pt.fouls || 0) + (pt.inPlay || 0);
                          const whiffRate = swings > 0 ? ((pt.swingAndMiss / swings) * 100).toFixed(1) : '—';
                          const contactRate = swings > 0 ? (((swings - pt.swingAndMiss) / swings) * 100).toFixed(1) : '—';
                          const csw = pt.count > 0 ? (((pt.calledStrikes || 0) + (pt.swingAndMiss || 0)) / pt.count * 100).toFixed(1) : '—';
                          return { yd, i, pt, usage, avgVelo, avgSpin, whiffRate, contactRate, csw };
                        }).filter(Boolean);

                        if (rows.length === 0) return null;

                        return rows.map((row, ri) => (
                          <TableRow key={`${typeCode}-${row.yd.year}`} hover sx={{
                            borderTop: ri === 0 ? `2px solid ${alpha(PITCH_COLORS_CY[typeCode] || '#999', 0.4)}` : 'none',
                            bgcolor: ri === 0 ? alpha(PITCH_COLORS_CY[typeCode] || '#999', 0.03) : 'transparent',
                          }}>
                            {ri === 0 && (
                              <TableCell rowSpan={rows.length} sx={{
                                fontWeight: 700, fontSize: '0.78rem', position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1,
                                borderLeft: `3px solid ${PITCH_COLORS_CY[typeCode] || '#999'}`,
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PITCH_COLORS_CY[typeCode] || '#999' }} />
                                  {PITCH_TYPE_NAMES_CY[typeCode] || typeCode}
                                </Box>
                              </TableCell>
                            )}
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.78rem', color: YEAR_COLORS[row.i % YEAR_COLORS.length] }}>{row.yd.year}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{row.pt.count}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{row.usage}%</TableCell>
                            <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{row.avgVelo}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{row.avgSpin}</TableCell>
                            <TableCell align="center" sx={{
                              fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700,
                              color: row.whiffRate !== '—' && parseFloat(row.whiffRate) >= 30 ? '#e74c3c' : row.whiffRate !== '—' && parseFloat(row.whiffRate) >= 20 ? '#ff9800' : 'text.primary',
                            }}>{row.whiffRate}{row.whiffRate !== '—' ? '%' : ''}</TableCell>
                            <TableCell align="center" sx={{
                              fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600,
                              color: row.contactRate !== '—' && parseFloat(row.contactRate) >= 85 ? '#2ecc71' : 'text.primary',
                            }}>{row.contactRate}{row.contactRate !== '—' ? '%' : ''}</TableCell>
                            <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{row.csw}{row.csw !== '—' ? '%' : ''}</TableCell>
                          </TableRow>
                        ));
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* ---- Zone Whiff Rate Heatmaps (side-by-side per year) ---- */}
          {!pitchLoading && comparedPitchYears.length >= 2 && Object.keys(zoneDataByYear).length >= 2 && (
            <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                  Zone Whiff Rate by Season
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Where {playerType === 'pitcher' ? 'batters' : 'this batter'} swings and misses — higher % = more whiffs in that zone
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                  {comparedPitchYears.map((yd, yi) => {
                    const zones = zoneDataByYear[yd.year] || {};
                    const maxWhiff = Math.max(...Object.values(zones).map(z => z.whiffRate || 0), 1);
                    const cellSize = isMobile ? 44 : 50;
                    const chaseH = isMobile ? 28 : 32;         // chase zone height (top/bottom)
                    const chaseW = isMobile ? 28 : 32;         // chase zone width (left/right)
                    const gridW = cellSize * 3;                // total width of 3x3 grid
                    // LHB: zone 13 is spatially outside (left from catcher), zone 14 is inside (right)
                    const isLHB = playerType !== 'pitcher' && (player?.batSide === 'L' || player?.bats === 'L');
                    const leftChaseZone = isLHB ? 14 : 13;
                    const rightChaseZone = isLHB ? 13 : 14;

                    const renderZoneCell = (zoneNum, w, h, isChase = false) => {
                      const z = zones[zoneNum];
                      const whiff = z?.whiffRate || 0;
                      const pitches = z?.pitches || 0;
                      const label = { 11: 'Above', 12: 'Below', 13: 'Inside', 14: 'Outside' }[zoneNum] || `Zone ${zoneNum}`;
                      return (
                        <Tooltip key={zoneNum} title={`${label}: ${whiff}% whiff (${pitches}p, ${z?.swings || 0} sw, ${z?.whiffs || 0} wh)`} arrow>
                          <Box sx={{
                            width: w, height: h,
                            bgcolor: zoneHeatColor(whiff, maxWhiff),
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            border: isChase ? `1px dashed ${alpha(theme.palette.text.secondary, 0.25)}` : `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                            borderRadius: isChase ? '4px' : 0,
                            cursor: 'default',
                          }}>
                            <Typography sx={{ fontWeight: 800, fontSize: isChase ? '0.75rem' : '0.8rem', fontFamily: 'monospace', lineHeight: 1 }}>
                              {whiff > 0 ? `${whiff}%` : '—'}
                            </Typography>
                            {!isChase && (
                              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1, mt: 0.15 }}>
                                {pitches}p
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      );
                    };

                    return (
                      <Box key={yd.year} sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={800} sx={{ mb: 1, color: YEAR_COLORS[yi % YEAR_COLORS.length] }}>
                          {yd.year} ({yd.team})
                        </Typography>
                        {/* Full zone layout: Above → [Inside | 3x3 | Outside] → Below */}
                        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          {/* Chase: Above */}
                          {renderZoneCell(11, gridW, chaseH, true)}
                          {/* Middle row: Inside + 3x3 + Outside (zones swapped for LHB) */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {renderZoneCell(leftChaseZone, chaseW, cellSize * 3, true)}
                            <Box sx={{ border: '2px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                              {[1, 4, 7].map(rowStart => (
                                <Box key={rowStart} sx={{ display: 'flex' }}>
                                  {[0, 1, 2].map(col => renderZoneCell(rowStart + col, cellSize, cellSize))}
                                </Box>
                              ))}
                            </Box>
                            {renderZoneCell(rightChaseZone, chaseW, cellSize * 3, true)}
                          </Box>
                          {/* Chase: Below */}
                          {renderZoneCell(12, gridW, chaseH, true)}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* ---- Zone-by-Zone Diff Grid (oldest vs newest) ---- */}
          {!pitchLoading && comparedPitchYears.length >= 2 && Object.keys(zoneDataByYear).length >= 2 && (() => {
            const oldest = comparedPitchYears[comparedPitchYears.length - 1];
            const newest = comparedPitchYears[0];
            const oldZones = zoneDataByYear[oldest.year] || {};
            const newZones = zoneDataByYear[newest.year] || {};
            const allZoneIds = [...new Set([...Object.keys(oldZones), ...Object.keys(newZones)])].map(Number).filter(z => (z >= 1 && z <= 9) || (z >= 11 && z <= 14));

            if (allZoneIds.length === 0) return null;

            const isPitcherView = playerType === 'pitcher';
            const cellSize = isMobile ? 56 : 64;
            const chaseH = isMobile ? 32 : 36;
            const chaseW = isMobile ? 32 : 36;
            const gridW = cellSize * 3;
            // LHB: zone 13 is spatially outside (left from catcher), zone 14 is inside (right)
            const isLHBDiff = !isPitcherView && (player?.batSide === 'L' || player?.bats === 'L');
            const leftDiffZone = isLHBDiff ? 14 : 13;
            const rightDiffZone = isLHBDiff ? 13 : 14;

            const renderDiffCell = (zoneNum, w, h, isChase = false) => {
              const oldW = oldZones[zoneNum]?.whiffRate || 0;
              const newW = newZones[zoneNum]?.whiffRate || 0;
              const diff = newW - oldW;
              const isGood = isPitcherView ? diff > 0 : diff < 0;
              const isBad = isPitcherView ? diff < 0 : diff > 0;
              const absDiff = Math.abs(diff);
              const intensity = Math.min(absDiff / 20, 1);
              const label = { 11: 'Above', 12: 'Below', 13: 'Inside', 14: 'Outside' }[zoneNum] || `Zone ${zoneNum}`;
              return (
                <Tooltip key={zoneNum} title={`${label}: ${oldW}% → ${newW}% (${diff > 0 ? '+' : ''}${diff.toFixed(1)}%)`} arrow>
                  <Box sx={{
                    width: w, height: h,
                    bgcolor: absDiff < 1 ? alpha(theme.palette.divider, 0.05)
                      : isGood ? alpha('#4caf50', 0.1 + intensity * 0.5)
                      : alpha('#f44336', 0.1 + intensity * 0.5),
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    border: isChase ? `1px dashed ${alpha(theme.palette.text.secondary, 0.25)}` : `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    borderRadius: isChase ? '4px' : 0,
                  }}>
                    <Typography sx={{
                      fontWeight: 800, fontSize: isChase ? '0.7rem' : '0.85rem', fontFamily: 'monospace', lineHeight: 1,
                      color: absDiff < 1 ? 'text.secondary' : isGood ? '#2e7d32' : isBad ? '#c62828' : 'text.primary',
                    }}>
                      {absDiff < 0.1 ? '—' : `${diff > 0 ? '+' : ''}${diff.toFixed(1)}%`}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1, mt: 0.2 }}>
                      {oldW.toFixed(0)}→{newW.toFixed(0)}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            };

            return (
              <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                    Whiff Rate Change: {oldest.year} → {newest.year}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    {isPitcherView
                      ? 'Green = gained more whiffs · Red = batters make more contact now'
                      : 'Green = improved contact (fewer whiffs) · Red = more swing-and-miss now'}
                  </Typography>

                  {/* Full zone layout: Above → [Inside | 3x3 | Outside] → Below */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    {/* Chase: Above */}
                    {renderDiffCell(11, gridW, chaseH, true)}
                    {/* Middle row: Inside + 3x3 + Outside (zones swapped for LHB) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {renderDiffCell(leftDiffZone, chaseW, cellSize * 3, true)}
                      <Box sx={{ border: '2px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                        {[1, 4, 7].map(rowStart => (
                          <Box key={rowStart} sx={{ display: 'flex' }}>
                            {[0, 1, 2].map(col => renderDiffCell(rowStart + col, cellSize, cellSize))}
                          </Box>
                        ))}
                      </Box>
                      {renderDiffCell(rightDiffZone, chaseW, cellSize * 3, true)}
                    </Box>
                    {/* Chase: Below */}
                    {renderDiffCell(12, gridW, chaseH, true)}
                  </Box>
                </CardContent>
              </Card>
            );
          })()}

          {/* ---- Pitch Whiff% Visual Bars per type ---- */}
          {!pitchLoading && comparedPitchYears.length >= 2 && allPitchTypes.length > 0 && (
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, fontSize: '0.85rem' }}>
                  Whiff Rate by Pitch Type
                </Typography>
                {allPitchTypes.map((typeCode) => {
                  const values = comparedPitchYears.map(yd => {
                    const pt = getPitchTypeForYear(yd.year, typeCode);
                    if (!pt) return 0;
                    const swings = (pt.swingAndMiss || 0) + (pt.fouls || 0) + (pt.inPlay || 0);
                    return swings > 0 ? parseFloat(((pt.swingAndMiss / swings) * 100).toFixed(1)) : 0;
                  });
                  if (values.every(v => v === 0)) return null;
                  const max = Math.max(...values, 1);

                  return (
                    <Box key={typeCode} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PITCH_COLORS_CY[typeCode] || '#999' }} />
                        <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
                          {PITCH_TYPE_NAMES_CY[typeCode] || typeCode}
                        </Typography>
                      </Box>
                      {comparedPitchYears.map((yd, i) => (
                        <Box key={yd.year} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                          <Typography variant="caption" sx={{ width: 36, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>{yd.year}</Typography>
                          <Box sx={{ flex: 1, height: 14, bgcolor: alpha(theme.palette.divider, 0.1), borderRadius: 1, overflow: 'hidden' }}>
                            <Box sx={{
                              width: `${(values[i] / max) * 100}%`, height: '100%',
                              bgcolor: alpha(PITCH_COLORS_CY[typeCode] || '#999', 0.7), borderRadius: 1, transition: 'width 0.6s ease-out',
                            }} />
                          </Box>
                          <Typography variant="caption" sx={{ width: 40, fontWeight: 700, fontSize: '0.75rem', fontFamily: 'monospace', textAlign: 'right' }}>
                            {values[i] > 0 ? `${values[i]}%` : '—'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </>
      )}

    </Box>
  );
};

export default PlayerDetail;
